import type { ScheduledStateName, PreviewSends } from './sendVisibility';
import { supabase } from './supabase';

// ⚠️ Every call goes through apiRequest: the site's own address first, the
// worker's as a backup (lib/apiBase.ts, 2026-09-26 « Connexion au serveur impossible »).
import { apiRequest } from './apiBase';
/** The worker's own address — still used by the customer upload-link calls below. */
const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

/** Resumable / TUS upload descriptor returned alongside the signed `upload_url`
 *  by the photo-upload-url endpoints. Null when the worker can't mint a JWT
 *  (SUPABASE_JWT_SECRET unset) → the client uses the signed-URL fallback. */
export interface TusBlock {
  storage_host: string;
  anon_key: string;
  bucket: string;
  object_path: string;
  token: string;
  expires_in: number;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }
  return headers;
}

/** Exported for lib/pushPrefsApi.ts (2026-10-01), which keeps its calls in a
 *  file of its own so parallel work on this file never collides. */
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await apiRequest(path, {
    ...options,
    headers: { ...headers, ...(options.headers ?? {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    // Attach the HTTP status so withRetry can tell a permanent 4xx (don't retry)
    // from a transient 5xx/408/429 (do retry). `body` carries the full JSON error
    // payload (e.g. the /submit originals-missing details) for callers that need it.
    throw Object.assign(new Error(err.error ?? `HTTP ${res.status}`), { status: res.status, body: err });
  }
  return res.json();
}

/** Authenticated multipart POST — no JSON Content-Type, the browser writes the
 *  boundary itself. */
async function apiFetchForm<T>(path: string, form: FormData): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await apiRequest(path, {
    method: 'POST',
    headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw Object.assign(new Error((err as { error?: string }).error ?? `HTTP ${res.status}`), { status: res.status, body: err });
  }
  return res.json();
}

/** Authenticated fetch that returns raw TEXT (AMEEX label print sheets are
 *  HTML, not JSON). Callers open the result in a blob tab for printing. */
async function apiFetchText(path: string): Promise<string> {
  const headers = await getAuthHeaders();
  const res = await apiRequest(path, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  return res.text();
}

/** Fetch a print sheet (auth'd), open it in a new tab, trigger print. Fixes
 *  the old bug where a bare link (no JWT) bounced to the AMEEX dashboard.
 *
 *  POPUP-BLOCKER GOTCHA (2026-06-11): window.open AFTER an await is no longer
 *  inside the user gesture → Chrome blocks it silently (Yahya: "nothing
 *  happens"). So the tab opens SYNCHRONOUSLY on click, shows a small loading
 *  note, and navigates to the blob once the sheet arrives. */
export async function openPrintSheet(path: string): Promise<void> {
  const tab = openLoadingTab('Chargement de l\'étiquette AMEEX…');
  return loadPrintSheetInto(tab, path);
}

/** Open a tab SYNCHRONOUSLY (must be called directly inside a click handler)
 *  with a small loading note. Returns the window to pass to
 *  loadPrintSheetInto once the sheet is ready — this is how long async flows
 *  (batch parcel creation) keep the popup alive without getting blocked. */
export function openLoadingTab(message: string): Window | null {
  const tab = window.open('', '_blank');
  if (tab) {
    tab.document.write(`<title>Impression…</title><p style="font-family:sans-serif;color:#8B6F26;padding:24px">${message}</p>`);
  }
  return tab;
}

/** Fetch a print sheet (auth'd) and load it into an already-open tab. */
export async function loadPrintSheetInto(tab: Window | null, path: string): Promise<void> {
  try {
    const html = await apiFetchText(path);
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    if (tab && !tab.closed) tab.location.href = url;
    else window.location.href = url; // popup denied entirely — same-tab fallback
  } catch (e) {
    try { tab?.close(); } catch { /* already closed */ }
    throw e;
  }
}

export const ameexPrintApi = {
  /** Load the batch labels into a PRE-OPENED tab (popup-blocker-safe). */
  labelsByRefInto: (tab: Window | null, ref: string, type = 'Label_100_100') =>
    loadPrintSheetInto(tab, `/ameex/labels/${encodeURIComponent(ref)}?type=${encodeURIComponent(type)}`),
  /** Load labels for the SELECTED order ids into a PRE-OPENED tab (prints
   *  already-confirmed + newly-confirmed; the endpoint returns a message page
   *  when nothing prints, so the tab is never left blank/closed). */
  labelsForOrdersInto: (tab: Window | null, ids: string[]) =>
    loadPrintSheetInto(tab, '/ameex/labels-for-orders?ids=' + ids.map(encodeURIComponent).join(',')),
  /** Per-order label sheet — ensures today's bon de livraison first. */
  orderLabel: (shopifyOrderId: string, type = 'Label_100_100') =>
    openPrintSheet(`/orders/${shopifyOrderId}/ameex/label?type=${encodeURIComponent(type)}`),
  /** Batch label sheet by BL ref (delivery page). */
  labelsByRef: (ref: string, type = 'Label_100_100') =>
    openPrintSheet(`/ameex/labels/${encodeURIComponent(ref)}?type=${encodeURIComponent(type)}`),
  manifestByRef: (ref: string) =>
    openPrintSheet(`/ameex/manifest/${encodeURIComponent(ref)}`),
};

// ─── Orders API ───────────────────────────────────────────────────────────────

export interface BlocklistEntry {
  id: string;
  kind: 'ip' | 'phone';
  value: string;
  note: string | null;
  created_at: string;
}

/** One row of the admin "Commandes confirmées" widget. Every row is an explicit
 *  "✓ Confirmer la commande" click — orders that merely reached ready-to-print
 *  are NOT counted (see GET /orders/confirmations in worker/src/routes/orders.ts).
 *  `agent_name` falls back to null only if the profile join comes back empty. */
export interface ConfirmedOrderRow {
  order_id: string;
  shopify_order_id: string | null;
  order_name: string | null;
  customer_name: string | null;
  bundle: string | null;
  status: string | null;
  total_price: number | null;
  confirmed_at: string;
  agent_name: string | null;
  rate: number;
  albums: number | null;
}

/** One line of what an order contains (migration 070). Comes from the orders
 *  list + detail endpoints. NOTE: `unit_price` is deliberately absent — the
 *  list endpoint does not select it. */
export interface OrderItem {
  order_id: string;
  kind: 'offer' | 'upsell';
  title: string | null;
  variant_title: string | null;
  quantity: number;
  shopify_product_gid: string | null;
  shopify_variant_gid: string | null;
  /** Resolved server-side: variant image → product image → bundle fallback. */
  image_url: string | null;
}

/** A scheduled panel message (worker migration 227) and, since 2026-10-15,
 *  what became of it — the last 7 days, every state (worker lib/scheduledSend.ts). */
export interface ScheduledReply {
  id: string;
  body: string;
  send_after: string;
  quoted_message_id: string | null;
  scheduled_by: string | null;
  created_at: string;
  status?: string;
  sent_at?: string | null;
  last_error?: string | null;
  attempts?: number;
  state?: ScheduledStateName;
  /** Why it is held / failed / late / cancelled, in plain words. */
  why?: string | null;
}

/** What the milestone sweep will do with an approved preview (worker lib/previewSendPlan.ts). */
export interface PreviewSendPlan {
  sends: PreviewSends;
  code: string;
  reason: string;
  at: string | null;
  label: string;
}

export type WhatsappFunnelStepKey = 'chats' | 'photos' | 'approved' | 'confirmed';
export interface WhatsappFunnel {
  steps: Array<{ key: WhatsappFunnelStepKey; count: number; pct: number | null }>;
  weeks: Array<{ week: string; chats: number; photos: number; approved: number; confirmed: number }>;
  window: { from: string; to: string };
  ruleOnAt: string | null;
  ruleOn: boolean;
  archivedOrders: number;
}

export interface Order {
  id: string;
  /** What the order contains. Attached by the list + detail endpoints. */
  items?: OrderItem[];
  shopify_order_id: string;
  shopify_order_name: string;
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  bundle: 'Solo' | 'Duo' | 'Trio' | null;
  page_count: number;
  destination_1: string | null;
  year_1: string | null;
  destination_2: string | null;
  year_2: string | null;
  destination_3: string | null;
  year_3: string | null;
  layout: string | null;
  total_price: number | null;
  /** Coupon used on this order (migration 282) — null/absent = none. */
  coupon_code?: string | null;
  /** DH taken off the total by that coupon (0 = none). */
  coupon_discount?: number | null;
  /** Where this order's own printed coupons went: {album_index, pages} | {separate_pdf_path}. */
  coupon_print?: { album_index?: number; pages?: number[]; separate_pdf_path?: string } | null;
  /** Pages the customer added beyond THIS order's base allowance (1 paper
   *  = 2 pages; the current base is 18 papers / 36 pages). Each extra page is
   *  billed at settings.price_per_page MAD. */
  extra_pages?: number | null;
  extra_pages_cost?: number | null;
  /** Pages included per album in this order's base price, FROZEN at creation
   *  (migration 082). 36 on orders sold from 2026-08-12; 50 on everything
   *  older. Pricing displays must read THIS, never the live constant — see
   *  lib/orderPricing.ts. Null only on a row that predates the migration. */
  base_pages_per_album?: number | null;
  /** Extra-page price FROZEN on this order at creation (migration 230). Read it
   *  with `resolvePricePerPage` — never `app_settings.price_per_page`. */
  price_per_page?: number | string | null;
  /** "Couverture Relief 3D" add-on (migration 069). Order-level: one purchase
   *  covers every album. `relief_3d_cost` is the TOTAL charged (79 Solo /
   *  118 Duo / 177 Trio), snapshotted at purchase, and must be unwound from the
   *  base and re-added to the total — see lib/orderPricing.ts. */
  relief_3d?: boolean | null;
  relief_3d_cost?: number | null;
  /** Accepted add-ons OTHER than the Relief (express delivery accepted in the
   *  chat — migration 188), already inside total_price, in MAD. NULL = none.
   *  Unwound and re-added like the Relief — see lib/orderPricing.ts. */
  addons_cost?: number | null;
  /** The extras offer after the preview was approved (migration 208): when it
   *  went out, and when her answer was recorded. */
  extras_offer_sent_at?: string | null;
  extras_decided_at?: string | null;
  /** Admin-corrected authoritative total (MAD). null → normal auto pricing.
   *  When set, the worker pins total_price to this and prevents auto re-prices
   *  (AMEEX edits, regens) from clobbering it. */
  price_override?: number | null;
  status: string;
  pdf_status: 'generating' | 'ready' | 'failed' | null;
  pdf_render_error?: string | null;
  cover_pdf_url: string | null;
  interior_pdf_url: string | null;
  full_book_pdf_url: string | null;
  customer_note: string | null;
  album_submitted_at: string | null;
  /** Append-only trail of admin "force print with available copies" actions
   *  (migration 060). Each event lists exactly which photos were exported
   *  below print spec (display copy substituted for the missing original). */
  force_print_log?: Array<{
    at: string;
    admin_id: string | null;
    substituted: Array<{ photo_id: string; filename: string | null; album_index: number; from_path: string; to_path: string }>;
    removed: Array<{ photo_id: string; filename: string | null }>;
  }> | null;
  upload_token: string | null;
  upload_token_expires_at: string | null;
  upload_token_revoked_at: string | null;
  client_photos_first_upload_at: string | null;
  client_photos_last_upload_at: string | null;
  payment_method: 'cod' | 'virement' | null;
  /** Acquisition channel resolved at order creation from UTM / utm:* tag /
   *  referrer (facebook|instagram|tiktok|google|chatgpt|snapchat|youtube|
   *  whatsapp|direct|other). Null on orders predating the feature. */
  traffic_source?: string | null;
  /** Why this order exists, in French (migration 220, worker lib/orderIntent.ts). */
  /** Migration 226: archived (no photos, no money, made before the rule). Status is 'cancelled' while archived. */
  archived_at?: string | null;
  archived_reason?: string | null;
  created_because?: string | null;
  /** photo_batch | payment | cover_request | says_order | picks_pack | name_address | asks_rib | transfer_link | manager | site_form | shopify | admin | ameex */
  created_because_signal?: string | null;
  /** code | model | manager | site | shopify | admin | ameex */
  created_because_source?: string | null;
  created_because_message_id?: string | null;
  /** Manually-assigned origin/attribution label (admin-set): meta_form,
   *  meta_whatsapp, tiktok, organic, instagram, whatsapp_manual, or free-text.
   *  Distinct from the auto-derived traffic_source. Null until set. */
  origin?: string | null;
  is_test?: boolean;
  /** Device family the customer first ordered/uploaded from (iPhone|iPad|
   *  Android|Mobile|Desktop). Set once — first non-null wins, never
   *  overwritten. Null on orders predating the feature. */
  device?: string | null;
  /** Fraud guard (2026-07-08): the ordering browser's IP + user-agent,
   *  captured by storiz-cod at COD form submit. Null on older orders. */
  client_ip?: string | null;
  client_user_agent?: string | null;
  /** Server-side flipbook-preview render status (migration 059). */
  previews_status?: 'generating' | 'ready' | 'failed' | null;
  previews_error?: string | null;
  /** The manager's approval of the current 3D preview build (migration 224). */
  preview_approval_state?: 'failed' | 'waiting' | 'approved' | null;
  preview_approval_check?: { problems?: Array<{ albumIndex?: number; what?: string }> } | null;
  preview_link_sent_at?: string | null;
  previews_generated_at?: string | null;
  created_at: string;
  updated_at: string;
  /** Last time the customer's editor session was saved. If newer than
   *  cover_pdf_updated_at, the PDF is stale and needs re-generation. */
  session_last_saved_at?: string | null;
  /** Timestamp on the cover.pdf object in Supabase Storage. */
  cover_pdf_updated_at?: string | null;
  /** Image proof of the 50 MAD advance payment (bank-transfer screenshot
   *  etc.) the admin uploads to verify the customer paid before AMEEX
   *  ships. Null when no proof has been recorded. */
  advance_proof_url?: string | null;
  advance_proof_uploaded_at?: string | null;
  /** Per-album PDF URLs. Solo has 1 entry; Duo/Trio have 2/3. */
  album_pdfs?: Array<{
    album_index: number;
    destination: string | null;
    cover_pdf_url: string | null;
    interior_pdf_url: string | null;
    full_book_pdf_url: string | null;
    /** Operator replaced this album's cover by hand — regeneration cannot
     *  overwrite it until the override is cleared. See migration 067. */
    cover_manual_override?: boolean;
    cover_manual_uploaded_at?: string | null;
    cover_manual_filename?: string | null;
    /** Interior override (migration 068) — independent of the cover. */
    interior_manual_override?: boolean;
    interior_manual_uploaded_at?: string | null;
    interior_manual_filename?: string | null;
    interior_manual_pages?: number | null;
    /** The album opens on its themed S1 page: no intro page is printed
     *  (design system v2 §7 S1, bookStructure.ts). Absent = plain book. */
    themed_book?: boolean;
  }>;
  // ─── AMEEX delivery integration ─────────────────────────────────────────────
  ameex_parcel_code?: string | null;
  ameex_city_id?: number | null;
  ameex_status?: AmeexCanonicalStatus | null;
  ameex_status_raw?: string | null;
  ameex_status_sub?: string | null;
  ameex_status_comment?: string | null;
  ameex_status_color?: string | null;
  ameex_last_event_at?: string | null;
  ameex_refund_received?: boolean | null;
  ameex_refund_received_at?: string | null;
  // ─── Paid-status (admin override of the global acompte) ─────────────────────
  /** The acompte recorded on the order from the AMEEX card (MAD). The AMEEX
   *  COD is total − what was actually received (advance_received_amount, else
   *  this); null and nothing received → the full total (money bug 3,
   *  2026-09-29 — it used to fall back to app_settings.advance_amount). */
  advance_amount?: number | null;
  /** True when the customer has paid the album in full (full transfer, free
   *  upgrade, etc.). Admin sets this from the AMEEX delivery card. */
  paid_in_full?: boolean;
  /** Server-stamped on the false → true transition. */
  paid_at?: string | null;
  /** Money actually received against this order (a verified receipt — written
   *  by the auto-accept sweep or a human accepting a payment proposal).
   *  Drives the 💰 chip in the orders list: "who sent money, who has not". */
  advance_received_amount?: number | null;
  advance_received_at?: string | null;
  /** Redo (réimpression) history — newest first. Inlined on the order-detail
   *  GET response. Empty/absent when the order has never been redone. */
  redos?: OrderRedo[];
  /** Who clicked "✓ Commande confirmée" (agent commission system). Null when
   *  not yet confirmed. Inlined on the order-detail GET.
   *
   *  `agent_id`, `rate` and `profiles` are OPTIONAL on purpose: the API strips
   *  them unless the caller is a full admin or the agent who confirmed it, so
   *  one agent cannot read another's identity or commission (2026-08-11).
   *  `confirmed_at` always survives — the UI keys "is it confirmed?" off it. */
  agent_confirmation?: {
    agent_id?: string; confirmed_at: string; rate?: number | string;
    profiles?: { full_name: string | null; email: string | null } | null;
    /** 'auto_payment' = confirmed automatically after a person recorded the
     *  payment (2026-10-02, no commission, album not approved); 'button' or
     *  absent = a person clicked « Confirmer la commande ». */
    source?: 'button' | 'auto_payment' | string | null;
  } | null;
}

export type RedoScope = 'full' | 'cover-only' | 'interior-only' | 'custom-pages';
export type RedoReason = 'print-error' | 'client-request' | 'quality-issue' | 'other';
export type RedoStatus =
  | 'pending' | 'generating' | 'ready' | 'sent-to-printer' | 'completed' | 'cancelled';

export interface OrderRedo {
  id: string;
  order_id: string;
  album_index: number;
  scope: RedoScope;
  /** 0-based content-page indices for a `custom-pages` redo; null otherwise. */
  page_indices: number[] | null;
  reason: RedoReason;
  reason_detail: string | null;
  /** Internal paper+ink cost (MAD). Tracking only — NEVER charged to the
   *  customer / never affects orders.total_price. */
  redo_cost_mad: number | null;
  status: RedoStatus;
  /** Order status snapshotted at create time, restored on completion. */
  status_before_redo: string;
  cover_pdf_url: string | null;
  interior_pdf_url: string | null;
  created_at: string;
  completed_at: string | null;
  updated_at: string;
}

export type AmeexCanonicalStatus =
  | 'pending' | 'in_progress' | 'distribution' | 'delivered'
  | 'postponed' | 'returned' | 'refused' | 'cancelled' | 'refunded' | 'unresolved';

export interface AmeexStatusEvent {
  id: string;
  order_id: string | null;
  parcel_code: string;
  statut: string;
  statut_s: string | null;
  comment: string | null;
  statut_name: string | null;
  statut_color: string | null;
  date: string | null;
  received_at: string;
}

export const ordersApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiFetch<{ orders: Order[]; total: number }>(`/orders${qs}`);
  },
  get: (shopifyOrderId: string) =>
    apiFetch<{ order: Order }>(`/orders/${shopifyOrderId}`),
  /** Agent's explicit "✓ Commande confirmée" — one per order, rate locked. */
  confirmOrder: (shopifyOrderId: string) =>
    apiFetch<{ ok: boolean; confirmation: { agent_id: string; confirmed_at: string; rate: number } }>(
      `/orders/${shopifyOrderId}/confirm-order`, { method: 'POST', body: '{}' }),
  /** Admin dashboard "Commandes confirmées" widget: count + list + who
   *  confirmed each, for a date window (YYYY-MM-DD, inclusive). Includes
   *  orders that REACHED ready-to-print without an explicit agent stamp
   *  (agent_name null). No spend/ROI data by design. */
  confirmations: (params?: { from?: string; to?: string }) => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    const q = qs.toString();
    return apiFetch<{ count: number; confirmations: ConfirmedOrderRow[] }>(
      `/orders/confirmations${q ? `?${q}` : ''}`);
  },

  // ── Claim-based assignment (2026-08-11) ───────────────────────────────────
  // `available` = the shared queue, `mine` = what this agent holds. `held` is
  // returned to admins ONLY: an agent must never see another agent's work.
  queue: () => apiFetch<OrderQueue>('/orders/queue'),
  claim: (shopifyOrderId: string) =>
    apiFetch<{ ok: true; claimed: boolean }>(`/orders/${shopifyOrderId}/claim`, { method: 'POST' }),
  release: (shopifyOrderId: string) =>
    apiFetch<{ ok: true; released: boolean }>(`/orders/${shopifyOrderId}/release`, { method: 'POST' }),

  /**
   * THE IGNORE BUTTON — we stop chasing, the client is not blocked.
   *
   * Silences every follow-up for this order and takes it off the waiting
   * lists. Changes NOTHING else: not the status, not the price, not a photo.
   * If the customer writes, the assistant still answers.
   */
  ignore: (shopifyOrderId: string, reason?: string) =>
    apiFetch<{ ok: true; ignored: string }>(`/orders/${shopifyOrderId}/ignore`, {
      method: 'POST', body: JSON.stringify({ reason: reason ?? null }),
    }),
  unignore: (shopifyOrderId: string) =>
    apiFetch<{ ok: true }>(`/orders/${shopifyOrderId}/unignore`, { method: 'POST' }),
  /** Everything currently ignored, so the button is never a black hole. */
  ignored: () =>
    apiFetch<{
      items: Array<{
        id: string; kind: 'order' | 'chat'; ref: string; reason: string | null;
        ignored_at: string; ignored_by: string | null;
        order: {
          id: string; shopify_order_name: string; customer_name: string | null;
          customer_phone: string | null; status: string;
        } | null;
      }>;
    }>('/orders/ignored'),
  /** Admin override. `agent_id: null` forces the order back to the queue. */
  assign: (shopifyOrderId: string, agentId: string | null) =>
    apiFetch<{ ok: true; assigned_agent_id: string | null }>(`/orders/${shopifyOrderId}/assign`, {
      method: 'POST', body: JSON.stringify({ agent_id: agentId }),
    }),
  /** Fix 4 — is this order print-ready? Byte-checks every photo's full-res
   *  original in storage; returns the missing ones (admin only). */
  printIntegrity: (shopifyOrderId: string) =>
    apiFetch<{
      ok: boolean;
      /** 2026-07-13 policy — true when nothing BLOCKS printing (clean or
       *  degraded-only: missing originals that have a thumbnail/preview). */
      printable: boolean;
      checked_sessions: number;
      unavailable?: boolean;
      missing: {
        session_id: string;
        album_index: number;
        photo_id: string;
        original_filename: string | null;
        has_thumbnail: boolean;
        has_fallback: boolean;
        placed: boolean;
      }[];
      /** placed + no printable copy at all → blocks submit/render. */
      blocking: { session_id: string; album_index: number; photo_id: string; original_filename: string | null; has_thumbnail: boolean; has_fallback: boolean; placed: boolean }[];
      /** placed + thumbnail/preview only → prints at reduced quality. */
      degraded: { session_id: string; album_index: number; photo_id: string; original_filename: string | null; has_thumbnail: boolean; has_fallback: boolean; placed: boolean }[];
    }>(`/orders/${shopifyOrderId}/print-integrity`),
  /** Admin sticky notes (2026-07-15) — internal team notes pinned to the top
   *  of the order page, with author + delete. */
  adminNotes: (shopifyOrderId: string) =>
    apiFetch<{ notes: OrderNote[] }>(`/orders/${shopifyOrderId}/admin-notes`),
  /** `step`: which step it is about (null = Activity); `is_problem`: pinned until « Mark as done » (2026-10-07). */
  addAdminNote: (shopifyOrderId: string, note: string, opts: { step?: OrderNoteStep | null; is_problem?: boolean } = {}) =>
    apiFetch<{ note: OrderNote }>(
      `/orders/${shopifyOrderId}/admin-notes`,
      { method: 'POST', body: JSON.stringify({ note, step: opts.step ?? null, is_problem: opts.is_problem === true }) },
    ),
  markAdminNoteDone: (shopifyOrderId: string, noteId: string, done: boolean) =>
    apiFetch<{ note: OrderNote }>(
      `/orders/${shopifyOrderId}/admin-notes/${noteId}`,
      { method: 'PATCH', body: JSON.stringify({ done }) },
    ),
  deleteAdminNote: (shopifyOrderId: string, noteId: string) =>
    apiFetch<{ deleted: boolean }>(
      `/orders/${shopifyOrderId}/admin-notes/${noteId}`,
      { method: 'DELETE' },
    ),
  /** EXPLICIT admin override for a print blocked on missing originals:
   *  substitutes the display copies, logs it permanently (force_print_log),
   *  cleans byteless unplaced rows, then fires the server PDF render.
   *  409 with `unfixable` when a PLACED photo has no copy at all. */
  forcePrintFallback: (shopifyOrderId: string) =>
    apiFetch<{
      success: boolean;
      substituted_count: number;
      removed_count: number;
      substituted: { photo_id: string; filename: string | null; album_index: number }[];
      render_started: boolean;
    }>(`/orders/${shopifyOrderId}/force-print-fallback`, { method: 'POST' }),
  /** Live pricing recomputed from the album's CURRENT content pages (the
   *  source of truth). The worker self-heals the orders row from this too, so
   *  the admin order page overlays it on load to reflect a post-regen page
   *  count + price + AMEEX COD without waiting for a /submit. */
  checkoutSummary: (shopifyOrderId: string) =>
    apiFetch<{
      order_id: string;
      page_count: number;
      extra_pages: number;
      extra_pages_cost: number;
      relief_3d: boolean;
      relief_3d_cost: number;
      base_price: number;
      total: number;
    }>(`/orders/${shopifyOrderId}/checkout-summary`),
  mine: () =>
    apiFetch<{ orders: Order[] }>('/orders/customer/mine'),
  /**
   * `paymentOverrideReason`: an admin's « Forcer sans avance » (lib/forceWithoutAdvance.ts).
   * `allowRepeats`: « Continuer quand même » past a photo placed twice (lib/statusChangeAsk.ts).
   */
  updateStatus: (shopifyOrderId: string, status: string, paymentOverrideReason?: string, allowRepeats?: boolean) =>
    apiFetch<{ order: Order }>(`/orders/${shopifyOrderId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({
        status,
        ...(paymentOverrideReason ? { payment_override_reason: paymentOverrideReason } : {}),
        ...(allowRepeats ? { allow_repeats: true } : {}),
      }),
    }),
  /** Capture which device the customer ordered/uploaded from. Fire-and-forget
   *  from the customer flow surfaces; the worker writes orders.device ONLY when
   *  it's still null (first non-null device wins, never overwritten). */
  recordDevice: (shopifyOrderId: string, device: string) =>
    apiFetch<{ device: string }>(
      `/orders/${shopifyOrderId}/device`,
      { method: 'POST', body: JSON.stringify({ device }) },
    ),
  /**
   * Admin-only — receipts the assistant has READ but not acted on.
   *
   * ⚠️ NOTHING HERE HAS TOUCHED AN ORDER. The assistant used to mark orders
   * paid on its own; since 2026-08-22 it files a proposal and waits. Until
   * somebody decides, the money is not recorded anywhere — which is why this
   * list must be visible rather than tidy.
   */
  paymentProposals: () =>
    apiFetch<{ proposals: PaymentProposal[] }>('/orders/payment-proposals'),

  /**
   * Admin-only — accept or refuse one reading.
   *
   * `amount` and `kind` are OPTIONAL OVERRIDES, and correcting them is the
   * normal case: a person is in this loop precisely because a vision model
   * reading a phone photo of a bank slip gets things wrong. Left out, the
   * model's own reading is used.
   */
  decidePaymentProposal: (
    id: string,
    body: { decision: 'accept' | 'reject'; amount?: number; kind?: 'advance' | 'full' },
  ) =>
    apiFetch<{ ok: true; decision: string; written: Record<string, unknown> | null }>(
      `/orders/payment-proposals/${id}/decide`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * Admin-only — what the CONVERSATION says is still owed.
   *
   * ⚠️ A DIFFERENT LIST FROM EVERY OTHER QUEUE, AND THE DIFFERENCE IS THE
   * POINT. Every other follow-up reads the ORDER: this album has no photos,
   * that one has no cover. These come from the MESSAGES — photos promised
   * « ghedda » that never came, an edit nobody made, an extra album agreed to
   * and never added. None of it is in a column, so nothing else can see it.
   *
   * Proposals only. Nothing here has messaged anybody.
   */
  openLoops: () =>
    apiFetch<{ loops: OpenLoop[]; installed?: boolean }>('/orders/open-loops'),

  /**
   * Admin-only — close one, either because it is done or because it is not
   * real. Both are recorded as an order note; a loop that just vanished would
   * leave no trace that anybody ever saw it.
   */
  decideOpenLoop: (id: string, body: { decision: 'done' | 'dismiss' }) =>
    apiFetch<{ ok: true; decision: string }>(
      `/orders/open-loops/${id}/decide`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** Admin-only — record a client advance bank transfer as ACTUALLY received
   *  (real cash). Feeds the "Cash en main" figure on the accounting page. */
  advanceReceived: (shopifyOrderId: string, data: { amount: number; date?: string }) =>
    apiFetch<{ order: { id: string; shopify_order_id: string; advance_received_amount: number | null; advance_received_at: string | null } }>(
      `/orders/${shopifyOrderId}/advance-received`,
      { method: 'POST', body: JSON.stringify(data) },
    ),
  /** Admin-only — unlock (or re-lock) a submitted album's editor so the
   *  customer can re-enter to fix something (e.g. re-upload failed photos). */
  setEditorUnlocked: (shopifyOrderId: string, albumIndex: number, unlocked: boolean) =>
    apiFetch<{ success: boolean; editor_unlocked: boolean }>(
      `/orders/${shopifyOrderId}/editor-lock/${albumIndex}`,
      { method: 'POST', body: JSON.stringify({ unlocked }) },
    ),
  /** Admin-only — open a 48h window letting the customer re-enter a submitted
   *  album and append more photos. Sets editor_unlocked + upload_allowed and
   *  returns the expiry. Resubmit closes it. See migration 018. */
  shareForUpload: (shopifyOrderId: string, albumIndex: number) =>
    apiFetch<{ success: boolean; editor_unlocked: boolean; upload_allowed: boolean; upload_allowed_until: string }>(
      `/orders/${shopifyOrderId}/share-upload/${albumIndex}`,
      { method: 'POST' },
    ),
  /** Admin-only — edit customer contact info on the order. Writes Supabase
   *  (source of truth) then best-effort pushes the same fields to the live
   *  Shopify order. `shopify_synced` reflects whether that push succeeded;
   *  the local save always succeeds independently. */
  updateCustomerInfo: (shopifyOrderId: string, body: {
    customer_name?: string;
    customer_phone?: string;
    customer_email?: string;
    customer_note?: string;
    ameex_city_id?: number | null;
    origin?: string | null;
    is_test?: boolean;
  }) =>
    apiFetch<{ order: Order; shopify_synced: boolean; shopify_error?: string }>(
      `/orders/${shopifyOrderId}/customer-info`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  /** Admin-only — update advance_amount + paid_in_full on the order. Use
   *  null on advance_amount to clear the recorded acompte. The worker stamps
   *  paid_at when paid_in_full flips false → true. ⚠️ Sending paid_in_full
   *  false NEVER clears paid_at (money bug 6, 2026-09-29: the AMEEX card's
   *  « Enregistrer » sends the box on every save and wiped #1190's payment
   *  date); only `mark_unpaid: true` does. */
  updatePricing: (shopifyOrderId: string, body: {
    advance_amount?: number | null;
    paid_in_full?: boolean;
    /** The explicit « mark unpaid » — the only way to clear paid_at. */
    mark_unpaid?: boolean;
  }) =>
    apiFetch<{ success: boolean; order: Order }>(`/orders/${shopifyOrderId}/pricing`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  /** Admin-only — correct a mistaken order total and sync it everywhere (orders
   *  row, Shopify subtotal, AMEEX parcel COD). Pass a number to SET an
   *  authoritative manual total (price_override), or null to CLEAR it and restore
   *  the normal auto tier+pages pricing. AMEEX only updates if a parcel exists
   *  and isn't already shipped/delivered (see ameex_skipped_reason). */
  correctPrice: (shopifyOrderId: string, total: number | null) =>
    apiFetch<{
      ok: boolean;
      total: number;
      price_override: number | null;
      shopify_synced: boolean;
      ameex_synced: boolean;
      ameex_skipped_reason?: string;
    }>(`/orders/${shopifyOrderId}/correct-price`, {
      method: 'POST',
      body: JSON.stringify({ total }),
    }),
  /** Admin (« Corriger le prix » permission) — set THIS order's Relief 3D
   *  price in MAD, 0 = offered. The worker re-prices the order through the one
   *  pricing function (total, cash-to-collect and payment messages follow) and
   *  writes an order note. Refused: a negative price (400), an order without
   *  the Relief or with a hand-corrected total (409). `ameex_parcel_open`: a
   *  parcel already created keeps its old amount until the AMEEX card's
   *  « Modifier » sends the new one. Shopify is not edited. */
  setReliefPrice: (shopifyOrderId: string, price: number) =>
    apiFetch<{
      ok: boolean;
      unchanged?: boolean;
      relief_before: number;
      relief_3d_cost: number;
      total_before: number;
      total: number;
      ameex_parcel_open: boolean;
      note_written?: boolean;
    }>(`/orders/${shopifyOrderId}/relief-price`, {
      method: 'POST',
      body: JSON.stringify({ price }),
    }),
  submit: (shopifyOrderId: string, data?: { page_count?: number; extra_pages?: number; extra_pages_cost?: number }) =>
    apiFetch<{ order: Order; success: boolean }>(`/orders/${shopifyOrderId}/submit`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    }),
  /** Fraud-guard blocklist (Settings + order detail "Bannir"). Admin-only. */
  blocklist: {
    list: () =>
      apiFetch<{ entries: BlocklistEntry[] }>(`/orders/blocklist`),
    add: (kind: 'ip' | 'phone', value: string, note?: string) =>
      apiFetch<{ entry: BlocklistEntry }>(`/orders/blocklist`, {
        method: 'POST',
        body: JSON.stringify({ kind, value, note }),
      }),
    remove: (id: string) =>
      apiFetch<{ success: boolean }>(`/orders/blocklist/${id}`, { method: 'DELETE' }),
  },
  /** In-app checkout final click (StepCheckout): server-side twin of the
   *  storiz-checkout.liquid confirm — promotes the Shopify draft to a real
   *  order (fires the confirmation email) + records the payment method.
   *  Returns the FINAL Shopify order id for the thank-you redirect. */
  checkoutConfirm: (shopifyOrderId: string, payment_method: 'cod' | 'virement') =>
    apiFetch<{ success: boolean; final_order_id: string; draft_completed: boolean }>(
      `/orders/${shopifyOrderId}/checkout-confirm`,
      { method: 'POST', body: JSON.stringify({ payment_method }) },
    ),
  generatePdf: (shopifyOrderId: string) =>
    apiFetch<{ message: string; pdf_status: string }>(`/pdf/generate/${shopifyOrderId}`, {
      method: 'POST',
    }),
  /** Server-side flipbook-preview render (2026-07-08): headless browser
   *  rasterizes the STORED print PDFs into the previews bucket. Fire-and-poll
   *  via orders.previews_status (generating → ready|failed). */
  generatePreviewsServer: (shopifyOrderId: string) =>
    apiFetch<{ started: boolean }>(`/pdf/server-previews/${shopifyOrderId}`, { method: 'POST' }),
  advanceProofUploadUrl: (shopifyOrderId: string, filename: string, content_type: string) =>
    apiFetch<{ upload_url: string; token: string; storage_path: string; content_type: string }>(
      `/orders/${shopifyOrderId}/advance-proof/upload-url`,
      { method: 'POST', body: JSON.stringify({ filename, content_type }) },
    ),
  advanceProofFinalize: (shopifyOrderId: string, storage_path: string) =>
    apiFetch<{ ok: boolean; advance_proof_url: string; uploaded_at: string }>(
      `/orders/${shopifyOrderId}/advance-proof/finalize`,
      { method: 'POST', body: JSON.stringify({ storage_path }) },
    ),
  advanceProofDelete: (shopifyOrderId: string) =>
    apiFetch<{ ok: boolean }>(
      `/orders/${shopifyOrderId}/advance-proof`,
      { method: 'DELETE' },
    ),
  // ─── Send-to-printer (admin bulk) ──────────────────────────────────────────
  /** Sign a one-shot upload URL for the assembled print-batch zip. The browser
   *  PUTs the fflate-zipped bytes there (collection + zipping are client-side
   *  to dodge the worker's subrequest cap). */
  printBatchUploadUrl: (filename: string) =>
    apiFetch<{ upload_url: string; token: string; storage_path: string }>(
      '/orders/print-batch/upload-url',
      { method: 'POST', body: JSON.stringify({ filename }) },
    ),
  /** Turn the uploaded zip into a long-expiry signed download URL. */
  printBatchFinalize: (storage_path: string) =>
    apiFetch<{ ok: boolean; download_url: string }>(
      '/orders/print-batch/finalize',
      { method: 'POST', body: JSON.stringify({ storage_path }) },
    ),
  /** Mint a shareable printer download link (token-based, 7-day expiry, no
   *  login). INSTANT — just records the shopify order ids (and/or redo ids);
   *  the public page builds ZIPs on demand in the printer's browser. */
  createPrinterLink: (order_ids: string[], redo_ids?: string[]) =>
    apiFetch<{ token: string; expires_at: string }>('/orders/printer-link', {
      method: 'POST',
      body: JSON.stringify({ order_ids, ...(redo_ids?.length ? { redo_ids } : {}) }),
    }),
  /** Public (no-login) data for the printer download page. apiFetch rejects with
   *  the HTTP status on 404 (unknown token) / 410 (expired) so the page can branch. */
  printerLinkPublic: (token: string) =>
    apiFetch<{
      orders: Array<{
        order_id: string;
        order_name: string;
        bundle: string | null;
        /** Paid embossed-cover add-on. The printer works only from this page,
         *  so this is the only place the instruction can reach them. */
        relief_3d?: boolean;
        /** The printer's words for the relief (products.printer_label). */
        relief_label?: string | null;
        /** Express delivery bought (website or WhatsApp) → print it first. */
        urgent?: boolean;
        /** Paid add-ons (migration 070) with the PRINTER's wording
         *  (products.printer_label) — e.g. "vernis selectif", not "Relief 3D".
         *  Written onto the production sheet inside the ZIP so the instruction
         *  cannot be separated from the files. */
        upsells?: Array<{ label: string; quantity: number }>;
        zip_available: boolean;
        albums: Array<{
          album_index: number; destination: string | null; cover_url: string | null; interior_url: string | null;
          /** The front cover as the 3D preview rendered it (2026-10-12) — so the
           *  printer SEES the job, and where a relief goes. Null if not rendered. */
          cover_preview_url?: string | null;
          /** Interior pages as printed (the preview manifest's count). */
          pages?: number | null;
        }>;
      }>;
      redos?: Array<{
        redo_id: string;
        order_name: string;
        album_index: number;
        scope: string;
        pages: number[] | null;
        cover_url: string | null;
        interior_url: string | null;
      }>;
      expires_at: string;
    }>(`/orders/printer-link/public/${encodeURIComponent(token)}`),
  /** Email the printer a link to the assembled zip via MailChannels. Degrades
   *  gracefully: returns { emailed:false, reason } (HTTP 200) when the channel
   *  is disabled, unconfigured, or MailChannels isn't set up — never throws on
   *  config. */
  sendToPrinter: (zipUrl: string, orderIds: string[]) =>
    apiFetch<{
      emailed: boolean;
      reason?: string;
      status?: number;
    }>('/orders/send-to-printer', {
      method: 'POST',
      body: JSON.stringify({ zip_url: zipUrl, order_ids: orderIds }),
    }),
  pdfUploadUrls: (shopifyOrderId: string, albumIndex: number = 1) =>
    apiFetch<{
      /** null when an operator has manually replaced this album's cover —
       *  the worker refuses to issue a cover upload URL so regeneration cannot
       *  overwrite it. Interior is always issued. See migration 067. */
      cover:    { upload_url: string; token: string; storage_path: string } | null;
      /** null when an operator has manually replaced this album's interior.
       *  Cover and interior lock independently. See migration 068. */
      interior: { upload_url: string; token: string; storage_path: string } | null;
      cover_locked?: boolean;
      interior_locked?: boolean;
    }>(`/pdf/upload-urls/${shopifyOrderId}?album=${albumIndex}`, { method: 'POST' }),
  /** Admin « Régénérer »: queue a forced server rebuild of every album's PDFs;
   *  the 3D preview follows. `already` = a rebuild was already queued/running. */
  /** `album`: only that album is rebuilt (owner, 2026-10-17). */
  pdfRegenerate: (shopifyOrderId: string, opts?: { guides?: boolean; album?: number }) => {
    const body: Record<string, unknown> = {};
    if (opts && typeof opts.guides === 'boolean') body.guides = opts.guides;
    if (opts && Number.isInteger(opts.album)) body.album = opts.album;
    return apiFetch<{ queued: boolean; already: boolean; quick?: { albums: number[]; runner: boolean } }>(`/pdf/regenerate/${shopifyOrderId}`, {
      method: 'POST', ...(Object.keys(body).length ? { body: JSON.stringify(body) } : {}),
    });
  },
  /** The live progress of the quick 3D redraw + where the print stands (admin). */
  quickStatus: (shopifyOrderId: string) =>
    apiFetch<QuickStatusResponse>(`/pdf/quick-status/${shopifyOrderId}`),
  pdfFinalize: (shopifyOrderId: string, albumIndex: number = 1, sourceHash?: string | null,
    /** The album's last_saved_at when the render READ it (#1241) — keeps an
     *  edit saved mid-render from being stamped as already printed. */
    sourceSavedAt?: string | null) =>
    apiFetch<{
      success: boolean;
      cover_pdf_url: string;
      interior_pdf_url: string;
      full_book_pdf_url: string;
      cover_locked?: boolean;
    }>(`/pdf/finalize/${shopifyOrderId}?album=${albumIndex}`, {
      method: 'POST',
      ...(sourceHash ? { body: JSON.stringify({ source_hash: sourceHash, ...(sourceSavedAt ? { source_saved_at: sourceSavedAt } : {}) }) } : {}),
    }),

  // ─── Manual cover replacement (migration 067) ──────────────────────────────
  // Upload goes browser → Supabase signed PUT (the worker never sees the bytes
  // in transit), then finalize makes the worker DOWNLOAD and measure the real
  // file before it is allowed anywhere near the live cover path.
  coverReplaceUploadUrl: (shopifyOrderId: string, albumIndex: number, filename: string) =>
    apiFetch<{
      upload_url: string;
      token: string;
      storage_path: string;
      content_type: string;
    }>(`/pdf/cover-replace/upload-url/${shopifyOrderId}?album=${albumIndex}`, {
      method: 'POST',
      body: JSON.stringify({ filename }),
    }),
  coverReplaceFinalize: (shopifyOrderId: string, albumIndex: number, storagePath: string, filename: string) =>
    apiFetch<{
      success: boolean;
      cover_pdf_url: string;
      format: 'pdf' | 'svg';
      dimensions: { wmm: number; hmm: number };
      previews: string;
    }>(`/pdf/cover-replace/finalize/${shopifyOrderId}?album=${albumIndex}`, {
      method: 'POST',
      body: JSON.stringify({ storage_path: storagePath, filename }),
    }),
  coverReplaceClear: (shopifyOrderId: string, albumIndex: number) =>
    apiFetch<{ success: boolean }>(`/pdf/cover-replace/clear/${shopifyOrderId}?album=${albumIndex}`, {
      method: 'POST',
    }),

  // ─── Manual INTERIOR replacement (migration 068) ───────────────────────────
  // Same three-step shape as the cover: signed PUT to staging → worker
  // downloads, measures every page and only then promotes it.
  interiorReplaceUploadUrl: (shopifyOrderId: string, albumIndex: number, filename: string) =>
    apiFetch<{ upload_url: string; token: string; storage_path: string; content_type: string }>(
      `/pdf/interior-replace/upload-url/${shopifyOrderId}?album=${albumIndex}`, {
        method: 'POST', body: JSON.stringify({ filename }),
      }),
  /** `conversion` is the browser's prepare report (lib/interiorPrepare.ts). It is
   *  REQUIRED for files the worker can't open itself (>25 MB) and is stored on
   *  the album either way as the audit trail. */
  interiorReplaceFinalize: (
    shopifyOrderId: string,
    albumIndex: number,
    storagePath: string,
    filename: string,
    conversion?: Record<string, unknown>,
  ) =>
    apiFetch<{ success: boolean; interior_pdf_url: string; pages: number; previews: string }>(
      `/pdf/interior-replace/finalize/${shopifyOrderId}?album=${albumIndex}`, {
        method: 'POST', body: JSON.stringify({ storage_path: storagePath, filename, conversion }),
      }),
  interiorReplaceClear: (shopifyOrderId: string, albumIndex: number) =>
    apiFetch<{ success: boolean }>(`/pdf/interior-replace/clear/${shopifyOrderId}?album=${albumIndex}`, {
      method: 'POST',
    }),

  // ─── Client-PDF import: spec validation (reject-only) ──────────────────────
  // The import rasterizes in the admin's browser, so the worker never sees the
  // source PDF. We stage it once and ask for a verdict, which lets the server
  // reuse the SAME validators the cover-replace flow uses instead of the rules
  // being duplicated client-side.
  importValidateUploadUrl: (kind: 'cover' | 'interior') =>
    apiFetch<{ upload_url: string; token: string; storage_path: string }>(
      `/pdf/import-validate/upload-url?kind=${kind}`, { method: 'POST' }),
  importValidate: (storagePath: string, kind: 'cover' | 'interior') =>
    apiFetch<{
      ok: boolean;
      kind: 'cover' | 'interior';
      pages: number;
      expected?: { wmm?: number; hmm?: number; trim?: { wmm: number; hmm: number }; bleed?: { wmm: number; hmm: number }; bleedMm?: number };
      actual?: { wmm: number; hmm: number };
      distinct_sizes?: string[];
      offending_pages?: Array<{ page: number; wmm: number; hmm: number }>;
      rotation_normalised?: boolean;
      message?: string;
    }>(`/pdf/import-validate`, {
      method: 'POST',
      body: JSON.stringify({ storage_path: storagePath, kind }),
    }),
  /** Reconcile orders.page_count / extra pages / price after an import, and
   *  snapshot the print cost. Reuses recomputeAndPersistOrderPricing +
   *  computeAlbumCost server-side — nothing re-implemented here. */
  importReprice: (shopifyOrderId: string) =>
    apiFetch<{
      success: boolean;
      total_pages: number | null;
      extra_pages: number | null;
      extra_pages_cost: number | null;
      total_price: number | null;
      print_cost: number | null;
    }>(`/pdf/import-reprice/${shopifyOrderId}`, { method: 'POST' }),

  createManual: (data: {
    customer_name: string;
    customer_phone: string;
    customer_email?: string;
    bundle: 'Solo' | 'Duo' | 'Trio';
    page_count: number;
    total_price?: number;
    layout?: string;
    customer_note?: string;
    destination_1?: string;
    year_1?: string;
    destination_2?: string;
    year_2?: string;
    destination_3?: string;
    year_3?: string;
    origin?: string;
  }) =>
    apiFetch<{ order: Order; success: boolean }>('/orders/manual', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  /** Add a NEW album to an existing order. Inserts an album_sessions row at the
   *  next free album_index, adds `album_price` to total_price and bumps the
   *  bundle/destination columns to match. Returns the new session + the
   *  refreshed order. */
  addAlbum: (
    shopifyOrderId: string,
    data: {
      destination?: string;
      year?: string;
      cover_id?: string | null;
      page_count?: number;
      album_price: number;
    },
  ) =>
    apiFetch<{ album_session: AlbumSession; order: Order }>(
      `/orders/${shopifyOrderId}/albums`,
      { method: 'POST', body: JSON.stringify(data) },
    ),
  /** Remove an album from an existing order by its album_index. Renumbers the
   *  remaining albums, reprices by tier (Solo/Duo/Trio + extra pages), deletes
   *  that album's photos and syncs Shopify. Refuses (400) if it's the last
   *  album. Returns the refreshed order. */
  removeAlbum: (shopifyOrderId: string, albumIndex: number) =>
    apiFetch<{ order: Order; removed_index: number; shopify_synced: boolean }>(
      `/orders/${shopifyOrderId}/albums/${albumIndex}`,
      { method: 'DELETE' },
    ),
  delete: (
    shopifyOrderId: string,
    opts?: { sync_shopify?: boolean; sync_ameex?: boolean },
  ) =>
    apiFetch<{
      ok: boolean;
      shopify_order_name?: string;
      photos_deleted: number;
      pdfs_deleted: number;
      shopify_action: 'draft_deleted' | 'cancelled' | 'skipped' | 'failed' | 'none';
      ameex_action: 'cancelled' | 'skipped' | 'failed' | 'none';
      error?: string;
    }>(`/orders/${shopifyOrderId}`, {
      method: 'DELETE',
      body: JSON.stringify(opts ?? {}),
    }),
  bulkDelete: (
    shopifyOrderIds: string[],
    opts?: { sync_shopify?: boolean; sync_ameex?: boolean },
  ) =>
    apiFetch<{
      ok: boolean;
      total: number;
      deleted: number;
      failed: number;
      results: Array<{
        id: string;
        ok: boolean;
        shopify_order_name?: string;
        photos_deleted: number;
        pdfs_deleted: number;
        shopify_action: string;
        ameex_action: string;
        error?: string;
      }>;
    }>('/orders/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ shopify_order_ids: shopifyOrderIds, ...(opts ?? {}) }),
    }),
  /** `bytes_freed` is summed from `storage.objects` BEFORE the delete — the
   *  `photos.file_size_kb` column is null on every WhatsApp-ingested row, so
   *  it would report a confident zero for exactly the photos taking the space. */
  runCleanup: () => apiFetch<{
    success: boolean; photos_deleted: number; pdfs_deleted: number; bytes_freed: number;
    /** Still eligible after this run — one pass is capped so it cannot time out
     *  half-done. Non-zero means "run it again", and saying so is the whole
     *  point: it used to report 1000 deleted with 6,333 left and no hint. */
    photos_remaining: number;
    /** Delivered orders whose PDFs were deliberately KEPT because the setting is
     *  "manuellement uniquement". Explains an otherwise alarming "0 PDFs". */
    pdfs_skipped_manual: number;
    /** WhatsApp copies the test mode (« dry_run ») counted and did NOT delete. */
    whatsapp_copies_would_delete?: number;
  }>('/cleanup/run', { method: 'POST' }),
  previewCleanup: () => apiFetch<{ photo_count: number; pdf_count: number }>('/cleanup/preview'),
  /** Migration 280: one row per order whose photos were deleted (or counted in test mode). */
  cleanupLog: () => apiFetch<{ rows: PhotoDeletionRow[] }>('/cleanup/log'),
  /** The photo dedup for the manager (migration 267): counts + the pairs. */
  photoDedup: (shopifyOrderId: string) =>
    apiFetch<PhotoDedupSummary>(`/orders/${shopifyOrderId}/photo-dedup`),
  /** « Retirer de l'album » — a duplicate placed twice leaves its page. */
  removeDuplicateFromAlbum: (shopifyOrderId: string, photoId: string) =>
    apiFetch<{ ok: boolean; error?: string }>(`/orders/${shopifyOrderId}/photo-dedup/${photoId}/remove-from-album`, { method: 'POST' }),
  photosSummary: (shopifyOrderId: string) =>
    apiFetch<{
      sessions: Array<{ id: string; album_index: number; destination: string | null; year: string | null; cover_destination_text?: string | null; title_declined?: boolean | null }>;
      photos: Array<{
        id: string;
        album_session_id: string;
        submission_batch_id: string | null;
        submitted_at: string | null;
        created_at: string;
      }>;
    }>(`/orders/${shopifyOrderId}/photos-summary`),
  followUp: (shopifyOrderId: string) =>
    apiFetch<{
      customer: { name: string | null; phone: string | null };
      destinations: { 1: string | null; 2: string | null; 3: string | null };
      bundle: string | null;
      status: string;
      ameex_status: string | null;
      // Payment fields drive the Album Soumis deposit amount (resolveDepositAmount).
      advance_amount: number | null;
      total_price: number | null;
      payment_method: 'cod' | 'virement' | null;
      sessions: Array<{
        id: string;
        album_index: number;
        cover_id: string | null;
        pages: unknown | null;
        last_saved_at: string | null;
      }>;
      photo_counts: Record<string, number>;
      resume_token: string;
      continue_url: string;
    }>(`/orders/${shopifyOrderId}/follow-up`),
  pendingFollowUp: () =>
    apiFetch<{
      orders: Array<{
        shopify_order_id: string;
        shopify_order_name: string;
        customer_name: string | null;
        customer_phone: string | null;
        destinations: { 1: string | null; 2: string | null; 3: string | null };
        bundle: string | null;
        status: string;
        created_at: string;
        sub_state: string;
        last_touched_album: number;
        resume_token: string;
        continue_url: string;
        // Payment fields for the Album Soumis deposit amount.
        advance_amount: number | null;
        total_price: number | null;
        payment_method: 'cod' | 'virement' | null;
        // Most recent WhatsApp follow-up for this order (null = never relancé).
        last_follow_up: {
          sent_at: string;
          sent_by_name: string | null;
          /** Ladder rung last sent — drives the "à envoyer aujourd'hui" view.
           *  Null on rows written before 2026-08-12; those restart at 1. */
          attempt?: number | null;
          status_at_send: string | null;
        } | null;
      }>;
    }>('/orders/follow-up/pending'),
  // Append-only record of a WhatsApp follow-up send (Relances page). Fire-and-
  // forget; the admin identity is captured from the JWT server-side.
  /** `subState` is what drives the relance ladder. Passing only the order
   *  status (the old signature) is why 92% of follow-ups were logged as
   *  `pending-album` and every client kept receiving message #1. */
  recordFollowUp: (shopifyOrderId: string, statusAtSend: string, subState?: string) =>
    apiFetch<{ ok: true; sub_state: string | null; attempt: number | null }>(
      `/orders/${shopifyOrderId}/follow-up/record`, {
        method: 'POST',
        body: JSON.stringify({ status_at_send: statusAtSend, sub_state: subState ?? statusAtSend }),
      }),
  getPdfStatus: (shopifyOrderId: string) =>
    apiFetch<{ pdf_status: string | null; cover_pdf_url: string | null; interior_pdf_url: string | null; full_book_pdf_url: string | null }>(`/pdf/status/${shopifyOrderId}`),
  getStats: (params?: { from?: string; to?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return apiFetch<{
      stats: {
        // Cash position
        cashCollected: number;
        cashInTransit: number;
        cashLost: number;
        profitDelivered: number;
        profitMargin: number | null;
        costDelivered: number;
        advanceAmount: number;
        // Pipeline funnel
        funnel: {
          pendingAlbum: number;
          albumSubmitted: number;
          printedNotShipped: number;
          shipped: number;
          delivered: number;
        };
        // Shipment counts
        deliveredCount: number;
        inTransitCount: number;
        lostCount: number;
        // Actions
        actionsCount: {
          stalled: number;
          awaitingValidation: number;
          readyToShip: number;
          stuckInTransit: number;
          missingCoverSearched: number;
          /** Clients who asked for changes on their 3D preview and are waiting
           *  on US. Optional — older worker versions do not send it. */
          changesRequested?: number;
        };
        actions: Array<
          | { kind: 'stalled-pending'; severity: 'warning'; shopify_order_id: string; customer_name: string | null; customer_phone: string | null; days_since_created: number }
          | { kind: 'awaiting-validation'; severity: 'info'; shopify_order_id: string; customer_name: string | null; customer_phone: string | null }
          | { kind: 'ready-to-ship'; severity: 'info'; shopify_order_id: string; customer_name: string | null; total_price: number }
          | { kind: 'stuck-in-transit'; severity: 'error'; shopify_order_id: string; customer_name: string | null; ameex_status: string | null; ameex_parcel_code: string | null; days_since_event: number }
          | { kind: 'missing-cover-searched'; severity: 'info'; destination: string; query_norm: string; search_count: number; client_count: number; sample: Array<{ name: string | null; phone: string | null; shopify_order_id: string | null }> }
        >;
        // Operational KPIs
        deliveryRate: number | null;
        lossRate: number | null;
        avgDeliveryDays: number | null;
        // Customer signal
        repeatCustomers: number;
        topClients: Array<{ name: string; phone: string; orders: number; revenue: number; delivered_count: number; delivered_revenue: number }>;
        // Previous-period comparison (for trend deltas). Null when range is "all".
        previous: {
          from: string | null;
          to: string | null;
          cashCollected: number;
          cashInTransit: number;
          cashLost: number;
          deliveredCount: number;
          inTransitCount: number;
          lostCount: number;
          orderCount: number;
          profitDelivered: number;
        } | null;
        // Legacy + chart
        totalRevenue: number;
        totalOrders: number;
        printed: number;
        submitted: number;
        pending: number;
        totalCost: number;
        totalProfit: number;
        deliveredRevenue: number;
        deliveredCost: number;
        deliveredProfit: number;
        costPerBook: number;
        byDay: Record<string, { orders: number; revenue: number }>;
        bundles: Record<string, number>;
        // Traffic-source breakdown — count + revenue per acquisition channel.
        bySource: Record<string, { orders: number; revenue: number }>;
      };
    }>(`/orders/stats/dashboard${qs}`);
  },
};

// ─── Redo (réimpression) API ──────────────────────────────────────────────────
// Admin marks part of an order for reprint. Cost is internal-only tracking;
// PDFs are regenerated client-side and uploaded via the redo-pdf signed pair.
export const redosApi = {
  /** List redos for an order (newest first). The order-detail GET already
   *  inlines `redos`, so this is the post-mutation refresh source. */
  list: (shopifyOrderId: string) =>
    apiFetch<{ redos: OrderRedo[] }>(`/orders/${shopifyOrderId}/redos`),

  /** Create a redo. Snapshots the order's current status, computes the internal
   *  cost, inserts the redo (status 'pending'), and flips the order to
   *  'in-redo'. */
  create: (
    shopifyOrderId: string,
    data: {
      album_index: number;
      scope: RedoScope;
      page_indices?: number[];
      reason: RedoReason;
      reason_detail?: string;
    },
  ) =>
    apiFetch<{ redo: OrderRedo }>(`/orders/${shopifyOrderId}/redos`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** Patch a redo — set PDF URLs + status 'ready' after client generation, or
   *  flip to 'sent-to-printer'. */
  update: (
    redoId: string,
    data: { status?: RedoStatus; cover_pdf_url?: string; interior_pdf_url?: string },
  ) =>
    apiFetch<{ redo: OrderRedo }>(`/orders/redos/${redoId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /** Complete a redo: status 'completed', stamps completed_at, restores the
   *  order to its pre-redo status. */
  complete: (redoId: string, paymentOverrideReason?: string) =>
    apiFetch<{ redo: OrderRedo; restored_status: string }>(
      `/orders/redos/${redoId}/complete`,
      { method: 'POST', ...(paymentOverrideReason ? { body: JSON.stringify({ payment_override_reason: paymentOverrideReason }) } : {}) },
    ),

  /** Sign a one-shot upload URL for a redo PDF (cover|interior). */
  pdfUploadUrl: (orderId: string, redoId: string, kind: 'cover' | 'interior') =>
    apiFetch<{ upload_url: string; token: string; storage_path: string }>(
      '/orders/redo-pdf/upload-url',
      { method: 'POST', body: JSON.stringify({ order_id: orderId, redo_id: redoId, kind }) },
    ),

  /** Turn an uploaded redo PDF into a long-expiry signed download URL. */
  pdfFinalize: (storage_path: string) =>
    apiFetch<{ ok: boolean; download_url: string }>(
      '/orders/redo-pdf/finalize',
      { method: 'POST', body: JSON.stringify({ storage_path }) },
    ),
};

// ─── AMEEX delivery API ──────────────────────────────────────────────────────
// All four endpoints are admin-only on the worker; the parcel POST has two
// modes: with `parcel_code_override` we just attach an existing AMEEX
// parcel code (when admin created it in the AMEEX dashboard directly),
// otherwise we call AMEEX Add Parcel and store whatever it returns.

export const ameexApi = {
  createParcel: (
    shopifyOrderId: string,
    input: {
      city_id?: number;
      receiver_override?: string;
      phone_override?: string;
      address_override?: string;
      cod_override?: number;
      /** What the admin says was received for THIS parcel (the card's
       *  acompte field). When omitted the worker reads the money recorded on
       *  the order — never a settings default (money bug 3, 2026-09-29):
       *  COD = total − what was actually received, 0 when paid in full. */
      advance_override?: number;
      comment?: string;
      /** Per-order AMEEX form overrides. Each defaults to its
       *  app_settings ameex_default_* counterpart. */
      product_override?: string;
      fragile_override?: boolean;
      open_override?: boolean;
      try_override?: boolean;
      /** Ship BEFORE the date the customer asked for. The worker refuses with
       *  409 while `parcel_hold_until` is ahead; this is the deliberate way
       *  past it, for the customer who rings up and says send it now. */
      force?: boolean;
      /** A new parcel in place of one AMEEX has locked (2026-10-07). */
      replace_locked?: boolean;
    },
  ) =>
    apiFetch<{ ok: boolean; parcel_code: string | null; ameex_response: unknown }>(
      `/orders/${shopifyOrderId}/ameex/parcel`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  editParcel: (
    shopifyOrderId: string,
    input: {
      city_id?: number;
      receiver?: string;
      phone?: string;
      address?: string;
      cod?: number;
      comment?: string;
    },
  ) =>
    apiFetch<{ ok: boolean }>(`/orders/${shopifyOrderId}/ameex/parcel`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  /** `localOnly` — AMEEX refused: the order lets go of the code, the parcel stays at AMEEX. */
  cancelParcel: (shopifyOrderId: string, localOnly = false) =>
    apiFetch<{ ok: boolean }>(`/orders/${shopifyOrderId}/ameex/parcel${localOnly ? '?local=1' : ''}`, { method: 'DELETE' }),
  setRefundReceived: (shopifyOrderId: string, received: boolean) =>
    apiFetch<{ ok: boolean; received: boolean }>(
      `/orders/${shopifyOrderId}/ameex/refund-received`,
      { method: 'PATCH', body: JSON.stringify({ received }) },
    ),
  /** Attach an externally-created parcel code (admin pasted it from AMEEX UI). */
  attachManual: (shopifyOrderId: string, parcelCode: string, cityId?: number) =>
    apiFetch<{ ok: boolean }>(`/orders/${shopifyOrderId}/ameex/manual`, {
      method: 'POST',
      body: JSON.stringify({ parcel_code: parcelCode, city_id: cityId ?? null }),
    }),
  events: (shopifyOrderId: string) =>
    apiFetch<{ events: AmeexStatusEvent[] }>(`/orders/${shopifyOrderId}/ameex/events`),
  /** The delivery page (2026-10-10): numbers, lanes, « À expédier », « À vérifier ». */
  board: () => apiFetch<AmeexBoard>('/ameex/board'),
  /** One parcel, read live from AMEEX: timeline, courier, COD, what the agent did. */
  parcel: (code: string) => apiFetch<AmeexParcelDetail>(`/ameex/parcel/${encodeURIComponent(code)}`),
  dashboard: () =>
    apiFetch<{
      counts: Record<AmeexCanonicalStatus, number>;
      ready: Array<Pick<Order,
        'id' | 'shopify_order_id' | 'shopify_order_name' | 'customer_name' |
        'customer_phone' | 'customer_email' | 'customer_note' | 'bundle' |
        'total_price' | 'status' | 'created_at'>>;
      /** A month old and never shipped (worker lib/ameexReady.ts). */
      toCheck?: Array<Pick<Order,
        'id' | 'shopify_order_id' | 'shopify_order_name' | 'customer_name' |
        'total_price' | 'status' | 'created_at'> & { missing: string[] }>;
      /** Still being made: not approved, not printed or no full address. */
      notYet?: number;
      active: Array<Pick<Order,
        'id' | 'shopify_order_id' | 'shopify_order_name' | 'customer_name' |
        'customer_phone' | 'ameex_parcel_code' | 'ameex_city_id' |
        'ameex_status' | 'ameex_status_sub' | 'ameex_status_comment' |
        'ameex_status_color' | 'ameex_last_event_at' | 'ameex_refund_received' |
        'total_price' | 'status' | 'created_at'>>;
      returnsShelf: Array<Pick<Order,
        'id' | 'shopify_order_id' | 'shopify_order_name' | 'customer_name' |
        'customer_phone' | 'ameex_parcel_code' | 'ameex_status' |
        'ameex_status_comment' | 'ameex_last_event_at' | 'total_price'>>;
      totalParcels: number;
    }>('/ameex/dashboard'),
  /** End-of-day batch: create AMEEX parcels for every selected order, open
   *  a delivery note (bordereau), return URLs the admin can hit to print
   *  every label + the manifest. */
  batchConfirm: (shopifyOrderIds: string[], labelType?: string) =>
    apiFetch<{
      ok: boolean;
      batch_ref: string;
      delivery_note_created: boolean;
      label_type: string;
      labels_url: string | null;
      manifest_url: string | null;
      created: number;
      skipped: number;
      failed: number;
      results: Array<
        | { shopify_order_id: string; status: 'created'; parcel_code: string | null }
        | { shopify_order_id: string; status: 'skipped'; reason: string; parcel_code?: string | null }
        | { shopify_order_id: string; status: 'failed'; error: string }
      >;
    }>('/ameex/batch-confirm', {
      method: 'POST',
      body: JSON.stringify({ shopify_order_ids: shopifyOrderIds, label_type: labelType }),
    }),
  /** Force a MassTracking sync against AMEEX right now. Same logic the 6h
   *  cron runs — pulls statuses for any non-terminal parcels. */
  reconcile: () =>
    apiFetch<{ ok: boolean; checked?: number; updated?: number }>(
      '/ameex/reconcile',
      { method: 'POST' },
    ),
  /** Per-city delivery health — pulled live from AMEEX (lifetime). */
  insightsByCity: () =>
    apiFetch<{
      source: 'ameex_live';
      fetched_at: string;
      cities: Array<{
        city: string;
        total: number;
        delivered: number;
        returned: number;
        refused: number;
        in_transit: number;
        revenue: number;
        delivery_rate: number | null;
        loss_rate: number | null;
      }>;
    }>('/ameex/insights-by-city'),
  /** Profit-relevant KPIs derived from our local mirror of AMEEX parcel
   *  status. `from`/`to` are ISO timestamps; omit both for lifetime. */
  insights: (range?: { from?: string; to?: string }) => {
    const qs = new URLSearchParams();
    if (range?.from) qs.set('from', range.from);
    if (range?.to)   qs.set('to', range.to);
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiFetch<{
      source: 'ameex_live';
      range: { from: string | null; to: string | null };
      totals: {
        parcels: number;
        delivered: number;
        in_transit: number;
        returned: number;
        refused: number;
      };
      revenue: {
        collected: number;
        in_transit: number;
      };
      delivery_rate: number | null;
      counts_by_status: Record<string, number>;
      top_cities: Array<{ city_name: string; count: number; revenue: number }>;
      fetched_at: string;
    }>(`/ameex/insights${suffix}`);
  },
};

// ─── Covers API ───────────────────────────────────────────────────────────────

export interface TextZone {
  type: 'destination' | 'year' | 'custom';
  x: number;      // 0-1 (relative to cover width)
  y: number;      // 0-1
  width: number;  // 0-1
  height: number; // 0-1
  font_size?: number;
  font_family?: string;
  color?: string;
  align?: 'left' | 'center' | 'right';
  default_text?: string; // extracted from PDF
}

export interface Cover {
  id: string;
  name: string;
  categories: string[];
  product_types: string[];
  storage_path: string | null;
  thumbnail_url: string | null;
  /** Optional transparent PNG (illustration only, no baked-in background).
   *  When present the editor composites `dominant_color` + this image so the
   *  customer's color picks actually recolor the front face. Null falls back
   *  to thumbnail_url with the legacy baked-in bg behaviour. */
  design_url: string | null;
  /** Pre-generated ICON asset (design trimmed to its artwork bbox, ≤800px WebP
   *  w/ alpha — scripts/generate_cover_icons.py). Grids + page elements prefer
   *  it; full-res design_url stays the cover-print source. */
  icon_url?: string | null;
  format: string | null;
  text_zones: TextZone[] | null;
  dominant_color: string | null;
  width_px: number | null;
  height_px: number | null;
  back_cover_style: 'solid' | 'blank' | 'photo' | 'mirrored' | null;
  description: string | null;
  search_tags: string[] | null;
  usage_count: number | null;
  is_visible: boolean;
  /** « Visible clients » in the public catalog (migration 252; missing = visible). */
  public_visible?: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CoverCategory {
  id: string;
  slug: string;
  label_fr: string;
  icon: string | null;
  sort_order: number;
}

export interface CoverUploadUrlResponse {
  upload_url: string;
  token: string;
  storage_path: string;
  public_url: string;
  cover_id: string;
  content_type: string;
}

/** One cover as the PUBLIC catalog shows it (worker lib/coverCatalog.ts publicCover) — no internal field. */
export interface CatalogCover {
  id: string; slug: string; name: string; categories: string[];
  thumbnail_url: string | null; thumbnail_url_hd: string | null;
  design_url: string | null; design_url_hd: string | null;
  dominant_color: string | null; text_zones: unknown; width_px: number | null; height_px: number | null;
}
export interface CatalogPage {
  enabled: boolean; preview?: boolean;
  categories?: Array<{ slug: string; label: string; icon: string | null }>;
  cat?: string | null; covers?: CatalogCover[]; more?: boolean;
}
/** The public cover catalog (app.storiz.ma/couvertures, 2026-10-17) — ships OFF. */
export const catalogApi = {
  list: (cat: string | null, offset = 0) =>
    apiFetch<CatalogPage>(`/catalog?${new URLSearchParams({ ...(cat ? { cat } : {}), offset: String(offset) })}`),
  /** The team's view while the catalog is OFF. */
  preview: (cat: string | null, offset = 0) =>
    apiFetch<CatalogPage>(`/catalog/preview?${new URLSearchParams({ ...(cat ? { cat } : {}), offset: String(offset) })}`),
  event: (id: string, kind: 'view' | 'choose') =>
    apiFetch<{ ok: boolean }>(`/catalog/${id}/event`, { method: 'POST', body: JSON.stringify({ kind }) }).catch(() => ({ ok: false })),
  whatsapp: () => apiFetch<{ pool: Array<{ n: string; w: number }> }>('/catalog/whatsapp'),
  stats: () => apiFetch<{ stats: Record<string, { views: number; chooses: number }>; missing: boolean }>('/catalog/stats'),
};

export const coversApi = {
  list: (params?: { category?: string; product_type?: string; include_hidden?: boolean }) => {
    const qs: Record<string, string> = {};
    if (params?.category) qs.category = params.category;
    if (params?.product_type) qs.product_type = params.product_type;
    if (params?.include_hidden) qs.include_hidden = '1';
    const queryString = Object.keys(qs).length
      ? '?' + new URLSearchParams(qs).toString()
      : '';
    return apiFetch<{ covers: Cover[] }>(`/covers${queryString}`);
  },

  get: (id: string) => apiFetch<{ cover: Cover }>(`/covers/${id}`),

  create: (data: Partial<Cover>) =>
    apiFetch<{ cover: Cover }>('/covers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<Cover>) =>
    apiFetch<{ cover: Cover }>(`/covers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiFetch<{ success: boolean }>(`/covers/${id}`, { method: 'DELETE' }),

  getUploadUrl: (filename: string, content_type: string) =>
    apiFetch<CoverUploadUrlResponse>('/covers/upload-url', {
      method: 'POST',
      body: JSON.stringify({ filename, content_type }),
    }),

  reorder: (items: { id: string; sort_order: number }[]) =>
    apiFetch<{ success: boolean }>('/covers/reorder', {
      method: 'POST',
      body: JSON.stringify({ items }),
    }),

  /** Ask the worker for up to 5 cover suggestions matching the query
   *  when the local substring search returned zero results. Calls
   *  Gemini server-side (~$0.001/call). Returns [] on failure. */
  suggestSimilar: (query: string) =>
    apiFetch<{ covers: Cover[] }>('/covers/suggest-similar', {
      method: 'POST',
      body: JSON.stringify({ query }),
    }),

  // Categories
  categories: () =>
    apiFetch<{ categories: CoverCategory[] }>('/covers/categories'),

  createCategory: (data: { slug: string; label_fr: string; icon?: string; sort_order?: number }) =>
    apiFetch<{ category: CoverCategory }>('/covers/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateCategory: (id: string, data: Partial<CoverCategory>) =>
    apiFetch<{ category: CoverCategory }>(`/covers/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteCategory: (id: string) =>
    apiFetch<{ success: boolean }>(`/covers/categories/${id}`, { method: 'DELETE' }),
};

// ─── Cover-search tracking + missing-cover fulfill (Task 4) ───────────────────
export const coverSearchesApi = {
  /** Fire-and-forget: log ONE debounced cover search from the client picker.
   *  found_cover_id = top local match's id, or null = no-result (a destination
   *  we don't have). Never throws — callers ignore failures. */
  record: (body: {
    shopify_order_id?: string;
    album_index?: number;
    query_text: string;
    found_cover_id?: string | null;
    customer_phone?: string | null;
    customer_name?: string | null;
  }) =>
    apiFetch<{ ok: boolean }>('/covers/cover-searches/record', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Admin: the new cover for `query_norm`/`query_text` is now available. Pins it
   *  for every order that searched it + marks those searches fulfilled, and
   *  returns the distinct clients so the UI can send the "now available" WA
   *  follow-up. */
  fulfill: (body: { cover_id: string; query_norm?: string; query_text?: string }) =>
    apiFetch<{
      query_norm: string;
      pinned_count: number;
      clients: Array<{ shopify_order_id: string | null; order_id: string; customer_name: string | null; customer_phone: string | null }>;
    }>('/covers/cover-searches/fulfill', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export const coverPinsApi = {
  /** Public: the cover (if any) pinned to the top of THIS order's picker. */
  get: (shopifyOrderId: string) =>
    apiFetch<{ cover_id: string | null }>(`/covers/cover-pins/${shopifyOrderId}`),
};

// ─── Layout Templates API ─────────────────────────────────────────────────────

export type LayoutTemplateAlbumCategory =
  | 'travel' | 'baby' | 'wedding' | 'birthday'
  | 'graduation' | 'pet' | 'family' | 'religious';

/** Maps the legacy single English album_category enum to the unified
 *  cover_categories slug taxonomy (see `coversApi.categories()`).
 *  Used as a display/init fallback while `album_categories` is empty. */
export const LEGACY_ALBUM_CATEGORY_TO_SLUG: Record<LayoutTemplateAlbumCategory, string> = {
  travel: 'voyage',
  baby: 'naissance',
  wedding: 'mariage',
  birthday: 'anniversaire',
  graduation: 'etudes',
  pet: 'autre',
  family: 'famille',
  religious: 'religieux',
};

export interface LayoutTemplateSlot {
  x: number;      // 0-1 page-relative bbox
  y: number;      // 0-1
  width: number;  // 0-1
  height: number; // 0-1
  rotation?: number;  // degrees, CSS-CW
  z?: number;         // stacking within Layer 2 (document order)
  /** SVG path data in slot-LOCAL unit coords (0-1 of bbox).
   *  null/absent = rectangular slot (current behavior). */
  clip_path?: string | null;
  /** Index of the corresponding <image> element in the template's original SVG
   *  document order. Populated by the SVG-href-swap pipeline (Task 6) so the
   *  pipeline can target the right placeholder image. */
  dom_index?: number;
}

export interface LayoutTemplate {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  preview_image_url: string | null;
  background_storage_path: string | null;
  /** Resolvable public URL for the background PNG (set by the worker; not
   *  a real DB column). */
  background_url: string | null;
  background_color: string | null;
  slots: LayoutTemplateSlot[];
  tags: string[];
  page_aspect: string;
  is_visible: boolean;
  sort_order: number;
  slot_count: number;
  album_category: LayoutTemplateAlbumCategory | null;
  /** Unified multi-category taxonomy — slugs from the `cover_categories`
   *  table. Empty array = usable for all categories (agnostic). */
  album_categories?: string[];
  is_ai_eligible: boolean;
  // Phase 2 — SVG-from-Canva pipeline. `*_path` fields are the real DB
  // columns; `*_url` fields are decorated by the worker (not in the DB) and
  // are what the editor / preview / PDF render code reads.
  original_svg_path: string | null;
  original_svg_url: string | null;
  overlay_svg_path: string | null;
  overlay_svg_url: string | null;
  overlay_print_png_path: string | null;
  overlay_print_png_url: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface LayoutTemplateUploadUrlResponse {
  upload_url: string;
  token: string;
  storage_path: string;
  public_url: string;
  content_type: string;
}

export const templatesApi = {
  list: (params?: {
    category?: LayoutTemplateAlbumCategory;
    ai_eligible?: boolean;
    include_hidden?: boolean;
  }) => {
    const qs: Record<string, string> = {};
    if (params?.category) qs.category = params.category;
    if (params?.ai_eligible) qs.ai_eligible = '1';
    if (params?.include_hidden) qs.include_hidden = '1';
    const queryString = Object.keys(qs).length
      ? '?' + new URLSearchParams(qs).toString()
      : '';
    return apiFetch<{ templates: LayoutTemplate[] }>(`/templates${queryString}`);
  },

  get: (id: string) =>
    apiFetch<{ template: LayoutTemplate }>(`/templates/${id}`),

  create: (data: Partial<LayoutTemplate> & { name: string; slug: string }) =>
    apiFetch<{ template: LayoutTemplate }>('/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<LayoutTemplate>) =>
    apiFetch<{ template: LayoutTemplate }>(`/templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiFetch<{ success: boolean }>(`/templates/${id}`, { method: 'DELETE' }),

  getUploadUrl: (filename: string, content_type: string) =>
    apiFetch<LayoutTemplateUploadUrlResponse>('/templates/upload-url', {
      method: 'POST',
      body: JSON.stringify({ filename, content_type }),
    }),

  bulk: (
    action: 'set_ai_eligible' | 'set_category' | 'set_visibility'
          | 'add_category' | 'add_categories' | 'clear_categories'
          | 'add_tags' | 'remove_tags' | 'delete' | 'redetect',
    template_ids: string[],
    payload?: Record<string, unknown>,
  ) =>
    apiFetch<{
      results: Array<{ id: string; ok: boolean; error?: string }>;
      summary: { ok: number; failed: number };
    }>('/templates/bulk', {
      method: 'POST',
      body: JSON.stringify({ action, template_ids, payload }),
    }),

  classifyCandidates: (
    candidates: Array<{ id: string; thumbnailDataUrl: string }>,
  ) =>
    apiFetch<{
      results: Array<{
        id: string;
        prediction: 'SLOT' | 'DECOR';
        confidence: number;
        reasoning: string;
      }>;
    }>('/templates/classify-candidates', {
      method: 'POST',
      body: JSON.stringify({ candidates }),
    }),
};

// ─── Settings API ─────────────────────────────────────────────────────────────

export interface AppSettings {
  /** The render pages download through the worker's R2 copy (migration 269). Absent = OFF — lib/renderFetch.ts. */
  render_photo_cache_enabled?: boolean;
  /** « Nouvel éditeur mobile » for every phone (migration 257, NOT applied). Absent = OFF — see lib/mobileEditorV2.ts. */
  mobile_editor_v2?: boolean;
  /** The public cover catalog app.storiz.ma/couvertures (migration 252) — OFF until the owner switches it on. */
  cover_catalog_enabled?: boolean;
  /** Quick 3D preview after « Régénérer » (migration 253). Absent = ON; false switches it off without a deploy. */
  quick_preview_enabled?: boolean;
  /** COUPONS (migration 282, NOT applied) — all three absent/false = zero behaviour change.
   *  « Imprimer les coupons dans les albums ». */
  coupons_enabled?: boolean;
  /** « Accepter les codes sur le site » (checkout field + admin apply). */
  coupon_redeem_enabled?: boolean;
  /** « Page de fin dans l'éditeur » (the « + » menu's outro page). */
  editor_outro_page_enabled?: boolean;
  /** How the printer turns the sheet for the backs: 'long' (bord long, default) | 'short'. */
  coupon_duplex_flip?: 'long' | 'short';
  /** Duplex offset applied to the coupon backs, in mm (default 0). */
  coupon_offset_x_mm?: number;
  coupon_offset_y_mm?: number;
  id: number;
  logo_url: string | null;
  /** Branding page prepended before the customer's content in the interior PDF. */
  intro_page_1_url: string | null;
  /** Branding page appended after the customer's content in the interior PDF. */
  outro_page_2_url: string | null;
  default_page_count: number;
  price_per_page: number;
  /** Commission (MAD) per confirmed order — copied onto each confirmation at
   *  click time, so changing it never alters past earnings. */
  agent_commission_rate?: number;
  /** Claim-based assignment (2026-08-11). The cap ships OFF — with one agent a
   *  limit only obstructs her. Auto-release runs regardless of the cap. */
  agent_hold_cap_enabled?: boolean;
  agent_hold_cap?: number;
  /** WhatsApp assistant allowlist (2026-08-13, `TestNumbersCard`).
   *  Canonicalised (`212XXXXXXXXX`) phone numbers.
   *
   *  Two paths read this and they differ on the EMPTY case, deliberately:
   *   · conversational replies (worker/src/lib/agentReply.ts,
   *     `isAllowlistedPhone`) — EMPTY sends to NOBODY. It gates a capability
   *     that types into a live support line, so "no allowlist" must fail
   *     closed. This is what `TestNumbersCard` describes.
   *   · confirmation-agent journey steps (worker/src/lib/agentOutbox.ts,
   *     `isTestNumberMatch`) — EMPTY means "no per-chat restriction", the
   *     older rule, kept because that queue's real switch is elsewhere. */
  agent_test_numbers?: string[] | null;
  /** Master switch for ACTUALLY SENDING assistant replies (2026-08-14,
   *  migration 089). Independent of `assistant_enabled`, which only governs
   *  whether a draft is written: the intended first-days mode is drafting ON
   *  and sending OFF, so the owner can read what the model would have said
   *  without a single customer receiving it. Default false. */
  assistant_send_enabled?: boolean;
  /** WhatsApp number that receives internal alerts (a customer file that did
   *  not arrive). Null/empty disables alerting entirely. */
  manager_whatsapp?: string | null;
  /** Where a delivered customer is sent to leave a public review (2026-08-17,
   *  migration 103). EMPTY IS THE OFF SWITCH for the whole post-delivery
   *  message — the worker refuses to send a review request with no link, the
   *  same way it refuses a payment message with no RIB, so the 10%-off line
   *  never goes out either. Set in `TestNumbersCard`. */
  review_link?: string | null;
  /** Go quiet automatically the moment anyone types to the customer from the
   *  phone (2026-08-17, migration 105). ⚠️ Needs the OpenWA gateway to forward
   *  OUTBOUND messages; until it does, the per-chat pause button is the only
   *  trigger and this switch does nothing. */
  agent_pause_on_human_reply?: boolean;
  /** The WhatsApp Business app's own automatic messages on our line (greeting /
   *  away), migration 259. An outgoing message with one of these texts is
   *  labelled « Message auto », never pauses the agent and never counts as an
   *  answer. Edited in `TestNumbersCard`. */
  wa_auto_reply_texts?: string[] | null;
  /** Compensation offered on the NEXT order to a customer whose complaint they
   *  have evidenced with a photo. Owner's figure: 20. Never applied to the
   *  current order — that is a refund, and a refund is a human decision. */
  agent_unhappy_discount_pct?: number;
  /** How many DISTINCT CONVERSATIONS the assistant may message per rolling 24h
   *  (migration 112). ⚠️ Conversations, not messages — a customer who receives
   *  six messages in a day counts as one. Default 5. */
  agent_daily_chat_cap?: number;
  /** Incident banner (migration 114). When on, the assistant repeats
   *  `announcement_text` VERBATIM in every reply, in addition to its normal
   *  answer — never instead of it. */
  announcement_enabled?: boolean;
  announcement_text?: string | null;
  /** ISO. Auto-off. NULL = until switched off by hand. */
  announcement_until?: string | null;
  /** Let the assistant answer "where is my order?" from the real status, in
   *  deliberately broad terms (three customer-facing stages, never a date).
   *  Off = it says a human will check. */
  agent_status_replies_enabled?: boolean;
  /** Download customer photos from the gateway. Its OWN switch, separate from
   *  the photo wait, after raising that wait to stop a runaway downloader also
   *  silenced every reply (2026-08-15). */
  assistant_media_pull_enabled?: boolean;
  /** Whether the assistant may turn a completed WhatsApp intake into a real
   *  order row. Default false — this one governs production and money. */
  assistant_create_orders_enabled?: boolean;
  /** Owner, 2026-10-08: every 3D preview waits for the manager’s OK (migration 224). Off by default. */
  preview_approval_required?: boolean;
  /** Owner, 2026-10-23: the agent changes the cover title font (8 numbered fonts, picture
   *  « Polices couverture ») — migration 266. Off by default; missing column = off. */
  agent_cover_font_enabled?: boolean;
  /** Owner, 2026-10-23: the agent re-frames photos whose faces are cut when the client
   *  complains (« les photos sont coupées ») — migration 266. Off by default. */
  agent_photo_reframe_enabled?: boolean;
  /** Owner, 2026-10-10: an order is created only from photos or money (migration 226). Off by default. */
  order_needs_photos_or_money?: boolean;
  /** Stamped by the database when the rule above is switched on. */
  order_rule_on_at?: string | null;
  /** Owner, 2026-10-10: manager alerts rebuild — 6 pings, 9 h / 18 h summary, panel (migration 229). Off by default. */
  owner_alerts_v2?: boolean;
  /** Confirmation fixes (migration 231), each off by default: the price shown with the preview (fix 2),
   *  no false status messages (fix 3), alerts when a client waits on us (fix 4). */
  price_before_approval?: boolean;
  status_claim_gate?: boolean;
  waiting_on_us_alerts?: boolean;
  /** Seconds of quiet after the last photo before a burst is treated as
   *  finished — used both to group photos and to stay silent while the
   *  customer is still sending. */
  assistant_photo_delay_seconds?: number;
  /** Hours of inactivity before a claim returns to the queue. 0 = never. */
  assignment_release_hours?: number;
  /** Funnel v2 (2026-07-22): true = streamlined funnel (cover → storefront
   *  checkout with upsells → thank-you; photos AFTER). false = classic 8-step. */
  funnel_v2_enabled?: boolean;
  /** Let the assistant re-read WhatsApp conversations looking for promises
   *  never kept (migration 123). ⚠️ OFF BY DEFAULT — it costs a model call per
   *  order per day, and on the day it is switched on it works through every
   *  conversation that has never been read. */
  open_loops_enabled?: boolean;
  /** ISSUE 3 — share of AI-Magic pages that should carry a layout template
   *  (0 = none, 100 = every page). Read by /ai/layout. */
  template_usage_pct?: number;
  /** Fraud guard — max COD orders per IP per rolling 24h (0 = disabled).
   *  Read by /orders/precheck. */
  max_orders_per_ip_per_day?: number;
  /** Internal redo (réimpression) cost rate, MAD per page. Tracking only —
   *  never charged to the customer. Defaults to 4 server-side. */
  redo_cost_per_page?: number;
  pdf_bleed_mm: number;
  pdf_dpi: number;
  logo_position_y_percent: number | null;
  print_format: 'A4' | 'A3' | null;
  printer_preset: 'konica_bizhub' | 'konica_accurio' | 'epson_l1850' | 'a4_standard' | 'custom' | null;
  pdf_crop_marks: boolean | null;
  cost_per_book: number | null;
  /** MAD acompte paid by virement before delivery. Subtracted from the
   *  AMEEX COD so the courier doesn't double-charge the customer. */
  advance_amount: number;
  /** AMEEX auto-fill defaults — Phase 1 of the parcel-form prefill
   *  feature. Each maps 1:1 to a field on AMEEX's Ajouter Colis form so
   *  the admin doesn't retype the same toggles for every order. */
  ameex_default_product: string | null;
  ameex_default_fragile: boolean | null;
  ameex_default_open: boolean | null;
  ameex_default_try: boolean | null;
  ameex_default_label_type: string | null;
  /** Feature flag for the batched Duo/Trio multi-album customer flow.
   *  See docs/superpowers/specs/2026-06-04-multi-album-flow-design.md.
   *  Solo orders bypass this branch entirely — flag only affects Duo + Trio. */
  flag_multi_album_v2: boolean;
  photo_retention_trigger: 'after_upload_hours' | 'after_delivery' | 'manual' | null;
  photo_retention_hours: number | null;
  pdf_retention_trigger: 'after_delivery' | 'manual' | null;
  auto_cleanup_enabled: boolean | null;
  /** Migration 280: X in « deleted X days after delivery » (default 14). */
  photo_delete_after_days?: number | null;
  /** Migration 280: the photos the client SENT on WhatsApp — off | dry_run (count only) | on. */
  purge_whatsapp_copies?: 'off' | 'dry_run' | 'on' | null;
  // Pixel tracking — paste the same IDs configured in the Shopify theme so
  // events on app.storiz.ma land in the same Ads Manager / GA dashboards as
  // events on storiz.ma. NULL = pixel disabled (no script injected).
  facebook_pixel_id: string | null;
  tiktok_pixel_id: string | null;
  ga4_measurement_id: string | null;
  gtm_container_id: string | null;
  clarity_project_id: string | null;
  /** Mirrors the Shopify theme setting of the same name — the theme cannot
   *  read this table, so the two are kept equal by hand. */
  openai_pixel_id: string | null;
  // Smart WhatsApp follow-up — editable templates + bulk-send config. Keys
  // on follow_up_templates are SubState slugs (cover_missing, photos_missing,
  // …). Values use {firstName}, {destination}, {link} placeholders. NULL →
  // frontend falls back to defaults in web/src/lib/followUpMessages.ts.
  follow_up_templates: Record<string, string> | null;
  follow_up_bulk_mode: 'auto' | 'manual';
  follow_up_bulk_delay_ms: number;
  /** Bindery assembly-template ("fiche de montage") dimensions, mm. Single
   *  global row → editing applies to every order's downloadable fiche, past
   *  and future. NULL → frontend falls back to DEFAULT_PRINT_SETTINGS. */
  print_template_settings: {
    sheetWmm: number;   // derived from the case spec (computeVinylSizeMm), not edited
    sheetHmm: number;
    boardWmm: number;
    boardHmm: number;
    spineWmm: number;
    jointGapMm: number;
    boardThicknessMm: number;
    turnInMm: number;
    edgeMm: number;
    safeZoneMm: number;
  } | null;
  // ─── Printer ("imprimeur") contact + channel toggles ───────────────────────
  // Used by the "Envoyer à l'imprimeur" admin bulk action. Email + WhatsApp are
  // independent: either, both, or neither may be on. See migration 019.
  printer_email: string | null;
  printer_whatsapp: string | null;
  printer_email_enabled: boolean;
  printer_whatsapp_enabled: boolean;
  // ─── Rate card (Phase 2 2026-06-10) — defaults for new printer batches ──────
  print_sheet_price?: number;      // MAD / feuille A3 70g recto-verso (= a3Cost)
  print_cover_price?: number;      // MAD / couverture vinyle
  delivery_cost_default?: number;  // forfait livraison when AMEEX fee unknown
  // ─── Coût de revient params (migration 048) — drive computeAlbumCost ────────
  pages_per_a3?: number;           // A3 yields N pages → pageUnit = print_sheet_price / N
  branding_pages?: number;         // branding pages added to every book (cost only)
  rigid_plaque_cost?: number;      // one rigid plaque
  boards_per_plaque?: number;      // boards cut per plaque → boardUnit
  boards_per_book?: number;        // rigid boards per book
  spine_cost?: number;             // spine per book (offcut)
  packaging_cost?: number;         // packaging per ORDER
  glue_cost?: number;              // glue/pins per book
  // ─── The extras offered after the preview is approved (migration 208) ──────
  /** Offer the Relief 3D cover + express delivery once, after approval. */
  extras_offer_enabled?: boolean | null;
  /** Only orders approved at/after this moment are offered (set by the
   *  migration; moving it back is the backfill). */
  extras_offer_since?: string | null;
  /** « Demander le paiement » button (migration 247) — ships OFF. */
  payment_request_button_enabled?: boolean | null;
  updated_at: string;
}

// ─── Stock management (Phase 1 2026-06-10) ───────────────────────────────────
export interface Material {
  id: string;
  name: string;
  category: 'consommable' | 'fourniture';
  /** Cost-model class: 'album' lines feed the per-album recette + profit;
   *  'business' = general atelier supplies, excluded from album cost. */
  material_class?: 'album' | 'business';
  purchase_unit: string;
  consume_unit: string;
  units_per_purchase: number;
  avg_cost: number;
  stock_qty: number;
  min_qty: number | null;
  max_qty: number | null;
  lead_time_days: number | null;
  active: boolean;
  notes: string | null;
  // Cutting calculator (2026-06-10): purchased sheet size + the album-spec
  // piece cut from it → units_per_purchase derived instead of guessed.
  purchase_w_mm?: number | null;
  purchase_h_mm?: number | null;
  piece_kind?: 'planche' | 'dos' | 'kit_album' | 'vinyle' | 'feuille_a3' | 'page_garde' | 'custom' | null;
  piece_w_mm?: number | null;
  piece_h_mm?: number | null;
  created_at: string;
  updated_at: string;
}

/** Gemini pre-fill for a material from a free-text description. */
export interface AiMaterialSuggestion {
  name?: string | null;
  category?: 'consommable' | 'fourniture' | null;
  purchase_unit?: string | null;
  consume_unit?: string | null;
  purchase_w_mm?: number | null;
  purchase_h_mm?: number | null;
  piece_kind?: Material['piece_kind'];
  piece_w_mm?: number | null;
  piece_h_mm?: number | null;
  price_per_purchase_unit?: number | null;
  min_qty?: number | null;
  lead_time_days?: number | null;
  notes?: string | null;
}

export interface StockMovement {
  id: string;
  material_id: string;
  qty: number;
  reason: 'achat' | 'commande' | 'ajustement' | 'perte';
  ref_order_id: string | null;
  ref_purchase_id: string | null;
  unit_cost: number | null;
  note: string | null;
  created_by: string | null;
  created_at: string;
  materials?: { name: string; consume_unit: string };
}

export interface BomLine {
  id: string;
  material_id: string;
  qty_fixed: number;
  qty_per_page: number;
  note: string | null;
  materials?: { name: string; consume_unit: string; avg_cost: number; category: string; material_class?: 'album' | 'business' };
}

/** Per-order profitability breakdown — live (BOM × albums) + recorded costs.
 *  Returned by GET /stock/order-profit/:shopifyOrderId. All amounts in MAD. */
export interface OrderProfit {
  shopify_order_id: string;
  shopify_order_name: string | null;
  albums: number;
  pages: number;
  revenue: number;
  page_unit: number;       // derived per-page interior cost (e.g. 2.25)
  interior_cost: number;   // imprimeur — (pages+branding) × pageUnit × albums
  cover_cost: number;      // imprimeur — vinyl × albums
  materials_cost: number;  // stock matières — boards + spine + glue (× albums)
  packaging_cost: number;  // stock — per order
  delivery_cost: number;
  delivery_estimated: boolean;
  redo_cost: number;
  lead_cost: number;       // acquisition cost (CAC) — only when tracked
  lead_tracked: boolean;
  cost_total: number;
  profit: number;
}

export interface Purchase {
  id: string;
  supplier: string | null;
  purchase_date: string;
  total_amount: number | null;
  receipt_path: string | null;
  status: string;
  note: string | null;
  created_at: string;
  purchase_lines?: Array<{
    id: string;
    material_id: string;
    qty_purchase_units: number;
    unit_price: number;
    materials?: { name: string; purchase_unit: string; units_per_purchase: number };
  }>;
}

// ─── Printer batches + order costs (Phase 2 2026-06-10) ─────────────────────
export interface PrintBatch {
  id: string;
  sent_at: string;
  status: 'envoyé' | 'reçu';
  payment_status: 'à_payer' | 'payé';
  sheet_unit_price: number;
  cover_unit_price: number;
  total_amount: number;
  paid_at: string | null;
  note: string | null;
  created_at: string;
  print_batch_orders?: Array<{
    id: string;
    shopify_order_id: string;
    albums: number;
    pages: number;
    sheets: number;
    covers: number;
    line_amount: number;
  }>;
}

export interface OrderCosts {
  shopify_order_id: string;
  materials_cost: number | null;   // matières: boards + spine + glue (× albums)
  interior_cost: number | null;    // imprimeur: (pages+branding) × pageUnit × albums
  cover_cost: number | null;       // imprimeur: vinyl × albums
  packaging_cost: number | null;   // per order
  delivery_cost: number | null;
  redo_cost: number | null;        // re-print / redo
  updated_at: string;
}

export const printApi = {
  batches: () => apiFetch<{ batches: PrintBatch[]; unpaid_total: number }>('/print-batches'),
  createBatch: (orderIds: string[]) =>
    apiFetch<{ batch_id: string }>('/print-batches', { method: 'POST', body: JSON.stringify({ order_ids: orderIds }) }),
  updateBatch: (id: string, data: { sheet_unit_price?: number; cover_unit_price?: number; status?: 'envoyé' | 'reçu'; note?: string }) =>
    apiFetch<{ batch: PrintBatch }>(`/print-batches/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  pay: (ids: string[]) =>
    apiFetch<{ paid: number; total: number }>('/print-batches/pay', { method: 'POST', body: JSON.stringify({ ids }) }),
  orderCosts: (shopifyOrderId: string) =>
    apiFetch<{ costs: OrderCosts | null }>(`/print-batches/order-costs/${shopifyOrderId}`),
};

export const stockApi = {
  materials: () => apiFetch<{ materials: Material[] }>('/stock/materials'),
  aiMaterial: (text: string) =>
    apiFetch<{ suggestion: AiMaterialSuggestion }>('/stock/ai-material', { method: 'POST', body: JSON.stringify({ text }) }),
  deleteMaterial: (id: string) =>
    apiFetch<{ deleted: boolean; deactivated?: boolean; reason?: string }>(`/stock/materials/${id}`, { method: 'DELETE' }),
  cancelPurchase: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/stock/purchases/${id}`, { method: 'DELETE' }),
  createMaterial: (data: Partial<Material>) =>
    apiFetch<{ material: Material }>('/stock/materials', { method: 'POST', body: JSON.stringify(data) }),
  updateMaterial: (id: string, data: Partial<Material>) =>
    apiFetch<{ material: Material }>(`/stock/materials/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  movements: (materialId?: string) =>
    apiFetch<{ movements: StockMovement[] }>(`/stock/movements${materialId ? `?material_id=${materialId}` : ''}`),
  adjust: (data: { material_id: string; qty: number; reason?: 'ajustement' | 'perte'; note?: string }) =>
    apiFetch<{ material: Material }>('/stock/movements', { method: 'POST', body: JSON.stringify(data) }),
  bom: () => apiFetch<{ bom: BomLine[] }>('/stock/bom'),
  saveBom: (lines: Array<{ material_id: string; qty_fixed: number; qty_per_page: number; note?: string }>) =>
    apiFetch<{ ok: true }>('/stock/bom', { method: 'PUT', body: JSON.stringify({ lines }) }),
  purchases: () => apiFetch<{ purchases: Purchase[] }>('/stock/purchases'),
  createPurchase: (data: {
    supplier?: string;
    purchase_date?: string;
    note?: string;
    lines: Array<{ material_id: string; qty_purchase_units: number; unit_price: number }>;
  }) => apiFetch<{ purchase: Purchase }>('/stock/purchases', { method: 'POST', body: JSON.stringify(data) }),
  alerts: () => apiFetch<{ alerts: Array<Pick<Material, 'id' | 'name' | 'stock_qty' | 'min_qty' | 'consume_unit' | 'lead_time_days'>> }>('/stock/alerts'),
  /** Live per-order profitability breakdown, all parametric via computeAlbumCost
   *  (revenue − interior − cover − matières − emballage − livraison − lead − redo).
   *  matières = (boards + spine + glue) × #albums; interior carries the paper,
   *  cover the vinyl — no double-count. lead_cost shows only when tracked. */
  orderProfit: (shopifyOrderId: string) =>
    apiFetch<{ order_profit: OrderProfit }>(`/stock/order-profit/${shopifyOrderId}`),
  // ─── Receipt photo intake (Task 3) — TWO steps, never auto-commit ───────────
  /** Step 1: send a ticket photo (data URL or https). Returns extracted lines
   *  + a deterministic match suggestion per line. Writes NOTHING to the DB. */
  receiptExtract: (image: string) =>
    apiFetch<{ receipt: ReceiptExtract }>('/stock/receipt/extract', {
      method: 'POST',
      body: JSON.stringify({ image }),
    }),
  /** Step 2: commit the admin-reviewed payload. Creates new materials then runs
   *  the same moving-average purchase commit as createPurchase. */
  receiptCommit: (data: {
    supplier?: string;
    purchase_date?: string;
    note?: string;
    lines: ReceiptCommitLine[];
  }) => apiFetch<{ purchase: Purchase }>('/stock/receipt/commit', { method: 'POST', body: JSON.stringify(data) }),
};

/** A single extracted ticket line + its match suggestion. */
export interface ReceiptExtractLine {
  label: string;
  qty: number;
  unit_price: number | null;
  matched: boolean;
  // present when matched === true
  material_id?: string;
  material_name?: string | null;
  material_class?: 'album' | 'business';
  purchase_unit?: string | null;
  units_per_purchase?: number;
  avg_cost?: number;
  match_score?: number;
  // present when matched === false
  proposed?: {
    name: string;
    category: string;
    material_class: 'album' | 'business';
    purchase_unit: string | null;
    consume_unit: string | null;
    units_per_purchase: number;
    min_qty: number | null;
    notes: string | null;
  };
}

export interface ReceiptExtract {
  supplier: string | null;
  purchase_date: string | null;
  lines: ReceiptExtractLine[];
}

/** A reviewed line ready to commit: either an existing material or a new one. */
export type ReceiptCommitLine =
  | { material_id: string; qty_purchase_units: number; unit_price: number }
  | {
      new_material: {
        name: string;
        category?: string;
        material_class?: 'album' | 'business';
        purchase_unit?: string;
        consume_unit?: string;
        units_per_purchase?: number;
      };
      qty_purchase_units: number;
      unit_price: number;
    };

// ─── Accounting cockpit (2026-06-18) ─────────────────────────────────────────
// Management cockpit (NOT statutory). Revenue recognized on delivery; COGS +
// contribution READ from order_costs; manual expenses ledger + targets blob.

export type ExpenseKind = 'recurring' | 'one_time' | 'variable';

export interface Expense {
  id: string;
  kind: ExpenseKind;
  category: string;
  label: string | null;
  vendor: string | null;
  amount: number;
  currency: string | null;
  expense_date: string;
  payment_method: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface AccountingExpenseInput {
  kind: ExpenseKind;
  category: string;
  label?: string;
  vendor?: string;
  amount: number;
  expense_date: string;
  payment_method?: string;
  notes?: string;
}

export interface AccountingTargetProgress {
  realized: number;
  goal: number;
  gap: number;
  progress: number | null;
}

export interface AccountingSummary {
  window: { from: string | null; to: string | null };
  pnl: {
    revenue: number;
    cogs: number;
    grossMargin: number;
    delivery: number;
    contributionMargin: number;
    recurring: number;
    variableManual: number;
    oneTime: number;
    netProfit: number;
    /** System 2 — total ad spend in window fed into the P&L. */
    adSpend: number;
    contributionAfterAd: number;
    netProfitAfterAd: number;
    deliveredCount: number;
    /** Orders created in the window (CPL denominator). */
    leadsCount: number;
  };
  cashflow: {
    cashIn: number;
    advancesIn: number;
    codRemainderIn: number;
    cashOut: number;
    cashPosition: number;
    monthlyBurn: number;
    recurringMonthly: number;
    avgMonthlyVariable: number;
    runwayMonths: number | null;
  };
  /** REAL spendable cash ("Cash en main") + its breakdown. */
  cash: {
    total: number;
    payouts_net: number;
    advances_received: number;
    added: number;
    spent: number;
  };
  targets: {
    dailyProfitTarget: number;
    avgContributionPerOrder: number;
    ordersPerDayNeeded: number | null;
    currentAOV: number;
    progress: {
      today: AccountingTargetProgress;
      week: AccountingTargetProgress;
      month: AccountingTargetProgress;
    };
  };
  byDay: Array<{ day: string; revenue: number; cogs: number; profit: number }>;
  expenseByCategory: Array<{ category: string; amount: number }>;
}

export interface AmeexPayout {
  id: string;
  payout_ref: string | null;
  amount: number;
  fee: number;
  net: number;
  received_at: string;
  status: string | null;
  note: string | null;
  created_at: string;
}

export interface CashAdjustment {
  id: string;
  amount: number;
  label: string;
  entry_date: string;
  created_at: string;
  /** Migration 062 — optional link to the order this cash entry belongs to. */
  order_id?: string | null;
  order?: { shopify_order_id: string; shopify_order_name: string | null; customer_name: string | null } | null;
  /** 'manual' (hand-typed) or 'auto' (created by an order payment action). */
  source?: string;
}

export interface ExpenseReceiptExtract {
  amount: number | null;
  date: string | null;
  vendor: string | null;
  category: string;
  kind_guess: ExpenseKind;
}

export const accountingApi = {
  summary: (params: { from?: string; to?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    const qs = q.toString();
    return apiFetch<{ summary: AccountingSummary }>(`/accounting/summary${qs ? `?${qs}` : ''}`);
  },
  listExpenses: (params: { from?: string; to?: string; kind?: ExpenseKind } = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.kind) q.set('kind', params.kind);
    const qs = q.toString();
    return apiFetch<{ expenses: Expense[] }>(`/accounting/expenses${qs ? `?${qs}` : ''}`);
  },
  addExpense: (data: AccountingExpenseInput) =>
    apiFetch<{ expense: Expense }>('/accounting/expenses', { method: 'POST', body: JSON.stringify(data) }),
  updateExpense: (id: string, data: AccountingExpenseInput) =>
    apiFetch<{ expense: Expense }>(`/accounting/expenses/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteExpense: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/accounting/expenses/${id}`, { method: 'DELETE' }),
  getTargets: () =>
    apiFetch<{ targets: { daily_profit_target: number } }>('/accounting/targets'),
  putTargets: (data: { daily_profit_target: number }) =>
    apiFetch<{ targets: { daily_profit_target: number } }>('/accounting/targets', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  /** Step 1: data URL → extracted expense suggestion. Writes NOTHING. */
  receiptExtract: (image: string) =>
    apiFetch<{ receipt: ExpenseReceiptExtract }>('/accounting/receipt/extract', {
      method: 'POST',
      body: JSON.stringify({ image }),
    }),
  /** Step 2: commit the admin-reviewed expense (never auto-commit). */
  receiptCommit: (data: AccountingExpenseInput) =>
    apiFetch<{ expense: Expense }>('/accounting/receipt/commit', { method: 'POST', body: JSON.stringify(data) }),

  // ── Cash en main — manual AMEEX payouts ──
  listPayouts: () => apiFetch<{ payouts: AmeexPayout[] }>('/accounting/payouts'),
  addPayout: (data: { amount: number; received_at: string; payout_ref?: string; fee?: number; note?: string }) =>
    apiFetch<{ payout: AmeexPayout }>('/accounting/payouts', { method: 'POST', body: JSON.stringify(data) }),
  importPayouts: (rows: Array<{ amount: number; received_at: string; ref?: string }>) =>
    apiFetch<{ inserted: number; payouts: AmeexPayout[] }>('/accounting/payouts/import', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),
  deletePayout: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/accounting/payouts/${id}`, { method: 'DELETE' }),

  // ── Cash en main — manual cash adjustments (signed) ──
  listAdjustments: () => apiFetch<{ adjustments: CashAdjustment[] }>('/accounting/cash-adjustments'),
  addAdjustment: (data: { amount: number; label: string; entry_date?: string; order_id?: string }) =>
    apiFetch<{ adjustment: CashAdjustment }>('/accounting/cash-adjustment', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  deleteAdjustment: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/accounting/cash-adjustment/${id}`, { method: 'DELETE' }),
};

// ─── Customers (CRM) ──────────────────────────────────────────────────────────
export interface CustomerRow {
  id: string;
  phone: string;
  name: string | null;
  email: string | null;
  acquisitionSource: string | null;
  ordersCount: number;
  isRepeat: boolean;
  lifetimeRevenue: number;
  lifetimeContribution: number;
  lifetimeAdCost: number;
  netProfit: number;
  lastOrderAt: string | null;
}

export interface CustomerListStats {
  totalCustomers: number;
  repeatCustomers: number;
  repeatRate: number; // percent
  avgLtv: number;
}

export interface CustomerOrderHistory {
  shopifyOrderId: string;
  orderName: string | null;
  createdAt: string | null;
  status: string | null;
  bundle: string | null;
  pageCount: number | null;
  extraPages: number;
  totalPrice: number;
  destinations: { name: string; year: string | null }[];
  coupon: string | null; // deferred — always null for now
  ameex: {
    status: string | null;
    parcelCode: string | null;
    statusComment: string | null;
    lastEventAt: string | null;
  };
  cogs: number;
  adCost: number;
  contribution: number;
  delivered: boolean;
}

export interface CustomerMoney {
  lifetimeRevenue: number;
  aov: number;
  ordersCount: number;
  lifetimeCogs: number;
  lifetimeContribution: number;
  cac: number; // ad cost on the FIRST order
  lifetimeAdCost: number;
  netProfit: number; // lifetime contribution − lifetime ad cost
  ltvToCac: number | null;
}

export interface CustomerDetail {
  id: string;
  phone: string;
  name: string | null;
  nameVariants: string[];
  email: string | null;
  acquisitionSource: string | null;
  firstOrderAt: string | null;
  lastOrderAt: string | null;
  notes: string;
  notesUpdatedAt: string | null;
  notesUpdatedBy: string | null;
  ordersCount: number;
  money: CustomerMoney;
}

export const customersApi = {
  list: () => apiFetch<{ customers: CustomerRow[]; stats: CustomerListStats }>('/customers'),
  get: (id: string) =>
    apiFetch<{ customer: CustomerDetail; orders: CustomerOrderHistory[] }>(`/customers/${id}`),
  saveNotes: (id: string, note: string) =>
    apiFetch<{ customer: { id: string; notes: string; notes_updated_at: string; notes_updated_by: string | null } }>(
      `/customers/${id}/notes`,
      { method: 'PUT', body: JSON.stringify({ note }) },
    ),
};

// ─── Marketing analytics (System 2) ──────────────────────────────────────────
export type AdChannel = 'facebook' | 'tiktok' | 'google';

export interface AdSpendRowRecord {
  id: string;
  channel: AdChannel;
  campaign_id: string | null;
  campaign_name: string | null;
  adset_id: string | null;
  ad_id: string | null;
  spend_date: string;
  spend: number;
  impressions: number | null;
  clicks: number | null;
  currency: string | null;
  source: 'api' | 'manual' | 'csv';
  created_at: string;
  updated_at: string;
}

export interface MarketingChannelRow {
  channel: string;
  spend: number;
  leads: number;
  confirmed: number;
  revenue: number;
  cpl: number | null;
  cac: number | null;
  aov: number | null;
  roas: number | null;
  contribution: number;
  contributionAfterAd: number;
  impressions: number;
  clicks: number;
}

export interface MarketingCampaignRow {
  campaign: string;
  channel: string | null;
  spend: number;
  leads: number;
  confirmed: number;
  revenue: number;
  cpl: number | null;
  cac: number | null;
  aov: number | null;
  roas: number | null;
  contribution: number;
  contributionAfterAd: number;
}

export interface MarketingAnalytics {
  window: { from: string | null; to: string | null };
  totals: {
    spend: number;
    leads: number;
    confirmed: number;
    revenue: number;
    cpl: number | null;
    cac: number | null;
    aov: number | null;
    roas: number | null;
    contribution: number;
    contributionAfterAd: number;
  };
  // Lead → delivered-album conversion for the window (cohort: real, non-test,
  // engaged leads created in the window; delivered = those now delivered).
  funnel?: {
    realLeads: number;
    delivered: number;
    /** Same lead cohort, agent's explicit confirm-button clicks only. */
    confirmed: number;
    /** Same lead cohort, reached the `paid` lifecycle milestone. */
    paid: number;
    /** Day the confirm button shipped; windows starting earlier under-report. */
    confirmedSince: string | null;
    paidInTransit: number;
    conversionRate: number | null;
  };
  channelRows: MarketingChannelRow[];
  campaignRows: MarketingCampaignRow[];
}

export interface AdAccountsConfig {
  // System 3 adds the conversion-side id (pixel / event-set / conversion-action)
  // to each channel. Tokens stay server secrets.
  facebook?: { ad_account_id?: string; pixel_id?: string; enabled?: boolean };
  tiktok?: { advertiser_id?: string; pixel_id?: string; event_set_id?: string; enabled?: boolean };
  google?: {
    customer_id?: string;
    login_customer_id?: string;
    conversion_action_id?: string;
    enabled?: boolean;
  };
}

// ─── Server-side conversion feedback (System 3) ───────────────────────────────
export type ConversionEventType = 'Purchase' | 'Delivered';
export type ConversionMatchKey = 'fbclid' | 'ttclid' | 'gclid' | 'phone_hash' | 'none';
export type ConversionResult = 'sent' | 'skipped' | 'error';

export interface ConversionEventRecord {
  id: string;
  order_id: string;
  channel: AdChannel;
  event_type: ConversionEventType;
  event_id: string;
  match_key: ConversionMatchKey | null;
  value: number | null;
  currency: string | null;
  http_status: number | null;
  ok: boolean | null;
  error_message: string | null;
  sent_by: string | null;
  created_at: string;
  result: ConversionResult;
  order_name: string | null;
  customer_name: string | null;
}

export interface ManualSpendInput {
  channel: AdChannel;
  campaign_name?: string;
  campaign_id?: string;
  spend: number;
  spend_date: string;
  impressions?: number;
  clicks?: number;
}

export interface CsvSpendRow {
  // Raw channel string — the worker normalizes it (Facebook/FB/Meta→facebook,
  // etc.) and reports the actual offending value if unknown.
  channel: string;
  campaign_name?: string;
  campaign_id?: string;
  spend: number;
  spend_date: string;
  /** Native currency of `spend` (e.g. USD). Worker converts to MAD. */
  currency?: string;
  impressions?: number;
  clicks?: number;
}

export interface ManualLeadInput {
  channel: AdChannel;
  leads: number;
  lead_date: string;
  note?: string;
}

export interface ManualLeadRecord {
  id: string;
  channel: string;
  leads: number;
  lead_date: string;
  note: string | null;
  source: string;
  created_at: string;
}

export const marketingApi = {
  /** Ad spend vs confirmed albums for a window (admin / marketing.manage).
   *  Headline = cost per ALBUM. Awareness campaigns excluded — the excluded
   *  total is returned so the figure is never silently wrong. */
  profitability: (params: { from?: string; to?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    const qs = q.toString();
    return apiFetch<{ profitability: {
      window: { from: string | null; to: string | null };
      spend_mad: number; spend_excluded_mad: number; excluded_campaigns: string[];
      confirmed_orders: number; confirmed_albums: number;
      cost_per_album: number | null; cost_per_order: number | null;
      profit_mad: number; commissions_mad: number;
      /** The shared profit net (worker lib/profit.ts); null when the ad data is missing. */
      net_profit_mad: number | null;
      ads_missing?: boolean; ads_last_sync?: string | null;
      delivered_orders?: number; revenue_mad?: number; expenses_mad?: number;
      profit_before_ads_mad?: number; cost_incomplete?: boolean; missing_cost_count?: number;
    } }>(`/marketing/profitability${qs ? `?${qs}` : ''}`);
  },
  /** Owner, 2026-10-10: chats → photos sent → preview approved → confirmed (worker lib/whatsappFunnel.ts). */
  whatsappFunnel: (params: { from?: string; to?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    const qs = q.toString();
    return apiFetch<{ funnel: WhatsappFunnel }>(`/marketing/whatsapp-funnel${qs ? `?${qs}` : ''}`);
  },
  analytics: (params: { from?: string; to?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    const qs = q.toString();
    return apiFetch<{ analytics: MarketingAnalytics }>(`/marketing/analytics${qs ? `?${qs}` : ''}`);
  },
  listSpend: (params: { from?: string; to?: string; channel?: AdChannel } = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.channel) q.set('channel', params.channel);
    const qs = q.toString();
    return apiFetch<{ spend: AdSpendRowRecord[] }>(`/marketing/spend${qs ? `?${qs}` : ''}`);
  },
  syncNow: (params: { from?: string; to?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    const qs = q.toString();
    return apiFetch<{
      window: { from: string; to: string };
      channels: Record<string, { ok: boolean; rows_synced: number; error?: string }>;
    }>(`/marketing/sync${qs ? `?${qs}` : ''}`, { method: 'POST' });
  },
  addManualSpend: (data: ManualSpendInput) =>
    apiFetch<{ spend: AdSpendRowRecord }>('/marketing/spend/manual', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  addManualLead: (data: ManualLeadInput) =>
    apiFetch<{ lead: ManualLeadRecord }>('/marketing/leads/manual', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  listLeads: (params: { from?: string; to?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    const qs = q.toString();
    return apiFetch<{ leads: ManualLeadRecord[] }>(`/marketing/leads${qs ? `?${qs}` : ''}`);
  },
  deleteLead: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/marketing/leads/${id}`, { method: 'DELETE' }),
  importCsv: (rows: CsvSpendRow[]) =>
    apiFetch<{ imported: number; skipped: number; errors: string[] }>('/marketing/spend/csv', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),
  deleteSpend: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/marketing/spend/${id}`, { method: 'DELETE' }),
  getAdAccounts: () =>
    apiFetch<{
      ad_accounts: AdAccountsConfig;
      token_configured: Record<AdChannel, boolean>;
      pixel_configured: Record<AdChannel, boolean>;
      fx_rates: Record<string, number>;
    }>('/marketing/ad-accounts'),
  putAdAccounts: (data: AdAccountsConfig) =>
    apiFetch<{ ad_accounts: AdAccountsConfig }>('/marketing/ad-accounts', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  putFxRates: (rates: Record<string, number>) =>
    apiFetch<{ fx_rates: Record<string, number> }>('/marketing/fx-rates', {
      method: 'PUT',
      body: JSON.stringify({ rates }),
    }),
  // ─── System 3 — conversion feedback audit + retry + manual fire ─────────────
  listConversions: (
    params: { from?: string; to?: string; channel?: AdChannel; status?: ConversionResult | 'ok' | 'error' } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.channel) q.set('channel', params.channel);
    if (params.status) q.set('status', params.status);
    const qs = q.toString();
    return apiFetch<{ conversions: ConversionEventRecord[] }>(
      `/marketing/conversions${qs ? `?${qs}` : ''}`,
    );
  },
  retryConversion: (id: string) =>
    apiFetch<{ conversion: ConversionEventRecord | null }>(`/marketing/conversions/retry/${id}`, {
      method: 'POST',
    }),
  sendConversion: (shopifyOrderId: string, force = false) =>
    apiFetch<{ conversions: ConversionEventRecord[] }>(
      `/marketing/conversions/send/${shopifyOrderId}${force ? '?force=1' : ''}`,
      { method: 'POST' },
    ),
};

// ── Staff users & roles (RBAC stage a, 2026-07-16) — admin only ─────────────
export interface StaffRole { id: string; slug: string; name: string; description?: string | null; is_system?: boolean }
export interface StaffUser {
  id: string; email: string | null; full_name: string | null; whatsapp: string | null;
  is_active: boolean; role: string; roles: StaffRole[]; created_at: string | null;
  /**
   * Commission per album for THIS agent, overriding the global rate.
   * null = use the global rate (the default every human agent has).
   * 0 = earns nothing — how the WhatsApp bot account is configured.
   * Arrives from PostgREST as a numeric, which can be a string.
   */
  commission_rate?: number | string | null;
}
// ── WhatsApp assistant ───────────────────────────────────────────────────────
// The gateway lives on a VPS with no public port, so admin never talks to it
// directly: it writes what it WANTS here and the relay writes back what is
// actually TRUE. Every field below is one side of that conversation.
export interface WhatsAppNumber {
  id: string;
  label: string;
  session_id: string | null;
  /** Confirmed by WhatsApp once paired. Trust this one. */
  phone: string | null;
  /** What the admin typed to request a pairing code. A request, not a fact. */
  pairing_phone: string | null;
  desired_state: 'connected' | 'disconnected' | 'deleted';
  status: 'pending' | 'creating' | 'qr_ready' | 'connecting' | 'connected' | 'disconnected' | 'failed';
  /** Only present while pairing AND fresh — the API strips it otherwise. */
  qr_data_url: string | null;
  pairing_code: string | null;
  qr_updated_at: string | null;
  /** A QR existed but aged out; the relay is making a new one. */
  qr_stale: boolean;
  last_error: string | null;
  created_at: string | null;
  updated_at: string | null;
}
export interface WhatsAppRelayHealth {
  /** False also means "unknown" — never render a number as live when this is false. */
  online: boolean;
  seen_at: string | null;
  version: string | null;
}
/** A session running on the gateway that no Storiz number owns — typically made
 *  by hand in the OpenWA dashboard. Listed so a live WhatsApp connection can
 *  never be running unmanaged and invisible. */
export interface WhatsAppOrphanSession {
  id: string;
  name: string;
  status: string;
  phone: string | null;
}
/** A `products` row plus the three selling columns migration 106 adds. The
 *  catalogue is Shopify-synced — this is not a product editor. */
export interface UpsellProductRow {
  id: string;
  title: string;
  /** The catalogue handle — `livraison-express` is express delivery. */
  handle?: string | null;
  image_url: string | null;
  kind: string | null;
  enabled: boolean;
  shopify_product_gid: string | null;
  upsell_pitch: string | null;
  upsell_price: number | string | null;
  /** Priced per album: the assistant quotes — and bills — upsell_price × the
   *  order's albums (worker lib/upsell.ts upsellTotal). */
  upsell_per_album?: boolean | null;
  upsell_offer_when: string;
  /**
   * « Couverture Relief 3D » only: what the assistant quotes — and bills — for
   * 1 / 2 / 3 albums, the website's pack price (worker lib/reliefPricing.ts,
   * owner 2026-09-29 « 1a »). Its `upsell_price` is then not a price anybody
   * pays. 0 = switched off in the settings.
   */
  pack_prices?: number[] | null;
}

export interface OrderUpsellRow {
  id: string;
  product_id: string;
  state: 'suggested' | 'offered' | 'accepted' | 'declined' | string;
  offered_at: string | null;
  decided_at: string | null;
  products?: { id: string; title: string; upsell_price: number | string | null } | null;
}

/** What each option costs on ONE order. Mirrors worker lib/orderAddonToggle.ts. */
export interface OrderAddonsView {
  albums: number;
  /** product id → MAD that ticking it adds to this order (0 = cannot be added). */
  prices: Record<string, number>;
  /** product ids the order carries now. */
  taken: string[];
  /** Carried but not removable here (a Relief bought on the website). */
  locked: string[];
}

export interface SetOrderAddonResult {
  ok: true;
  unchanged: boolean;
  on: boolean;
  product_id: string;
  title: string;
  price: number;
  total_before: number;
  total: number;
  ameex_parcel_open: boolean;
  note_written?: boolean;
}

/** One customer's card on the pipeline board. */
export interface PilotageCard {
  chatId: string | null;
  orderId: string | null;
  orderName: string | null;
  /** The key of `/admin/orders/:shopifyOrderId` — link with lib/orderHref.ts. */
  shopifyOrderId?: string | null;
  name: string;
  initials: string;
  /** "il y a 3 j" — the board never shows a raw date. */
  age: string;
  /** ok | warm | hot — how long they have waited, judged per lane. */
  /** Past the 4h/24h clock — the only thing that paints a card red. */
  stuck?: boolean;
  heat: string;
  value: number;
  paused: boolean;
  complaint: boolean;
}

export interface PilotageData {
  generated_at: string;
  /** The window the board is showing. `from: null` = Maximum. `hiddenOlder`
   *  is how many orders fall outside it, so a quiet board is never mistaken
   *  for an empty business. */
  window: { from: string | null; to: string | null; hiddenOlder: number };
  phases: Array<{ key: string; label: string; lanes: string[] }>;
  laneLabels: Record<string, string>;
  lanes: Record<string, { count: number; money: number; cards: PilotageCard[] }>;
  attention: Array<{
    severity: 'crit' | 'warn' | 'info' | 'calm';
    chatId: string; title: string; detail: string; tag: string; age: string;
  }>;
  problems: Record<string, number>;
  /** [reason, count], already ranked — the list of what to automate next. */
  handovers: Array<[string, number]>;
  kpis: {
    /** Cards with no movement for 4h when the ball is with us, 24h when it
     *  is with the client — the one number the owner reads first. */
    stuck: number;
    live: number; paused: number;
    moneyWaiting: number; ordersWaiting: number;
    moneyPaidNotPrinting: number; paidNotPrinting: number;
    needsHuman: number; complaints: number; reviewsAsked: number;
    upsellsOffered: number; upsellsAccepted: number;
    costTodayUsd: number; messagesToday: number;
  };
}

// ─── Part B (2026-09-25): the Pipeline from facts, the Inbox, Live ─────────
// Computed in the worker (lib/clientStage.ts, lib/clientBoard.ts, lib/inbox.ts,
// lib/liveFeed.ts) and carried in the same /pilotage snapshot, so every tab
// describes the same moment.

export type ChipState = 'done' | 'todo' | 'warn' | 'info';
export interface FactChip { key: string; label: string; state: ChipState }
export type ClientBadge = 'returning' | 'cancelled' | 'paused' | 'complaint';

/** One client = one card, at the furthest thing that is TRUE about her. */
export interface PipelineClient {
  key: string;
  name: string;
  phone: string | null;
  chatId: string | null;
  chatIds: string[];
  /** Null only when every order is cancelled. */
  stage: string | null;
  stageLabel: string;
  sinceIso: string | null;
  ballWith: 'ours' | 'theirs' | 'courier' | 'nobody';
  stuck: boolean;
  primary: { id: string; name: string | null; shopifyOrderId: string | null; status: string | null; total: number } | null;
  money: number;
  chips: FactChip[];
  badges: ClientBadge[];
  otherOrders: Array<{ id: string; name: string | null; shopifyOrderId: string | null; status: string | null; total: number; stage: string; stageLabel: string }>;
  cancelledOrders: Array<{ id: string; name: string | null }>;
  /** False = off the active board (all orders cancelled) — still searchable. */
  active: boolean;
  ignored: boolean;
  lastActivityIso: string | null;
  /** Absent on a worker older than 2026-10-10. */
  journey?: JourneyTimes;
}

export type InboxReason = 'complaint' | 'doubt' | 'handoff' | 'held' | 'silence' | 'paused' | 'photos' | 'stuck' | 'alert';

export interface InboxItem {
  key: string;
  reason: InboxReason;
  reasonLabel: string;
  /** `old` = older than 7 days (computed from age, never a reason's own tier). */
  tier: 'now' | 'today' | 'old';
  chatId: string | null;
  orderId: string | null;
  orderName: string | null;
  shopifyOrderId: string | null;
  name: string;
  title: string;
  detail: string | null;
  /** « Elle veut » — what she is after, in her words when known (2026-10-07). */
  wants?: string | null;
  /** « À faire » — the one thing to do now. */
  todo?: string;
  sinceIso: string;
  ageMin: number;
  overdue: boolean;
  money: number;
  stageLabel: string | null;
  chips: FactChip[];
  activityIso: string;
  /** The chat whose FAILED answer can be sent again from the Inbox
   *  (`pilotageApi.retryReply`), or null — worker lib/inbox.ts, 2026-10-03. */
  retryChatId?: string | null;
  /** « Approve and send » this order's 3D preview (its shopify id) — worker lib/inbox.ts, 2026-10-08. */
  approvePreviewFor?: string | null;
}

export interface InboxLogRow {
  id: number;
  item_key: string;
  reason_kind: string;
  reasonLabel: string;
  chat_id: string | null;
  order_id: string | null;
  action: 'handled' | 'ignored' | 'snoozed' | 'undone';
  note: string | null;
  snooze_until: string | null;
  acted_by: string | null;
  acted_at: string;
  orderName: string | null;
  shopifyOrderId: string | null;
  name: string | null;
}

/** The Live page's mistake flags (worker/src/lib/agentWhy.ts `MistakeFlag`). */
export type MistakeFlag = 'twice' | 'price8' | 'handoff2' | 'nolink' | 'blocked';

/** One client's day, in plain words (worker/src/lib/liveJournal.ts). */
export interface JournalGroup {
  chatId: string | null;
  name: string;
  lastAt: string;
  entries: Array<{ at: string; text: string; why: string | null; kind: 'sent' | 'handoff' | 'blocked' | 'dropped' | 'failed' | 'held' | 'client'; flag: MistakeFlag | null }>;
  flags: MistakeFlag[];
  handoff: boolean;
}

export interface LiveData {
  status: { handlingNow: number; lastActionIso: string | null; lastActionAgoSec: number | null; stale: boolean; staleSinceIso: string | null };
  today: { replies: number; held: number; ordersCreated: number; handledAlone?: number };
  feed: Array<{ at: string; kind: 'reply' | 'held' | 'handoff' | 'order'; chatId: string | null; name: string; text: string }>;
  journal?: JournalGroup[];
  handledAloneChats?: string[];
}

/** When she reached each step of the journey map (worker/src/lib/clientStage.ts `JourneyTimes`). */
export interface JourneyTimes {
  ecrit: string | null;
  photos: string | null;
  album: string | null;
  apercu: string | null;
  valide: string | null;
  paye: string | null;
}

export interface PilotageSnapshot extends PilotageData {
  pipeline?: {
    stages: Array<{ key: string; label: string }>;
    phases: Array<{ key: string; label: string; stages: string[] }>;
    clients: PipelineClient[];
  };
  inbox?: {
    reasons: Array<{ key: InboxReason; label: string; tier: 'now' | 'today' }>;
    items: InboxItem[];
    hidden: { handled: number; ignored: number; snoozed: number };
    system: Array<{ kind: string; title: string; sinceIso: string }>;
  };
  live?: LiveData;
}

export const pilotageApi = {
  reopenHandled: (t: { kind: string; ref: string; lane: string }) =>
    apiFetch<{ ok: true }>('/pilotage/handled/reopen', { method: 'POST', body: JSON.stringify(t) }),
  handled: () =>
    apiFetch<{ items: Array<{
      id: string; kind: string; ref: string; lane: string; laneLabel: string; note: string | null;
      handled_at: string; handled_by: string | null; orderName: string | null; name: string | null;
    }> }>('/pilotage/handled'),
  /** Works for a chat with no order too — `ordersApi.unignore` cannot. */
  unignore: (t: { kind: 'order' | 'chat'; ref: string }) =>
    apiFetch<{ ok: true }>('/pilotage/unignore', { method: 'POST', body: JSON.stringify(t) }),
  /** The order page's conversation: a summary of facts, then the whole thread. */
  orderThread: (orderKey: string) =>
    apiFetch<OrderThread>(`/pilotage/order-thread?order=${encodeURIComponent(orderKey)}`),
  /** The same snapshot, typed with the Part B sections (Pipeline, Inbox, Live). */
  snapshot: () => apiFetch<PilotageSnapshot>('/pilotage?all=1'),
  /** ✗ on the board: off the active board, still findable (POST /pilotage/ignore). */
  ignore: (targets: Array<{ kind: 'order' | 'chat'; ref: string }>, reason?: string) =>
    apiFetch<{ ok: boolean; ignored: number; errors: string[] }>('/pilotage/ignore', { method: 'POST', body: JSON.stringify({ targets, reason }) }),
  /** What the agent is doing this second — the Live page's top line. */
  now: () => apiFetch<{ state: 'writing' | 'sent' | 'waiting'; chatId: string | null; name: string | null; at: string | null }>('/pilotage/now'),
  /** A client's last 5 steps, each with its reason (logged facts only). */
  why: (q: { chat?: string | null; order?: string | null }) => {
    const p = new URLSearchParams();
    if (q.chat) p.set('chat', q.chat);
    if (q.order) p.set('order', q.order);
    return apiFetch<{ lines: Array<{ at: string; title: string; why: string | null; kind: string }> }>(`/pilotage/why?${p.toString()}`);
  },
  /** ✓ / ✗ / snooze — one row per item in an append-only log (migration 180). */
  /**
   * `seenAt` — when the screen was drawn (the snapshot's `generated_at`): what
   * the decision covers. ✓ also hands every conversation in it back to the AI;
   * `resumed` lists the chats the AI took back (owner, 2026-10-03).
   */
  inboxAct: (items: Array<Pick<InboxItem, 'key' | 'reason' | 'chatId' | 'orderId'>>, action: 'handled' | 'ignored' | 'snoozed',
    opts: { note?: string; snoozeUntil?: string; seenAt?: string } = {}) =>
    apiFetch<{ ok: true; done: number; resumed?: string[] }>('/pilotage/inbox/act', {
      method: 'POST',
      body: JSON.stringify({ items: items.map((i) => ({ key: i.key, reason: i.reason, chatId: i.chatId, orderId: i.orderId })), action, ...opts }),
    }),
  /** `repaused`: the ✓ had handed the chat back to the AI — the person has it again. */
  inboxUndo: (key: string) =>
    apiFetch<{ ok: true; repaused?: boolean }>('/pilotage/inbox/undo', { method: 'POST', body: JSON.stringify({ key }) }),
  inboxLog: () => apiFetch<{ items: InboxLogRow[] }>('/pilotage/inbox/log'),
  /** Send a FAILED answer again — the same draft back through the same door,
   *  queued once however many clicks (worker lib/replyRetry.ts, 2026-10-03).
   *  A refusal (409) carries the reason in plain words. */
  retryReply: (chatId: string) =>
    apiFetch<{ ok: true; requeued: true }>(`/whatsapp/conversations/${encodeURIComponent(chatId)}/retry-reply`, { method: 'POST' }),
  /** The manager approves a 3D preview. The answer says what happens next —
   *  now · at_08 (night) · waits_for_pages · blocked (worker lib/previewSendPlan.ts). */
  approvePreview: (shopifyOrderId: string) =>
    apiFetch<{ ok: true; order: string; waits_for_pages?: boolean } & Partial<PreviewSendPlan>>(`/orders/${encodeURIComponent(shopifyOrderId)}/preview-approve`, { method: 'POST' }),
  /** Approved and not sent yet: when it leaves, or why it will not. */
  previewSendPlan: (shopifyOrderId: string) =>
    apiFetch<{ plan: PreviewSendPlan }>(`/orders/${encodeURIComponent(shopifyOrderId)}/preview-send-plan`),
};

export interface OrderThread {
  order: { id: string; name: string | null; customer: string | null; phone: string | null };
  chatId: string | null;
  summary: {
    stage: string; waitingOn: string; since: string; stuck: boolean;
    theyAsked: string[]; weSaid: string | null;
    unansweredCount: number; unansweredSince: string | null;
    needsHuman: string | null; paused: string | null; complaint: boolean;
  };
  messages: Array<{ id: string; direction: string; body: string | null; has_media: boolean | null; created_at: string }>;
}

/** A follow-up the owner wrote. `lane` is a pipeline lane key — the trigger
 *  vocabulary is the same one the Pilotage board draws. */
export interface FollowUpRule {
  id: string;
  name: string;
  lane: string;
  after_hours: number;
  message: string;
  source_text: string | null;
  /** ⚠️ Always false on creation. Arming is a separate, deliberate act. */
  enabled: boolean;
  requires_approval: boolean;
  max_sends: number;
  created_at: string;
}

export interface DraftedRule {
  name: string; lane: string; after_hours: number; message: string; source_text?: string;
}

/**
 * What one relance has actually achieved. Mirrors `FollowUpStat` in
 * worker/src/lib/followUpStats.ts.
 *
 * ⚠️ `advanced` IS NOT A CONVERSION. It counts orders that moved further along
 * the pipeline within the attribution window — it cannot see whether the
 * message caused that. Label it "moved on", never "converted".
 */
export interface FollowUpStat {
  sent: number;
  replied: number;
  advanced: number;
  /** Denominator for `advanced` — sends where the status was recorded. It is
   *  NOT `sent`; treating unknown rows as failures understates every rule. */
  advancedOf: number;
  lastSentAt: string | null;
  /** null below the minimum sample — show the raw counts, not a fake rate. */
  replyRate: number | null;
  advancedRate: number | null;
  verdict: 'unproven' | 'working' | 'weak' | 'dead';
}

export interface FollowUpQueueRow {
  /** ⚠️ Nullable since migration 108 — a cold-lead follow-up targets a chat,
   *  not an order, and the embedded `orders` join comes back null for it. */
  id: string; rule_id: string; order_id: string | null; chat_id: string | null;
  message: string; state: string; created_at: string;
  follow_up_rules?: { name: string } | null;
  orders?: { shopify_order_name: string | null; customer_name: string | null } | null;
  /** Resolved by the worker from the newest inbound message — a `@lid` chat id
   *  carries no phone, and a cold lead has no order to read a name from. */
  contact_phone?: string | null;
  contact_name?: string | null;
  /** ⚠️ false = approving will NOT send; the allowlist blocks it at serve
   *  time. null = settings unreadable, so make no claim either way. */
  reachable?: boolean | null;
  /** Queued more than 48 h ago: approving it drops it, nothing is sent
   *  (worker/src/lib/followUpQueueAge.ts, 2026-09-29). */
  stale?: boolean;
}

export const followUpsApi = {
  /** Sentence in, rule PROPOSAL out. Saves nothing. */
  draft: (text: string) =>
    apiFetch<{ rule: DraftedRule; lane_label: string }>('/followups/draft', {
      method: 'POST', body: JSON.stringify({ text }),
    }),
  rules: () => apiFetch<{
    rules: FollowUpRule[];
    /** Keyed by rule id. Every rule has an entry — a never-fired rule gets
     *  explicit zeros rather than a missing key, so "no data yet" cannot
     *  render as a blank that reads like "no result". */
    stats: Record<string, FollowUpStat>;
    /** The hard-coded ladders, which have no rule row of their own. The
     *  worker owns this list so the page cannot miss a newly added one. */
    builtIns: Array<{ bucket: string; stat: FollowUpStat }>;
    windows: { replyHours: number; attributionDays: number };
    /** Non-null when the counting query failed — must not read as zeros. */
    statsError: string | null;
    laneLabels: Record<string, string>;
  }>('/followups/rules'),
  createRule: (r: DraftedRule) =>
    apiFetch<{ rule: FollowUpRule }>('/followups/rules', { method: 'POST', body: JSON.stringify(r) }),
  updateRule: (id: string, patch: Record<string, unknown>) =>
    apiFetch<{ rule: FollowUpRule }>(`/followups/rules/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteRule: (id: string) =>
    apiFetch<{ ok: true }>(`/followups/rules/${id}`, { method: 'DELETE' }),
  queue: () => apiFetch<{
    queue: FollowUpQueueRow[];
    /** Older than this is dropped, never sent. */
    max_age_hours?: number;
    /** Night in Morocco (22:00–08:00): what is approved now waits until `sends_at`. */
    night?: { active: boolean; sends_at: string | null };
  }>('/followups/queue'),
  /** Approve sends the message that was QUEUED, not a fresh render of the rule.
   *  A row older than 48 h answers 410 and is dropped; at night `sends_at`
   *  says when the approved message will go (08:00 Morocco time). */
  decide: (id: string, ok: boolean) =>
    apiFetch<{ ok: true; state: string; sends_at?: string | null }>(`/followups/queue/${id}/${ok ? 'approve' : 'refuse'}`, {
      method: 'POST', body: '{}',
    }),
};

export const salesApi = {
  upsells: () => apiFetch<{ products: UpsellProductRow[] }>('/sales/upsells'),
  updateUpsell: (id: string, patch: Record<string, unknown>) =>
    apiFetch<{ product: UpsellProductRow }>(`/sales/upsells/${id}`, {
      method: 'PATCH', body: JSON.stringify(patch),
    }),
  /** The order's option rows, plus `addons`: what ticking each option adds to
   *  THIS order (MAD) and which ones it carries — null when unreadable. */
  orderUpsells: (orderId: string) =>
    apiFetch<{ upsells: OrderUpsellRow[]; addons?: OrderAddonsView | null }>(`/sales/orders/${orderId}/upsells`),
  /** The order page's options card (owner, 2026-10-14): tick = the client takes
   *  it — accepted, and the total goes up NOW; untick = taken back out. The
   *  worker refuses (409, French `error`) a paid-in-full order, a hand-corrected
   *  total, a shipped parcel, or a removal below what was already paid.
   *  `ameex_parcel_open`: a parcel already created keeps its old amount until
   *  the AMEEX card's « Modifier ». Shopify is not edited. */
  setOrderAddon: (orderId: string, productId: string, on: boolean) =>
    apiFetch<SetOrderAddonResult>(`/sales/orders/${orderId}/addons/${productId}`, {
      method: 'POST', body: JSON.stringify({ on }),
    }),
  /** Replace the picks for one order. Rows already offered/accepted/declined
   *  are kept — they record what happened to a real customer. */
  setOrderUpsells: (orderId: string, productIds: string[]) =>
    apiFetch<{ ok: true; kept_locked: string[] }>(`/sales/orders/${orderId}/upsells`, {
      method: 'PUT', body: JSON.stringify({ product_ids: productIds }),
    }),
};

/** Mirrors `worker/src/lib/agentOverlapDaily.ts` + the route. */
export interface AgentOverlapDaily {
  days: Array<{ day: string; count: number }>;
  today: number;
  recent: Array<{ chatId: string; personAt: string; agentAt: string; agentText: string }>;
  computedAt: string;
}

/** One client waiting on US (fix 4, 2026-10-10). Mirrors `worker/src/lib/waitingOnUs.ts`. */
export interface WaitingItem {
  kind: 'preview' | 'handover';
  since: string;
  hours: number;
  name: string | null;
  orderName: string | null;
  chatId: string | null;
  photos?: number | null;
  said?: string | null;
}

/** One customer mid-intake. Mirrors `worker/src/lib/formingOrders.ts`. */
export interface FormingOrder {
  chatId: string;
  customerName: string | null;
  address: string | null;
  albums: string[];
  layout: string | null;
  bundle: string | null;
  /** Required fields still outstanding, in the order the assistant asks them. */
  missing: string[];
  percent: number;
  /** Nothing outstanding — waiting on us to turn it into an order. */
  complete: boolean;
  lastActivity: string | null;
}

/** Why a client-promise follow-up will not go out — mirrors the worker's
 *  `ScheduledBlock` (worker/src/lib/clientPromiseSweep.ts). */
export type ClientPromiseBlock =
  | 'settings_unreadable' | 'switch_off' | 'send_off' | 'not_allowlisted' | 'no_room_in_day';

/** One arm of the welcome A/B (agent v2 `first_reply`, migration 276) —
 *  worker/src/lib/firstReplyRun.ts `abStats`. Counts are chats. */
export interface WelcomeArmStats {
  variant: 'long' | 'short';
  chats: number;
  replied: number;
  sent_photos: number;
  advance_paid: number;
  orders: number;
  duo_trio: number;
}

/** One thing the WhatsApp agent did or prepared on an order (agent v2
 *  `actions`, migration 278). worker/src/lib/agentActions.ts. */
/** One Claude shadow run of a client's album edit request (worker lib/albumEditShadow.ts). */
export interface EditShadowPlan { understanding: string; steps: Array<{ action: string; [k: string]: unknown }>; cannot: Array<{ clientWords: string; why: string }>; needsOwner: Array<{ topic: string; clientWords: string }> }
export interface EditShadowChecked { valid: Array<{ index: number; summary: string; hold?: boolean }>; forOwner: Array<Record<string, unknown>>; skipped: Array<{ index: number; why: string }> }
export interface EditShadowItem {
  id: string; created_at: string; request_id: string | null; source: 'live' | 'replay';
  order_id: string | null; chat_id: string | null; order_name: string | null; customer_name: string | null;
  model: string; status: 'done' | 'failed'; error: string | null; client_words: string | null;
  qwen_plan: EditShadowPlan | null; qwen_checked: EditShadowChecked | null;
  claude_plan: EditShadowPlan | null; claude_checked: EditShadowChecked | null;
  rounds: Array<{ round: number; error?: string; applied?: string[]; failed?: string[]; floor?: string[]; verify?: { ok: boolean; problems: string[]; missed: string[] } }>;
  changed: Array<{ at: number; before: string[] | null; after: string[] | null; beforeLayout: string | null; afterLayout: string | null; beforeText?: string | null; afterText?: string | null }> | null;
  self_ok: boolean | null; problems: string[]; cost_usd: number | null; duration_ms: number | null;
  owner_verdict: 'right' | 'wrong' | 'partly' | null; owner_note: string | null;
}

export interface AgentActionItem {
  id: string;
  chat_id: string;
  order_id: string | null;
  action: string;
  args: Record<string, unknown> | null;
  status: 'done' | 'undone' | 'failed' | 'pending_approval' | 'approved' | 'rejected';
  /** Signed change to what the client owes, DH. */
  impact_dh: number | null;
  approval_reason?: string | null;
  created_at: string;
  decided_by: string | null;
  decided_at: string | null;
  undone_at: string | null;
  error: string | null;
  /** What the action changed on the album (« titre », « couverture »…). */
  changed: string[];
  can_undo: boolean;
  undo_blocker: string | null;
}

/** A playbook the agent learned from real chats (agent v2 `playbooks`,
 *  migration 279). worker/src/lib/playbooks.ts. Only `approved` ones guide a reply. */
export interface PlaybookItem {
  id: string;
  situation: string;
  situation_tag: string | null;
  reply_guidance: string;
  /** The owner's own wording (Modifier) — wins over reply_guidance. */
  edited_text: string | null;
  /** What the reply is guided by: edited_text, else reply_guidance. */
  guidance: string;
  actions: string[] | null;
  status: 'proposed' | 'approved' | 'rejected';
  source_case_ids: string[] | null;
  created_at: string;
  decided_at: string | null;
  decided_by: string | null;
  uses_count: number | null;
  last_used_at: string | null;
  /** The real chats behind it. */
  cases: Array<{ id: string; situation: string | null; result: string | null; human_took_over: boolean; outcome: string | null }>;
}

/** One chat the agent closed with a reaction instead of a sentence (agent v2
 *  `reactions`, migration 275). worker/src/lib/reactOnlyRun.ts. */
export interface ReactionDecisionItem {
  id: string;
  chat_id: string;
  wa_message_id: string;
  emoji: string;
  /** Why, in the owner's words. */
  reason: string;
  client_text: string | null;
  mode: string | null;
  promise_about: 'payment' | 'photos' | 'other' | null;
  promise_day: string | null;
  promised_at: string | null;
  followup_at: string | null;
  created_at: string;
  customer_name: string | null;
  followup_state: 'pending' | 'done' | 'replaced' | null;
}

/** One follow-up waiting on a day a CLIENT named (migration 157). */
export interface ClientPromiseItem {
  chat_id: string;
  customer_name: string | null;
  phone: string | null;
  order: { id: string; name: string | null; status: string | null } | null;
  /** Their message, verbatim. */
  words: string;
  kind: string | null;
  promised_at: string | null;
  day: string;
  /** When the worker will send it (ISO), null when the day leaves no room. */
  fires_at: string | null;
  /** Exactly what the worker will send — composed there, never re-worded here. */
  preview: string;
  will_send: boolean;
  blocked: ClientPromiseBlock | null;
}

export const whatsappApi = {
  /** Wipe a TEST number's conversation with the assistant so the next message
   *  is the first it has ever seen. The worker refuses any number not on the
   *  test list — that guard lives there, not here. */
  resetTestNumber: (phone: string) =>
    apiFetch<{ ok: boolean; chats: number; deleted: Record<string, number>; orders_cancelled: number }>(
      `/whatsapp/test-numbers/${encodeURIComponent(phone)}/reset`,
      { method: 'POST' },
    ),

  /** Hand a conversation to a human: the assistant stops REPLYING but keeps
   *  ingesting everything (photos, batches, facts), so when it resumes it
   *  knows what was said while a person was talking. Any reply already queued
   *  for that chat is dropped. */
  pauseChat: (chatId: string) =>
    apiFetch<{ paused: true; by: string }>(
      `/whatsapp/chats/${encodeURIComponent(chatId)}/pause`, { method: 'POST', body: '{}' },
    ),
  /** « Plainte résolue » (owner, 2026-10-15): closes the complaint; the agent comes back
   *  when the pause was the complaint's own. Never closed by the code. */
  resolveComplaint: (chatId: string) =>
    apiFetch<{ ok: true; wasOpen: boolean; resumed: boolean }>(
      `/whatsapp/chats/${encodeURIComponent(chatId)}/complaint/resolve`, { method: 'POST', body: '{}' },
    ),
  /** Give it back to the assistant. */
  resumeChat: (chatId: string) =>
    apiFetch<{ paused: false }>(
      `/whatsapp/chats/${encodeURIComponent(chatId)}/resume`, { method: 'POST', body: '{}' },
    ),

  numbers: () => apiFetch<{
    numbers: WhatsAppNumber[]; orphans: WhatsAppOrphanSession[]; relay: WhatsAppRelayHealth;
  }>('/whatsapp/numbers'),
  adopt: (sessionId: string, label: string) =>
    apiFetch<{ number: WhatsAppNumber }>('/whatsapp/numbers/adopt', {
      method: 'POST', body: JSON.stringify({ session_id: sessionId, label }),
    }),
  addNumber: (label: string) =>
    apiFetch<{ number: WhatsAppNumber }>('/whatsapp/numbers', { method: 'POST', body: JSON.stringify({ label }) }),
  pairingCode: (id: string, phone: string) =>
    apiFetch<{ number: WhatsAppNumber }>(`/whatsapp/numbers/${id}/pairing-code`, { method: 'POST', body: JSON.stringify({ phone }) }),
  reconnect: (id: string) =>
    apiFetch<{ number: WhatsAppNumber }>(`/whatsapp/numbers/${id}/reconnect`, { method: 'POST', body: '{}' }),
  disconnect: (id: string) =>
    apiFetch<{ number: WhatsAppNumber }>(`/whatsapp/numbers/${id}/disconnect`, { method: 'POST', body: '{}' }),
  remove: (id: string) =>
    apiFetch<{ ok: boolean; pending_teardown: boolean }>(`/whatsapp/numbers/${id}`, { method: 'DELETE' }),

  // ── The brain: what the assistant is taught ────────────────────────────────
  knowledge: () => apiFetch<{ knowledge: AssistantKnowledge[]; assistant_enabled: boolean }>('/whatsapp/knowledge'),
  addKnowledge: (data: { kind: 'instruction' | 'image' | 'template'; title: string; body?: string; storage_path?: string; section?: string }) =>
    apiFetch<{ entry: AssistantKnowledge }>('/whatsapp/knowledge', { method: 'POST', body: JSON.stringify(data) }),
  updateKnowledge: (id: string, data: { title?: string; body?: string; enabled?: boolean; section?: string }) =>
    apiFetch<{ entry: AssistantKnowledge }>(`/whatsapp/knowledge/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteKnowledge: (id: string) =>
    apiFetch<{ ok: boolean }>(`/whatsapp/knowledge/${id}`, { method: 'DELETE' }),
  knowledgeUploadUrl: (filename: string) =>
    apiFetch<{ upload_url: string; storage_path: string }>('/whatsapp/knowledge/upload-url', {
      method: 'POST', body: JSON.stringify({ filename }),
    }),
  setEnabled: (enabled: boolean) =>
    apiFetch<{ ok: boolean }>('/whatsapp/settings', {
      method: 'POST', body: JSON.stringify({ assistant_enabled: enabled }),
    }),
  saveSettings: (data: { assistant_model?: string; reply_delay_seconds?: number }) =>
    apiFetch<{ ok: boolean }>('/whatsapp/settings', { method: 'POST', body: JSON.stringify(data) }),
  usage: () => apiFetch<AssistantUsage>('/whatsapp/usage'),

  /** Chat with the assistant without touching WhatsApp. Same prompt, knowledge
   *  and parser as a real message; writes nothing to any conversation. Files
   *  are named by the path `simulateUpload` gave them (2026-10-01). */
  simulate: (
    messages: SimulateMessage[],
    /** ⚠️ WITHOUT THIS THE SIMULATION SEES NONE OF THE CUSTOMER'S STATE. The
     *  live agent is handed fifteen fact blocks — the order, the photos
     *  actually received, the intake step, the payment stage, an open
     *  complaint. Passing a chat runs the same build against that customer, so
     *  the reply is the one they would really get. */
    chatId?: string | null,
  ) =>
    apiFetch<SimulateResult>('/whatsapp/simulate', {
      method: 'POST',
      body: JSON.stringify(chatId ? { messages, chat_id: chatId } : { messages }),
    }),

  /** A file the SIMULATED customer sends (owner, 2026-10-01). Kept where the
   *  panel keeps its files, judged by the panel's limits — and, unlike
   *  `sendMedia`, sent to nobody and pausing nobody. */
  simulateUpload: (a: { file: Blob; filename: string; kind: 'image' | 'video' | 'voice' | 'document' }) => {
    const form = new FormData();
    form.append('file', a.file, a.filename);
    form.append('kind', a.kind);
    return apiFetchForm<SimulateUpload>('/whatsapp/simulate/media', form);
  },

  /** Fresh one-hour links for the simulator's own files (the thread outlives them). */
  simulateMediaUrls: (paths: string[]) =>
    apiFetch<{ urls: Record<string, string> }>('/whatsapp/simulate/media-urls', {
      method: 'POST', body: JSON.stringify({ paths }),
    }),

  /** Every department with the WhatsApp numbers that cover it (migration 113).
   *  ⚠️ Must go through the worker — `role_contacts` has RLS on with nothing
   *  granted to the browser, because it holds staff phone numbers. */
  roleContacts: () => apiFetch<{ roles: RoleWithContacts[] }>('/whatsapp/role-contacts'),

  addRoleContact: (data: { role_id: string; phone: string; label?: string | null }) =>
    apiFetch<{ contact: RoleContact }>('/whatsapp/role-contacts', {
      method: 'POST', body: JSON.stringify(data),
    }),

  updateRoleContact: (id: string, patch: { enabled?: boolean; label?: string | null }) =>
    apiFetch<{ contact: RoleContact }>(`/whatsapp/role-contacts/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(patch),
    }),

  deleteRoleContact: (id: string) =>
    apiFetch<{ ok: true }>(`/whatsapp/role-contacts/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  /** How many customers heard the incident announcement and have not been
   *  picked back up yet. */
  /** Follow-ups scheduled on a day a CLIENT named — who, their words, when,
   *  and the exact message (the worker's own composer). Read-only. */
  clientPromises: () =>
    apiFetch<{ enabled: boolean; items: ClientPromiseItem[] }>('/whatsapp/client-promises'),

  /** Agent v2 `reactions`: the last 20 react-only decisions + the count per day. Read-only. */
  agentReactions: () =>
    apiFetch<{
      mode: string; items: ReactionDecisionItem[]; per_day: Array<{ day: string; count: number }>; table_missing?: boolean;
    }>('/whatsapp/agent-v2/reactions'),

  /** Agent v2 `actions`: newest first — all, one chat, or only those waiting for the owner. */
  agentActions: (o: { chat?: string | null; pending?: boolean; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (o.chat) q.set('chat', o.chat);
    if (o.pending) q.set('pending', '1');
    if (o.limit) q.set('limit', String(o.limit));
    const qs = q.toString();
    return apiFetch<{ items: AgentActionItem[]; table_missing?: boolean }>(`/whatsapp/agent-v2/actions${qs ? `?${qs}` : ''}`);
  },
  /** Put the album back as it was before the agent's action. */
  undoAgentAction: (id: string) =>
    apiFetch<{ ok: true }>(`/whatsapp/agent-v2/actions/${encodeURIComponent(id)}/undo`, { method: 'POST' }),
  /** The owner's OK: applied through the agent's own applier (or « à faire à la main »). */
  approveAgentAction: (id: string) =>
    apiFetch<{ ok: true; manual: boolean }>(`/whatsapp/agent-v2/actions/${encodeURIComponent(id)}/approve`, { method: 'POST' }),
  /** The owner's no: nothing changes. */
  rejectAgentAction: (id: string) =>
    apiFetch<{ ok: true }>(`/whatsapp/agent-v2/actions/${encodeURIComponent(id)}/reject`, { method: 'POST' }),

  /** Album edit agent phase 1: Claude's shadow runs next to Qwen's real edit, newest first. */
  editShadows: (limit = 20) =>
    apiFetch<{ items: EditShadowItem[]; thumbs: Record<string, string> }>(`/whatsapp/agent-v2/edit-shadows?limit=${limit}`),
  /** The owner's verdict on one shadow run. */
  editShadowVerdict: (id: string, verdict: 'right' | 'wrong' | 'partly', note?: string) =>
    apiFetch<{ ok: true }>(`/whatsapp/agent-v2/edit-shadows/${encodeURIComponent(id)}/verdict`, { method: 'POST', body: JSON.stringify({ verdict, note }) }),

  /** Agent v2 `playbooks`: proposed first, then approved, then rejected. */
  agentPlaybooks: () =>
    apiFetch<{ items: PlaybookItem[]; table_missing?: boolean }>('/whatsapp/agent-v2/playbooks'),
  /** The owner's OK (optionally with his wording): goes live when the switch is. */
  approvePlaybook: (id: string, editedText?: string | null) =>
    apiFetch<{ ok: true }>(`/whatsapp/agent-v2/playbooks/${encodeURIComponent(id)}/approve`, {
      method: 'POST', body: JSON.stringify({ edited_text: editedText ?? null }),
    }),
  /** His wording, status unchanged. */
  editPlaybook: (id: string, text: string) =>
    apiFetch<{ ok: true }>(`/whatsapp/agent-v2/playbooks/${encodeURIComponent(id)}/edit`, {
      method: 'POST', body: JSON.stringify({ text }),
    }),
  /** Never used, never proposed again. */
  rejectPlaybook: (id: string) =>
    apiFetch<{ ok: true }>(`/whatsapp/agent-v2/playbooks/${encodeURIComponent(id)}/reject`, { method: 'POST' }),

  /** Agent v2 `first_reply`: the welcome A/B, per arm (last 30 days). Read-only. */
  agentWelcomeAb: () =>
    apiFetch<{ mode: string; since: string; arms: WelcomeArmStats[]; column_missing?: boolean }>('/whatsapp/agent-v2/first-reply'),

  resumePending: () => apiFetch<{ pending: number }>('/whatsapp/announcement/resume'),

  /** Pick the conversations back up. ⚠️ Sends to real customers — scattered
   *  randomly across `window_hours` so the number is not flagged. */
  resumeAfterIncident: (data: { text: string; window_hours: number }) =>
    apiFetch<{
      queued: number; skipped: number; window_hours?: number;
      first_at?: string | null; last_at?: string | null; reason?: string;
    }>('/whatsapp/announcement/resume', { method: 'POST', body: JSON.stringify(data) }),

  /** The scenario suite, listed. Cheap — no model call. */
  scenarios: () => apiFetch<{ scenarios: ScenarioDef[] }>('/whatsapp/scenarios'),

  /** Run ONE scenario. ⚠️ Costs a model call each time — the caller loops so
   *  progress is visible and a failure costs one scenario, not the run. */
  runScenario: (id: string) =>
    apiFetch<ScenarioRun>(`/whatsapp/scenarios/${encodeURIComponent(id)}/run`, {
      method: 'POST', body: '{}',
    }),

  /** Ask what rule was missing, given a correction. Returns a PROPOSAL — the
   *  admin approves before anything is written to the brain. */
  teach: (data: {
    messages: Array<{ role: 'user' | 'assistant'; content: string }>;
    wrong_reply: string;
    corrected_reply: string;
  }) => apiFetch<{ proposal: TeachProposal; preview_reply: string | null }>('/whatsapp/teach', {
    method: 'POST', body: JSON.stringify(data),
  }),

  // ── Conversations (read-only — there is no send endpoint) ──────────────────
  conversations: () => apiFetch<{ conversations: WhatsAppConversation[] }>('/whatsapp/conversations'),

  /** Orders being assembled in the conversation, before they are orders.
   *  Reads `whatsapp_contact_facts.intake` — no `orders` row exists yet, which
   *  is the point: nothing half-finished can reach production or a count. */
  formingOrders: () => apiFetch<{ forming: FormingOrder[] }>('/whatsapp/forming-orders'),
  /** Who is waiting on us, oldest first: photos in and no preview, a handover nobody answered. */
  waitingOnUs: () => apiFetch<{ waiting: WaitingItem[] }>('/whatsapp/waiting-on-us'),
  /** Agent messages ≤10 min after a person's, no client message between — per day, target 0 (2026-10-16). */
  agentOverlapDaily: () => apiFetch<AgentOverlapDaily>('/whatsapp/agent-overlap-daily'),
  conversation: (chatId: string) =>
    apiFetch<{
      chat_id: string; messages: WhatsAppMessage[]; draft: WhatsAppDraft | null;
      draft_stale: boolean; draft_status: DraftStatus | null;
      /** Who holds it + the two switches for THIS chat (2026-10-18). Absent from an older worker = both off. */
      takeover?: ChatTakeoverState;
      /** An open complaint on THIS chat — even when it is too old for the list (2026-10-27). */
      complaint?: { open_at: string; quote: string | null } | null;
    }>(`/whatsapp/conversations/${encodeURIComponent(chatId)}`),

  /** ❤️ on a message, as a real WhatsApp reaction (2026-10-18). `emoji: ''`
   *  takes ours back. NOT a reply: never pauses the agent. */
  react: (chatId: string, targetWaMessageId: string, emoji: string) =>
    apiFetch<{ ok: boolean; emoji: string; target: string }>(`/whatsapp/conversations/${encodeURIComponent(chatId)}/react`, {
      method: 'POST', body: JSON.stringify({ target_wa_message_id: targetWaMessageId, emoji }),
    }),

  /** The panel's heartbeat: « I am looking at this chat » — the takeover push
   *  stays quiet meanwhile, and its count restarts (2026-10-18/19). `viewing: false` when it closes. */
  viewingChat: (chatId: string, viewing = true) =>
    apiFetch<{ ok: boolean }>(`/whatsapp/chats/${encodeURIComponent(chatId)}/viewing`, {
      method: 'POST', body: JSON.stringify({ viewing }),
    }),

  /** The order card beside the thread (owner 2026-09-26). Read-only; the 3D
   *  link is built from the order's CURRENT token. */
  conversationOrder: (chatId: string, order?: string | null) =>
    apiFetch<ConversationOrderCard>(`/whatsapp/conversations/${encodeURIComponent(chatId)}/order${order ? `?order=${encodeURIComponent(order)}` : ''}`),

  /** Tie this conversation to one of the client's orders (2026-10-10, #MAN-082802).
   *  Only an order tied to no other chat. */
  linkConversationOrder: (chatId: string, shopifyOrderId: string) =>
    apiFetch<{ ok: boolean; already?: boolean }>(`/whatsapp/conversations/${encodeURIComponent(chatId)}/link-order`, {
      method: 'POST', body: JSON.stringify({ shopify_order_id: shopifyOrderId }),
    }),

  /** Search every order and every message — the list only holds the newest
   *  300 messages (owner 2026-09-26: « anything possible »). */
  searchConversations: (q: string) =>
    apiFetch<{ results: ConversationSearchResult[] }>(`/whatsapp/search?q=${encodeURIComponent(q)}`),

  /** Send a message to the customer from the panel — the assistant's
   *  suggestion or the human's own words, exactly as typed. Owner, 2026-09-25
   *  (A2): sending from here PAUSES the agent on that chat, same as the phone.
   *  `quotedId` makes it a reply to that message. */
  sendReply: (chatId: string, text: string, quotedId?: string | null) =>
    apiFetch<{ ok: boolean; paused?: boolean }>(`/whatsapp/conversations/${encodeURIComponent(chatId)}/send`, {
      method: 'POST', body: JSON.stringify({ text, ...(quotedId ? { quoted_wa_message_id: quotedId } : {}) }),
    }),

  /** ⏰ The clock next to Send (2026-10-10): the same message, sent at `sendAtIso`.
   *  The agent keeps answering until it leaves; it pauses then. */
  scheduleReply: (chatId: string, text: string, sendAtIso: string, quotedId?: string | null) =>
    apiFetch<{ ok: boolean; scheduled: { id: string; send_at: string } }>(`/whatsapp/conversations/${encodeURIComponent(chatId)}/send`, {
      method: 'POST', body: JSON.stringify({ text, send_at: sendAtIso, ...(quotedId ? { quoted_wa_message_id: quotedId } : {}) }),
    }),

  /** Messages of this chat waiting for their time. */
  scheduledReplies: (chatId: string) =>
    apiFetch<{ scheduled: ScheduledReply[] }>(`/whatsapp/conversations/${encodeURIComponent(chatId)}/scheduled`),

  cancelScheduledReply: (id: string) =>
    apiFetch<{ ok: boolean }>(`/whatsapp/scheduled/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),

  sendScheduledNow: (id: string) =>
    apiFetch<{ ok: boolean }>(`/whatsapp/scheduled/${encodeURIComponent(id)}/send-now`, { method: 'POST' }),

  /** A2: a voice note, image, video or any file, from the panel. Pauses the
   *  agent like any send from here. */
  sendMedia: (chatId: string, a: { file: Blob; filename: string; kind: 'image' | 'video' | 'voice' | 'document'; caption?: string; quotedId?: string | null }) => {
    const form = new FormData();
    form.append('file', a.file, a.filename);
    form.append('kind', a.kind);
    if (a.caption?.trim()) form.append('caption', a.caption.trim());
    if (a.quotedId) form.append('quoted_wa_message_id', a.quotedId);
    return apiFetchForm<{ ok: boolean; paused?: boolean }>(`/whatsapp/conversations/${encodeURIComponent(chatId)}/send-media`, form);
  },

  /** « Supprimer pour tout le monde » on one of OUR messages (2026-09-29).
   *  Queued for the relay; the message shows « Suppression en cours… » until
   *  WhatsApp confirms. Does NOT pause the agent. Refused past 48 h (409). */
  deleteMessage: (chatId: string, waMessageId: string) =>
    apiFetch<{ ok: boolean; state: 'pending' | 'deleted'; already?: boolean }>(
      `/whatsapp/conversations/${encodeURIComponent(chatId)}/messages/delete`,
      { method: 'POST', body: JSON.stringify({ wa_message_id: waMessageId }) },
    ),

  // ── Confirmation-agent journey (order-scoped) — AgentJourneyCard ───────────
  /** `orderId` is the internal `orders.id` UUID (matches `Order.id`), NOT the
   *  Shopify order id — the worker route joins on `whatsapp_journeys.order_id`. */
  journey: (orderId: string) =>
    apiFetch<{ journey: AgentJourney | null; outbox: AgentJourneyOutboxCounts | null }>(
      `/whatsapp/journeys/${orderId}`,
    ),
  /** "Confier à un humain" and its reverse. 404s if the order has no live
   *  journey — callers should only offer this once `journey` is non-null. */
  handover: (orderId: string, paused: boolean) =>
    apiFetch<{ ok: boolean; paused: boolean }>(`/whatsapp/journeys/${orderId}/handover`, {
      method: 'POST', body: JSON.stringify({ paused }),
    }),
};

/** The WhatsApp agent's test suite (2026-11-04). « Mauvaise réponse » on an
 *  agent message freezes the chat into a never-again test case. */
export const agentSuiteApi = {
  markWrong: (chatId: string, waMessageId: string, note: string) =>
    apiFetch<{ ok: boolean; id: string; must: string[]; mustNot: string[] }>('/agent-suite/wrong', {
      method: 'POST', body: JSON.stringify({ chatId, waMessageId, note }),
    }),
};

/** One taught fact. For kind='image' the `body` is the DESCRIPTION — that text
 *  is what reaches the model; the picture itself never does. */
export interface AssistantKnowledge {
  id: string;
  kind: 'instruction' | 'image' | 'template';
  /** 'general' | 'sales' — an editing grouping for the Brain tab only. Every
   *  enabled entry reaches the prompt the same way whatever its section. */
  section?: string;
  title: string;
  body: string | null;
  storage_path: string | null;
  /** Short-lived signed preview URL, images only. */
  url?: string | null;
  enabled: boolean;
  sort_order: number;
  created_at: string;
}
/** What the assistant has cost, straight from OpenRouter's own figures. */
export interface AssistantUsage {
  cost_today_usd: number;
  cost_30d_usd: number;
  calls_30d: number;
  tokens_30d: number;
  by_model: { model: string; calls: number; cost: number }[];
  models: { id: string; label: string; note: string }[];
  current_model: string;
  reply_delay_seconds: number;
}
/** A rule the assistant suggests adding or changing after a correction. */
export interface TeachProposal {
  mode: 'new' | 'update' | 'skip';
  title: string;
  body: string;
  reason: string;
  /** Set when it wants to replace an existing instruction. */
  target_id: string | null;
}
export interface RoleContact {
  id: string;
  role_id: string;
  /** Canonicalised server-side by the same helper the allowlist uses. */
  phone: string;
  label: string | null;
  enabled: boolean;
}

export interface RoleWithContacts {
  id: string;
  name: string;
  contacts: RoleContact[];
}

export interface ScenarioDef {
  id: string; title: string; intent: string; messages: string[];
  expectHuman: boolean | null;
}

/** One graded rule. Mirrors `RuleResult` in worker/src/lib/scenarioRules.ts. */
export interface ScenarioRuleResult {
  rule: string;
  pass: boolean;
  detail: string;
  /** `weak` = keyword-based, can be wrong. Rendered differently on purpose. */
  confidence: 'strong' | 'weak';
  /** Nothing to judge — shown as a dash, never as a pass. */
  skipped?: boolean;
}

export interface ScenarioRun {
  id: string; reply: string; needs_human: boolean; reason: string | null;
  model: string; results: ScenarioRuleResult[]; facts_used: boolean;
}

/** Why the assistant replied, or did not — computed by the worker so the
 *  wording lives in one place. See worker lib/draftStatus.ts. */
export interface DraftStatus {
  state: 'sent' | 'pending' | 'blocked' | 'needs_human' | 'no_reply' | 'ready';
  title: string;
  detail: string;
  /** The machine code behind it — a small « détails » line only (2026-10-07). */
  code?: string;
}

export interface WhatsAppConversation {
  chat_id: string;
  name: string | null;
  /** The customer's real number, resolved by the worker — NOT the chat id,
   *  which is a `@lid` privacy id on most threads. Null when we have none. */
  phone: string | null;
  last_body: string | null;
  last_at: string | null;
  count: number;
  has_draft?: boolean;
  needs_human?: boolean;
  /** From the customer's most recent order, not from WhatsApp — push_name is
   *  null for most chats, which is why the list read as raw phone numbers. */
  order_name?: string | null;
  stage?: string | null;
  /** WhatsApp's own name, when `name` was taken from her order (2026-10-30). */
  push_name?: string | null;
  /** Her orders — the one this chat is about + the others (2026-10-30). Absent on an older worker. */
  orders?: ConversationOrders | null;
}

/** Mirrors `ListOrder` in worker/src/lib/conversationOrders.ts. */
export interface ConversationListOrder {
  name: string;
  status: string;
  paid: 'full' | 'advance' | null;
  created_at: string | null;
}

/** Mirrors `ConversationOrders` in worker/src/lib/conversationOrders.ts. */
export interface ConversationOrders {
  current: ConversationListOrder;
  others: ConversationListOrder[];
  customer_name: string | null;
  customer_phone: string | null;
}

/** Mirrors `ConversationOrder` in worker/src/lib/conversationOrder.ts. */
export interface ConversationOrder {
  shopify_order_id: string;
  name: string;
  status: string;
  created_at: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  city: string | null;
  address: string | null;
  total_price: number | null;
  bundle: string | null;
  albums: number[];
  base_pages: number | null;
  extra_pages: number;
  extra_pages_cost: number;
  relief_3d: boolean;
  payment: { method: string | null; paid_in_full: boolean; paid_at: string | null; advance_amount: number | null; advance_at: string | null; receipt_pending?: boolean };
  /** null when there is no live token — never a dead link. */
  preview_url: string | null;
  previews_status: string | null;
  /** « Preview sent », computed by the worker (lib/previewState.ts). Null when
   *  it could not be read; absent on an older worker. */
  preview?: PreviewCard | null;
  parcel_code: string | null;
  parcel_status: string | null;
}

/**
 * Mirrors `PreviewCard` in worker/src/lib/previewState.ts — the ONE meaning of
 * « preview sent ». The web draws it and never re-derives it.
 * Guard: worker/src/lib/previewStateWebParity.test.ts
 */
export const PREVIEW_STAGES = ['not_built', 'built', 'sent', 'seen', 'approved'] as const;
export interface PreviewCard {
  stage: typeof PREVIEW_STAGES[number];
  label: string;
  chip_state: 'done' | 'todo' | 'warn' | 'info';
  warnings: string[];
  built: boolean;
  rebuilding: boolean;
  sent: boolean;
  first_sent_at: string | null;
  last_sent_at: string | null;
  last_sent_by: 'agent' | 'team' | null;
  current_version_sent: boolean;
  new_version_unannounced: boolean;
  approved: boolean;
}

export interface ConversationOrderCard {
  order: ConversationOrder | null;
  other_orders: Array<{ shopify_order_id: string; name: string; status: string; created_at: string | null }>;
  /** `phone` = no order is linked to this chat; found by the customer's number. */
  matched_by: 'chat' | 'phone' | null;
}

/** Mirrors `SearchResult` in worker/src/lib/conversationSearch.ts. */
export interface ConversationSearchResult {
  /** The chat id, or `order:<shopify_order_id>` for an order with no conversation. */
  key: string;
  chat_id: string | null;
  shopify_order_id: string | null;
  order_name: string | null;
  stage: string | null;
  name: string | null;
  phone: string | null;
  last_body: string | null;
  last_at: string | null;
  match: { reason: 'order' | 'phone' | 'name' | 'message'; snippet: string | null };
}
/** Mirrors `JourneyState` in `worker/src/lib/agentJourney.ts` — keep in sync
 *  by hand; the worker file is the source of truth. */
export type AgentJourneyState =
  | 'opened'
  | 'awaiting_destination'
  | 'awaiting_cover'
  | 'collecting_photos'
  | 'confirming_batch'
  | 'downloading'
  | 'building'
  | 'previewing'
  | 'awaiting_client_ok'
  | 'awaiting_payment'
  | 'awaiting_receipt'
  | 'ready_for_human'
  | 'done';
/** One order's WhatsApp confirmation-agent conversation — AgentJourneyCard's
 *  data source. `paused_reason` non-null means "Confié à un humain": the
 *  agent will not act on this order until it's cleared. */
export interface AgentJourney {
  id: string;
  state: AgentJourneyState;
  photo_count: number;
  last_inbound_at: string | null;
  wake_at: string | null;
  step_attempts: number;
  paused_reason: string | null;
  updated_at: string;
}
/** Queued/failed message counts for one journey's outbox — a stuck
 *  conversation shows up here before a customer ever complains. */
export interface AgentJourneyOutboxCounts {
  queued: number;
  failed: number;
  /** WHY the newest failure happened, in words the admin can act on — never a
   *  guess. Null when nothing has failed. See worker lib/sendFailure.ts. */
  failure_reason?: string | null;
  failure_kind?: 'recipient_unreachable' | 'session_down' | 'unknown' | null;
}
/** One turn of a simulation. A file is named by the path `simulateUpload`
 *  gave it; a transcript already heard is sent back so it is not heard twice. */
export interface SimulateMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  media?: { path: string; kind: 'image' | 'video' | 'voice' | 'document'; mime: string; filename?: string | null };
  transcript?: string | null;
}
export interface SimulateResult {
  reply: string | null;
  needs_human: boolean;
  reason: string | null;
  model: string | null;
  /** false = ran without a chat, so the reply saw no customer state. */
  facts_used?: boolean;
  /** true = the agent answers this outright, without calling the model. */
  decided_without_model?: boolean;
  audio_error?: string | null;
  /** What each voice note sent in this call said, or why nobody heard it. */
  voice?: Array<{ id: string; transcript: string | null; error: string | null }>;
  /** The Brain images and videos the reply carries, one message each, as sent. */
  reply_media?: Array<{ url: string; kind: 'image' | 'video' | 'document' }>;
}
export interface SimulateUpload {
  path: string;
  url: string | null;
  kind: 'image' | 'video' | 'voice' | 'document';
  mime: string;
  filename: string | null;
  bytes: number;
}
export interface WhatsAppMessage {
  id: string;
  wa_message_id: string;
  body: string | null;
  direction: 'in' | 'out';
  push_name: string | null;
  has_media: boolean;
  media_mimetype: string | null;
  media_filename: string | null;
  /** A one-hour signed URL for the photo the customer sent, or null while its
   *  bytes are still downloading. `album-photos` is private — see the route. */
  media_url?: string | null;
  wa_timestamp: string | null;
  created_at: string;
  // ── A2 (2026-09-25): computed by the worker, drawn as-is ──────────────────
  /** Who wrote it. */
  /** `auto` = the phone's own WhatsApp Business greeting / away message (2026-10-21). */
  sender?: 'client' | 'agent' | 'person' | 'auto';
  kind?: 'text' | 'image' | 'video' | 'voice' | 'audio' | 'document' | 'sticker';
  /** What a reply must quote; null when WhatsApp's id for it is unknown. */
  quotable_id?: string | null;
  /** Ticks for our side: sent → delivered → read, or failed. */
  status?: 'sent' | 'delivered' | 'read' | 'failed' | 'pending' | 'unconfirmed' | null;
  /** What a voice note says. */
  transcript?: string | null;
  /** Why nobody could hear it — shown in place of « Transcription en cours… »
   *  (the Simulator, 2026-10-01). */
  transcript_error?: string | null;
  quoted_wa_message_id?: string | null;
  quoted_body?: string | null;
  // ── Delete for everyone (2026-09-29, migration 194) ──────────────────────
  /** Until when WhatsApp still lets us delete it for everyone (48 h after it
   *  left); null = never (hers, no WhatsApp id, already deleted). Computed by the worker. */
  delete_until?: string | null;
  /** pending → deleted, or failed (then `delete_error` says why). */
  delete_state?: 'pending' | 'deleted' | 'failed' | null;
  /** Set once the gateway confirmed the delete. The row is kept, shown as deleted. */
  deleted_at?: string | null;
  delete_error?: string | null;
  delete_requested_by?: string | null;
  /** ❤️ under the bubble (2026-10-18) — hers and ours. Only sent while reactions are on for the chat. */
  reactions?: Array<{ emoji: string; by: 'client' | 'staff' }>;
}

/** Who holds a chat and what is on for it (worker lib/takeoverPing.ts takeoverPanelState). */
export interface ChatTakeoverState {
  /** The push to whoever holds the chat is on here (was a WhatsApp ping until 2026-10-19). */
  alerts: boolean;
  /** Reactions + the emoji picker are on here. */
  reactions: boolean;
  paused: boolean;
  owner_email: string | null;
  /** Full name, else the email. */
  owner_name: string | null;
  /** 'holder' = the holder gets the push (a device is on); 'none' = nobody is told. */
  recipient: 'holder' | 'none';
}
export type OrderNoteStep = 'photos' | 'album' | 'pdf' | 'preview' | 'delivery' | 'followup';
/** An internal note on an order (order page redesign, 2026-10-07). */
export interface OrderNote {
  id: string; note: string; author_email: string | null; created_at: string;
  step?: OrderNoteStep | null; is_problem?: boolean; done_at?: string | null; done_by?: string | null;
}

export interface WhatsAppDraft {
  chat_id: string;
  reply: string | null;
  /** The assistant itself saying a person should handle this one. */
  needs_human: boolean;
  reason: string | null;
  /** sent · pending · sending · blocked · failed · none (2026-10-07: the send box reads it). */
  send_state?: string | null;
  model: string | null;
  error: string | null;
  updated_at: string;
}

export const usersApi = {
  mePermissions: () => apiFetch<{ permissions: string[] }>('/users/me/permissions'),
  list: () => apiFetch<{ users: StaffUser[] }>('/users'),
  roles: () => apiFetch<{ roles: StaffRole[] }>('/users/roles'),
  create: (data: { email: string; password: string; full_name: string; whatsapp?: string; role_ids: string[]; commission_rate?: number | null }) =>
    apiFetch<{ ok: boolean; user_id: string }>('/users', { method: 'POST', body: JSON.stringify(data) }),
  auditLog: (before?: string) =>
    apiFetch<{ entries: { id: string; at: string; actor_email: string | null; action: string; entity_type: string | null; entity_id: string | null; summary: string | null }[] }>(
      `/users/audit-log${before ? `?before=${encodeURIComponent(before)}` : ''}`),
  loginEvent: () => apiFetch<{ ok: boolean }>('/users/me/login-event', { method: 'POST', body: '{}' }),
  permissionRegistry: () => apiFetch<{ permissions: string[] }>('/users/permissions'),
  rolePermissions: (roleId: string) => apiFetch<{ permissions: string[] }>(`/users/roles/${roleId}/permissions`),
  createRole: (data: { name: string; description?: string; permissions: string[] }) =>
    apiFetch<{ role: StaffRole }>('/users/roles', { method: 'POST', body: JSON.stringify(data) }),
  updateRole: (roleId: string, data: { name?: string; description?: string; permissions?: string[] }) =>
    apiFetch<{ ok: boolean }>(`/users/roles/${roleId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteRole: (roleId: string) => apiFetch<{ deleted: boolean }>(`/users/roles/${roleId}`, { method: 'DELETE' }),
  update: (userId: string, data: { full_name?: string; whatsapp?: string; is_active?: boolean; role_ids?: string[]; new_password?: string; commission_rate?: number | null }) =>
    apiFetch<{ ok: boolean }>(`/users/${userId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  // ── Agent performance dashboard + commission payouts (admin-only) ──────────
  agentDashboard: (userId: string) => apiFetch<AgentDashboard>(`/users/${userId}/agent-dashboard`),
  /**
   * The CALLER's own agent dashboard. Same payload as `agentDashboard`, but the
   * agent id comes from the token server-side — there is no id to pass, and
   * therefore none to tamper with. Any authenticated staff may call it.
   */
  myAgentDashboard: () => apiFetch<AgentDashboard>('/users/me/agent-dashboard'),
  agentPayoutUploadUrl: (userId: string, filename: string, contentType: string) =>
    apiFetch<{ upload_url: string; storage_path: string; content_type: string }>(
      `/users/${userId}/agent-payout/upload-url`,
      { method: 'POST', body: JSON.stringify({ filename, content_type: contentType }) }),
  agentPayout: (userId: string, receiptStoragePath: string) =>
    apiFetch<{ ok: boolean; payout_id: string; amount: number; order_count: number; expense_id: string | null; expense_error: string | null }>(
      `/users/${userId}/agent-payout`,
      { method: 'POST', body: JSON.stringify({ receipt_storage_path: receiptStoragePath }) }),
};

/** One editor-history checkpoint (an editing session that ended). */
export interface SessionCheckpoint {
  id: string;
  snapshot_at: string;
  trigger: 'idle' | 'sweep' | 'pre-restore' | 'pre-destructive' | 'pre-relayout' | 'save' | 'pre-note' | 'pre-agent';
  page_count: number;
  photo_count: number;
  /** true when this checkpoint == the album's current state. */
  is_current: boolean;
}

// ─── Claim-based assignment (2026-08-11) ─────────────────────────────────────
export interface QueueOrder {
  id: string;
  shopify_order_id: string;
  shopify_order_name: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  bundle: string | null;
  status: string;
  total_price: number | string | null;
  created_at: string;
  assigned_agent_id: string | null;
  claimed_at: string | null;
  assignment_activity_at: string | null;
  /** Admin-only `held` list carries the holder; never present for agents. */
  profiles?: { full_name: string | null; email: string | null } | null;
}

export interface OrderQueue {
  /** Unclaimed — anyone may take these. */
  available: QueueOrder[];
  /** This caller's own claims. */
  mine: QueueOrder[];
  /** Held by ANOTHER agent. Listed so an order never appears to have vanished,
   *  but with the holder's identity stripped server-side — an agent sees that
   *  it is taken, never by whom. */
  taken: QueueOrder[];
  /** `enabled: false` (today's default) means no limit is applied at all. */
  cap: { enabled: boolean; limit: number; held: number };
  /** Admins only: every held order, with who holds it. */
  held?: QueueOrder[];
}

export interface AgentDashboard {
  agent: { id: string; full_name: string | null; email: string | null };
  confirmed_count: number;
  pipeline: Record<string, number>;
  active_orders: { shopify_order_id: string; name: string | null; customer: string | null; status: string; confirmed_at: string }[];
  activity: { at: string; action: string; entity_type: string | null; entity_id: string | null; summary: string | null }[];
  conversion: { delivered: number; cancelled: number; rate_pct: number | null };
  avg_creation_to_confirm_minutes: number | null;
  earnings: { total: number; paid: number; unpaid: number; unpaid_count: number };
  payouts: { id: string; amount: number; order_count: number; period_from: string | null; period_to: string | null; receipt_url: string; created_at: string; created_by: string | null }[];
}

// ─── Products catalogue (migration 071) ──────────────────────────────────────
// Products are authored in SHOPIFY ONLY. This mirrors them locally; the admin
// adds the two judgements Shopify cannot hold: `enabled` (does the system use
// it) and `kind` (Offre / Upsell). Title, price and image are Shopify's and are
// read-only here — a local edit could only ever drift from what is really billed.

export interface CatalogueVariant {
  id: string;
  product_id: string;
  shopify_variant_gid: string;
  title: string | null;
  /** Selling price mirrored from Shopify. Read-only. */
  price: number | null;
  image_url: string | null;
  /** What it costs US to make. Ours, editable. NULL = unknown (never 0). */
  production_cost: number | null;
  position: number | null;
  synced_at: string | null;
}

export interface CatalogueProduct {
  id: string;
  shopify_product_gid: string;
  handle: string | null;
  title: string | null;
  image_url: string | null;
  status: string | null;
  kind: 'offer' | 'upsell' | null;
  enabled: boolean;
  production_cost: number | null;
  synced_at: string | null;
  variants: CatalogueVariant[];
}

export const productsApi = {
  list: () => apiFetch<{ products: CatalogueProduct[] }>('/products'),
  sync: () =>
    apiFetch<{ ok: true; products: number; variants: number; pages: number }>(
      '/products/sync', { method: 'POST' }),
  update: (id: string, data: { enabled?: boolean; kind?: 'offer' | 'upsell' | null; production_cost?: number | null }) =>
    apiFetch<{ product: CatalogueProduct }>(`/products/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  updateVariant: (id: string, production_cost: number | null) =>
    apiFetch<{ variant: CatalogueVariant }>(`/products/variants/${id}`, {
      method: 'PATCH', body: JSON.stringify({ production_cost }),
    }),
};

/** GET /settings/theme-catalog — design system v2: every model a theme uses,
 *  expanded as it prints with stand-in photos and sample words (admin only;
 *  worker lib/themes/gallery.ts). A page is what ThemedPageView draws. */
export interface ThemeCatalogPhoto { id: string; width: number; height: number; faces?: Array<{ x: number; y: number; width: number; height: number }> | null }
export interface ThemeCatalogPage {
  model: string; theme: string; colourway: string; side: 'left' | 'right'; spreadHalf?: 'left' | 'right';
  blank_color: string; background: { imageUrl?: string; color: string; tint?: string[] };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  elements: Array<any>;
}
export interface ThemeCatalogModel {
  code: string; name: string; kind: 'simple' | 'decorated' | 'special'; style: string;
  minAlbumPages: number | null; favourite: boolean; spread: boolean;
  photos: ThemeCatalogPhoto[]; pages: ThemeCatalogPage[]; noPhotoPage: ThemeCatalogPage | null;
}
export interface ThemeCatalogTheme {
  slug: string; name: string; on: boolean; piecesOnline: boolean;
  colourways: Array<{ id: string; name: string; paper: string; accent: string }>;
}
export interface ThemeCatalogResponse {
  theme: { slug: string; name: string };
  colourway: { id: string; name: string; paper: string; accent: string; ink: string };
  /** The admin's colour the gallery is drawn in (?accent=), else null. */
  accentOverride?: string | null;
  facts: Record<string, string | null | undefined>;
  models: ThemeCatalogModel[];
  counts: { simple: number; decorated: number; special: number };
  themes: ThemeCatalogTheme[];
  themesSwitch: boolean;
  themesOnList: string[];
}

// ─── « Demander le paiement » (owner, 2026-10-14) ───────────────────────────
// Mirrors worker/src/lib/paymentRequestCard.ts + routes/paymentRequest.ts.

export interface PaymentRequestLine { label: string; amount: number }
export interface PaymentRequestPart { key: string; text: string; locked: boolean }
export interface PaymentRequestDraft {
  parts: PaymentRequestPart[];
  text: string;
  lines: PaymentRequestLine[];
  total: number;
  advance: number | null;
  remainder: number;
  alreadyReceived: number;
  locale: 'fr' | 'ary';
  localeFallback: boolean;
  lockHash: string;
}
export type PaymentRequestResult =
  | { ok: true; draft: PaymentRequestDraft }
  | { ok: false; blocked: string; lines: PaymentRequestLine[]; total: number | null };
export interface PaymentRequestPreview {
  enabled: boolean;
  order: { shopify_order_id: string | null; name: string | null; chat_id: string | null };
  result: PaymentRequestResult;
  warnings: string[];
  /** What the client writes in, as detected: 'fr' | 'ar' | 'ary-latn' | 'en' | null. */
  client_locale: string | null;
}

export const paymentRequestApi = {
  /** The message as it would go, built from the order — nothing is sent. */
  preview: (ref: { order: string } | { chat: string }) =>
    apiFetch<PaymentRequestPreview>(
      'order' in ref
        ? `/payment-request/preview?order=${encodeURIComponent(ref.order)}`
        : `/payment-request/preview?chat=${encodeURIComponent(ref.chat)}`,
    ),
  /** Send it: the server rebuilds the amounts and refuses if they moved since `lock_hash`. */
  send: (body: { order: string; lock_hash: string; edits: { intro?: string; closing?: string } }) =>
    apiFetch<{ ok: true }>('/payment-request/send', { method: 'POST', body: JSON.stringify(body) }),
};

/** GET /settings/album-theme/:sessionId — one album's theme settings and the defaults they fall back to. */
export interface AlbumThemeInfo {
  /** the theme this album gets: staff's choice, else the cover's, else Voyage */
  theme: string;
  /** staff's choice (migration 248): a slug, 'none', or null = the cover picks */
  chosen?: string | null;
  /** false before migration 248 */
  themeReady?: boolean;
  /** the theme the cover picks among the themes on (null = none) */
  coverTheme?: string | null;
  /** The theme the cover names even when switched off (2026-10-17). */
  coverNamedTheme?: string | null;
  /** the master switch app_settings.themes_enabled */
  themesSwitch?: boolean;
  themes?: Array<{ slug: string; name: string; on: boolean }>;
  /** saved colourway (null = auto) */
  saved: string | null;
  /** what the cover picks now */
  auto: string;
  colourways: Array<{ id: string; name: string; accent?: string; paper?: string }>;
  /** false before migration 244 */
  ready: boolean;
  /** the admin's « Couleur du thème » (null = the cover's) */
  accent?: string | null;
  /** false before migration 246 */
  accentReady?: boolean;
  defaultAccent?: string;
  accentSecond?: string | null;
  /** the album's own bars (null = default) */
  pct?: number | null;
  variety?: number | null;
  /** what null means: the global setting, else the theme's default */
  defaultPct?: number;
  defaultVariety?: number;
}

export const settingsApi = {
  get: () => apiFetch<{ settings: AppSettings }>('/settings'),

  /** « Thème de l'album » bar — one album's settings (admin; settings.manage). null = back to the default.
   *  `pct`/`variety`: the two bars (null = global). `colourway` (migration 244): an id, or null = auto.
   *  `accent` (migration 246): #RRGGBB, or null = the cover's. `theme` (migration 248): a theme slug
   *  staff picked for THIS album, 'none' = no theme, null = the cover picks. Send each column alone:
   *  before its migration only that one fails. */
  albumThemeBars: (sessionId: string, bars: { pct?: number | null; variety?: number | null; colourway?: string | null; accent?: string | null; theme?: string | null }) =>
    apiFetch<{ ok: true }>(`/settings/album-theme-bars/${sessionId}`, { method: 'PATCH', body: JSON.stringify(bars) }),

  /** Everything the « Thème de l'album » bar shows for one album (see AlbumThemeInfo). */
  albumTheme: (sessionId: string) => apiFetch<AlbumThemeInfo>(`/settings/album-theme/${sessionId}`),

  /** The advance the checkout promises a client before she orders — the
   *  figure the payment request will quote (the saved « RIB paiment » reply's
   *  first). Public; null = say « une petite avance ». */
  advance: () => apiFetch<{ min_advance: number | null }>('/settings/advance'),

  /** Design system v2 gallery (admin, templates.view): every model `theme` uses, in `colourway` (default its first). */
  /** `accent` (#RRGGBB): preview the theme in that colour — the admin's « Couleur du thème » (tinted pieces + accent). */
  themeCatalog: (theme: string, colourway?: string | null, accent?: string | null) =>
    apiFetch<ThemeCatalogResponse>(`/settings/theme-catalog?theme=${encodeURIComponent(theme)}${colourway ? `&colourway=${encodeURIComponent(colourway)}` : ''}${accent ? `&accent=${encodeURIComponent(accent.replace('#', ''))}` : ''}`),

  update: (data: Partial<AppSettings>) =>
    apiFetch<{ settings: AppSettings }>('/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  uploadAsset: (filename: string, asset_type: string) =>
    apiFetch<{
      upload_url: string;
      token: string;
      storage_path: string;
      public_url: string;
      asset_type: string;
    }>('/settings/upload-asset', {
      method: 'POST',
      body: JSON.stringify({ filename, asset_type }),
    }),
};

// ─── Flipbook preview link + assets ──────────────────────────────────────────
// 3D flipbook the client flips through before print (worker routes/preview-link.ts).

export interface PreviewNote {
  id: string;
  album_index: number;
  page_number: number;
  note: string;
  round: number;
  created_at: string;
  resolved_at: string | null;
}

export interface PreviewAlbumData {
  album_index: number;
  page_count: number;
  page_aspect: number;
  generated_at: string | null;
  cover: Partial<Record<'front' | 'spine' | 'back',
    { url: string | null; aspect: number | null; lqip: string | null } | null>>;
  pages: Array<{ n: number; url: string | null; lqip: string | null }>;
}

/** One album's quick 3D redraw (worker lib/quickJobs.ts quickStatusView). */
export interface QuickJobView {
  album: number;
  state: 'queued' | 'running' | 'done' | 'failed' | 'skipped' | 'expired';
  stage: string | null;
  done: number;
  total: number;
  pct: number;
  requestedAt: string;
  finishedAt: string | null;
  ms: number | null;
  error: string | null;
}
export interface QuickStatusResponse {
  quick: QuickJobView[];
  print: { pdf_status: string | null; pdf_render_started_at: string | null; pdf_render_error: string | null; previews_status: string | null } | null;
  runner: boolean;
}

export interface PreviewPublicData {
  order: {
    shopify_order_name: string | null;
    customer_first_name: string | null;
    bundle: 'Solo' | 'Duo' | 'Trio' | null;
    client_review_status: 'pending' | 'changes_requested' | 'approved' | null;
    client_review_at: string | null;
  };
  albums: PreviewAlbumData[];
  /** Albums bought but not finished yet — shown to the customer as
   *  "en préparation" instead of silently missing from a Duo/Trio. */
  pending_albums?: number[];
  total_albums?: number;
  notes: PreviewNote[];
  /** Preview render state — lets the page poll while 'generating' and resolve
   *  deterministically instead of hanging on the spinner. */
  previews_status?: 'generating' | 'ready' | 'failed' | null;
  previews_generated_at?: string | null;
  /** True once nothing more will happen automatically — the retry budget is
   *  spent or the failure needs a human. Without it a pending album that has
   *  been abandoned looks identical to one still rendering, and the customer
   *  waits forever on "en préparation" (order #1144). */
  preview_stopped?: boolean;
  /** Album edited after this book was rendered — show "mise à jour en cours"
   *  rather than passing old pages off as current (#1241). */
  preview_outdated?: boolean;
  /** The albums that are behind their edits — the banner shows on those only. */
  preview_outdated_albums?: number[];
  /** A new version on its way: the quick redraw per album and the print. */
  building?: { quick: Array<{ album: number; state: string; done: number; total: number; pct: number }>; print: boolean };
}

export const previewLinkApi = {
  // Admin
  generate: (shopifyOrderId: string, rotate = false) =>
    apiFetch<{ token: string; preview_url: string }>(
      `/preview-link/generate/${shopifyOrderId}${rotate ? '?rotate=1' : ''}`, { method: 'POST' }),
  revoke: (shopifyOrderId: string) =>
    apiFetch<{ success: boolean }>(`/preview-link/revoke/${shopifyOrderId}`, { method: 'POST' }),
  adminNotes: (shopifyOrderId: string) =>
    apiFetch<{
      preview_url: string | null;
      client_review_status: 'pending' | 'changes_requested' | 'approved' | null;
      client_review_at: string | null;
      notes: PreviewNote[];
    }>(`/preview-link/notes/${shopifyOrderId}`),
  resolveNotes: (shopifyOrderId: string) =>
    apiFetch<{ success: boolean }>(`/preview-link/resolve-notes/${shopifyOrderId}`, { method: 'POST' }),
  // Public (token is the pass — apiFetch sends no JWT for anonymous visitors)
  public: (token: string) =>
    apiFetch<PreviewPublicData>(`/preview-link/public/${encodeURIComponent(token)}`),
  addNote: (token: string, body: { album_index: number; page_number: number; note: string }) =>
    apiFetch<{ note: PreviewNote }>(`/preview-link/public/${encodeURIComponent(token)}/notes`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  deleteNote: (token: string, noteId: string) =>
    apiFetch<{ success: boolean }>(
      `/preview-link/public/${encodeURIComponent(token)}/notes/${noteId}`, { method: 'DELETE' }),
  confirm: (token: string) =>
    apiFetch<{ client_review_status: 'changes_requested' | 'approved'; open_notes: number }>(
      `/preview-link/public/${encodeURIComponent(token)}/confirm`, { method: 'POST' }),
};

export const previewAssetsApi = {
  uploadUrls: (shopifyOrderId: string, albumIndex: number, files: string[]) =>
    apiFetch<{ urls: Array<{ file: string; upload_url: string; storage_path: string }> }>(
      `/preview-assets/upload-urls/${shopifyOrderId}`, {
        method: 'POST', body: JSON.stringify({ album_index: albumIndex, files }),
      }),
  reset: (shopifyOrderId: string, albumIndex: number) =>
    apiFetch<{ success: boolean; removed: number }>(
      `/preview-assets/reset/${shopifyOrderId}`, {
        method: 'POST', body: JSON.stringify({ album_index: albumIndex }),
      }),
  /** Delete the album's preview files no book uses any more (age-guarded on the
   *  worker). Called after a new manifest is written — never before. */
  prune: (shopifyOrderId: string, albumIndex: number) =>
    apiFetch<{ success: boolean; removed: number }>(
      `/preview-assets/prune/${shopifyOrderId}`, {
        method: 'POST', body: JSON.stringify({ album_index: albumIndex }),
      }),
  /** The album's stored 3D-preview manifest, raw (admin). Null = none yet. */
  manifest: (shopifyOrderId: string, albumIndex: number) =>
    apiFetch<{ manifest: unknown }>(`/preview-assets/manifest/${shopifyOrderId}?album=${albumIndex}`),
};

// ─── Storage Upload Helper ────────────────────────────────────────────────────

/**
 * Upload a file to Supabase Storage using a signed upload URL.
 * Returns the public URL when done.
 */
// Resilient signed-URL uploader (stall watchdog + retry). Implementation lives
// in ./uploadToSignedUrl; re-exported here so existing `@/lib/api` imports keep
// working. See that module for why the plain no-timeout XHR was replaced.
export { uploadToSignedUrl, PDF_UPLOAD } from './uploadToSignedUrl';
export type { UploadOptions } from './uploadToSignedUrl';

// ─── Photos API ───────────────────────────────────────────────────────────────

export interface Photo {
  id: string;
  album_session_id: string;
  original_filename: string;
  storage_path: string;
  /** JPEG sibling for HEIC originals; null for already-renderable formats. */
  preview_storage_path: string | null;
  /** FIX 3 — small ~512px JPEG thumbnail path for the editor's fast-loading
   *  tier. Distinct from preview_storage_path; null until uploaded/backfilled. */
  thumbnail_storage_path?: string | null;
  /** FIX 3 — signed view URL for the small thumbnail, decorated by the worker
   *  on photosApi.list(). Null when no thumbnail exists yet. */
  thumbnail_signed_url?: string | null;
  width: number | null;
  height: number | null;
  /** Net rotation the PDF must still apply at draw to match the editor's
   *  displayed orientation. Invariant: stored bytes are kept upright, so this
   *  is 0 for normalized photos (migration 047). Never inferred from EXIF. */
  orientation?: number | null;
  orientation_source?: string | null;
  file_size_kb: number | null;
  exif_date: string | null;
  exif_gps_lat: number | null;
  exif_gps_lng: number | null;
  face_count: number;
  face_data: unknown | null;
  is_blurry: boolean;
  // Phase 1 — analysis metadata used by the smart layout engine
  aspect_ratio: 'landscape' | 'portrait' | 'square' | null;
  face_spread: number | null;
  analyzed_at: string | null;
  created_at: string;
  // Phase 5 — vision tags from Gemini Flash. null = not analyzed yet.
  vision_tags?: {
    subject?: 'baby' | 'child' | 'adult' | 'group' | 'object' | 'landscape' | 'indoor' | 'pet' | 'unknown';
    hero_score?: number; // 0..10
    face_visible?: boolean;
    has_date_stamp?: boolean;
    is_sideways?: boolean;
    background_quality?: 'clean' | 'noisy' | 'distracting';
    caption?: string;
  } | null;
  vision_analyzed_at?: string | null;
  /** Set by the list endpoint when the original storage object is absent or
   *  0 bytes (a failed upload that was nonetheless marked done). The flow page
   *  renders these as a retry tile rather than a silent blank, and never counts
   *  them as added photos. */
  original_missing?: boolean;
  /** Migration 267 — a burst / near-identical shot: in the tray, and the
   *  automatic layout leaves it out unless a person placed it (lib/photoDedup). */
  layout_hold?: 'similar' | null;
  similar_to?: string | null;
  /** Set only on `hidden_duplicates` rows: the photo this one duplicates. */
  duplicate_of?: string | null;
  dup_reason?: string | null;
}

/** A photo the dedup hid (migration 267), as the list hands it back apart. */
export type HiddenDuplicatePhoto = Photo & {
  signed_url: string | null;
  tray_signed_url?: string | null;
  duplicate_of: string | null;
  dup_reason: string | null;
};

export interface PhotoDedupItem {
  id: string;
  keeperId: string;
  albumSessionId: string;
  kind: 'hidden' | 'similar' | 'placed_twice' | 'other_album';
  reason: string | null;
  tile_url: string | null;
  keeper_tile_url: string | null;
}
export interface PhotoDedupSummary {
  hidden: number;
  similar: number;
  placedTwice: number;
  otherAlbum: number;
  items: PhotoDedupItem[];
}

export interface PhotoUploadUrlResponse {
  // Absent when `duplicate` is true (the worker created no row, no upload URL).
  upload_url?: string;       // PUT target for the full-res original (storage_path)
  photo_id?: string;
  storage_path?: string;
  // Small editor tier — the worker already mints this; uploading here first
  // (then stamping via thumbnailDone) makes the upload feel instant while the
  // full-res original uploads in the background.
  thumbnail_upload_url?: string | null;
  thumbnail_storage_path?: string;
  // MIGRATION 128 — the ~320px tray tile, replacing a billed image-transform.
  tile_upload_url?: string | null;
  tile_storage_path?: string;
  // Resumable / TUS block for the original. Null when SUPABASE_JWT_SECRET is
  // unset → the client falls back to the signed-URL PUT.
  tus?: TusBlock | null;
  duplicate?: boolean;
}

export const photosApi = {
  getUploadUrl: (
    session_id: string,
    filename: string,
    content_type: string,
    dedup?: { content_hash?: string | null; file_size_kb?: number | null },
  ) =>
    apiFetch<PhotoUploadUrlResponse>('/photos/upload-url', {
      method: 'POST',
      body: JSON.stringify({ session_id, filename, content_type, ...dedup }),
    }),

  /** Mint a fresh TUS auth JWT so a long resumable upload (flow path) can swap
   *  its Authorization header before the 1h token expires. */
  /** A fresh upload token for THIS file only (storage RLS, migration 240). */
  tusRefresh: async (objectPath: string): Promise<string> => {
    const data = await apiFetch<{ token: string }>('/photos/tus-refresh', {
      method: 'POST',
      body: JSON.stringify({ object_path: objectPath }),
    });
    return data.token;
  },

  /** Stamp the small editor thumbnail path after its bytes were PUT, so the
   *  list endpoint serves signed_url = thumbnail (fast editor) while the
   *  full-res original uploads in the background. */
  thumbnailDone: (
    photoId: string,
    thumbnail_storage_path: string,
    dims?: { width?: number | null; height?: number | null },
    /** The ~320px tray tile (migration 128) — stamped in the same call so a
     *  successful upload costs one request, not two. */
    tile_storage_path?: string | null,
  ) =>
    apiFetch<{ success: boolean }>(`/photos/${photoId}/thumbnail-done`, {
      method: 'PATCH',
      body: JSON.stringify({
        thumbnail_storage_path,
        ...(tile_storage_path ? { tile_storage_path } : {}),
        ...(dims?.width ? { width: dims.width } : {}),
        ...(dims?.height ? { height: dims.height } : {}),
      }),
    }),

  updateMetadata: (photoId: string, metadata: {
    face_count: number;
    face_data: unknown;
    exif_date: string | null;
    exif_gps_lat: number | null;
    exif_gps_lng: number | null;
    is_blurry: boolean;
    width: number;
    height: number;
    file_size_kb: number;
    // FIX 3 — small editor thumbnail path, when the client uploaded one.
    thumbnail_storage_path?: string | null;
  }) =>
    apiFetch<{ photo: Photo }>(`/photos/${photoId}/metadata`, {
      method: 'POST',
      body: JSON.stringify(metadata),
    }),

  list: (sessionId: string) =>
    apiFetch<{
      photos: (Photo & {
        signed_url: string | null;
        /** Small (~320px) tier for the photo-LIST grids (desktop tray, slot
         *  chooser, mobile "Mes photos"). NOT used by canvas/preview/PDF. */
        tray_signed_url?: string | null;
        original_signed_url: string | null;
        preview_signed_url: string | null;
        original_missing?: boolean;
      })[];
      /** Migration 267 — hidden duplicates, for « Doublons masqués ». */
      hidden_duplicates?: HiddenDuplicatePhoto[];
    }>(`/photos/session/${sessionId}`),

  /** « Restaurer » — a hidden duplicate (or held similar shot) becomes an
   *  ordinary photo of the album again, in the tray. */
  restoreDuplicate: (photoId: string) =>
    apiFetch<{ ok: boolean; album_session_id?: string }>(`/photos/${photoId}/restore-duplicate`, { method: 'POST' }),

  delete: (photoId: string) =>
    apiFetch<{ success: boolean }>(`/photos/${photoId}`, { method: 'DELETE' }),

  /** Get a signed upload URL for OVERWRITING the photo's existing bytes.
   *  Used by the rotate-photo flow — client uploads rotated JPEG back to
   *  the same storage_path so nothing else in the codebase needs to know
   *  about rotation. */
  overwriteUrl: (photoId: string) =>
    apiFetch<{ upload_url: string; token: string; storage_path: string; previous_storage_path?: string }>(
      `/photos/${photoId}/overwrite-url`,
      { method: 'POST' },
    ),

  /** Authoritatively confirm the original's bytes actually landed in storage
   *  (object present AND non-zero size). Called right after an upload resolves,
   *  before marking the photo done — a TUS "success" can fire without all bytes
   *  committed, so we never count a byteless photo as added. */
  verifyOriginal: (photoId: string) =>
    apiFetch<{ ok: boolean; size?: number; error?: string }>(
      `/photos/${photoId}/verify`,
      { method: 'GET' },
    ),

  /** SERVER-TRUTH batch byte-check for the pre-submit gate: which of this
   *  session's photos have NO full-res original in storage (absent/0-byte
   *  object)? Consulted by finalizeOriginals regardless of the in-memory
   *  tracker — after a refresh that tracker is empty and must not pass. */
  originalsStatus: (sessionId: string) =>
    apiFetch<{
      checked: number;
      missing: { photo_id: string; original_filename: string | null; has_thumbnail: boolean }[];
    }>(`/photos/session/${sessionId}/originals-status`),

  /** Tell the worker the photo was rotated — it swaps width/height and
   *  clears stale face_data so the lazy analyzer re-detects in the new
   *  orientation on next editor load. */
  postRotate: (photoId: string, dims: { new_width: number; new_height: number; storage_path?: string }) =>
    apiFetch<{ success: boolean }>(`/photos/${photoId}/post-rotate`, {
      method: 'PATCH',
      body: JSON.stringify(dims),
    }),

  /** Run the vision model (Gemini Flash) on every photo in a session and
   *  persist the resulting tags into `photos.vision_tags`. Returns counts
   *  so the UI can show progress. Idempotent — photos with fresh tags
   *  (< 7 days) are skipped. Used by the editor's "Analyser les photos"
   *  button so the AI layout has real content judgement, not just face
   *  geometry. */
  visionAnalyzeSession: (sessionId: string) =>
    apiFetch<{
      total: number;
      analyzed: number;
      cached: number;
      failed: number;
      results: Array<{ id: string; status: 'analyzed' | 'cached' | 'failed' }>;
    }>(`/photos/session/${sessionId}/vision-analyze-all`, {
      method: 'POST',
    }),

  // Bulk-wipe every photo for a session (admin-only). Used to clean up test
  // data or reset a customer's album from scratch.
  wipeSession: (sessionId: string) =>
    apiFetch<{ success: boolean; storage_deleted: number; db_deleted: number; total_paths: number }>(
      `/photos/session/${sessionId}/wipe`,
      { method: 'DELETE' },
    ),

  // Backfill: trigger HEIC → JPEG conversion for every photo in a session
  // that doesn't yet have a preview. Returns counts so the UI can confirm.
  convertHeicSession: (sessionId: string) =>
    apiFetch<{ success: boolean; total: number; succeeded: number; failed: number; message?: string }>(
      `/photos/session/${sessionId}/convert-heic`,
      { method: 'POST' },
    ),

  // Back-cover photos go to album-photos bucket under back-cover/ prefix
  // and are tracked separately in cover_customizations.backCoverImages.
  // No DB row is created — the storage_path is the canonical reference.
  getBackCoverUploadUrl: (session_id: string, filename: string, content_type: string) =>
    apiFetch<{ upload_url: string; token: string; storage_path: string; content_type: string }>(
      '/photos/back-cover-upload-url',
      { method: 'POST', body: JSON.stringify({ session_id, filename, content_type }) },
    ),
  // Sign view URLs for back-cover storage paths. Has to go through the worker
  // (service role) because RLS on the album-photos bucket blocks browser-side
  // signing for the back-cover/ prefix. Also handles the blank-cover/ prefix
  // (the /back-cover-sign route whitelists both).
  signBackCoverUrls: (storage_paths: string[]) =>
    apiFetch<{ urls: { storage_path: string | null; signed_url: string | null }[] }>(
      '/photos/back-cover-sign',
      { method: 'POST', body: JSON.stringify({ storage_paths }) },
    ),

  // Client uploads their OWN front-cover image for a blank ("vierge") cover.
  // Goes to album-photos under blank-cover/{session_id}/{uuid}.{ext}; persisted
  // as cover_customizations.blankCoverImage = { storage_path }. Sign view URLs
  // with signBackCoverUrls (it whitelists the blank-cover/ prefix too).
  getBlankCoverUploadUrl: (session_id: string, filename: string, content_type: string) =>
    apiFetch<{ upload_url: string; token: string; storage_path: string; content_type: string }>(
      '/photos/blank-cover-upload-url',
      { method: 'POST', body: JSON.stringify({ session_id, filename, content_type }) },
    ),
};

// ─── Sessions API ─────────────────────────────────────────────────────────────

export type AlbumCategory =
  | 'travel' | 'baby' | 'wedding' | 'birthday'
  | 'graduation' | 'pet' | 'family' | 'religious';

export const ALBUM_CATEGORIES: readonly AlbumCategory[] = [
  'travel', 'baby', 'wedding', 'birthday',
  'graduation', 'pet', 'family', 'religious',
] as const;

export interface AlbumSession {
  id: string;
  order_id: string;
  album_index: number;
  destination: string | null;
  year: string | null;
  cover_id: string | null;
  cover_destination_text: string | null;
  /** He chose to print NO words on the cover — a decision, not a blank.
   *  `albumScoreboard.titleSettled` reads it so nobody asks him again. */
  title_declined?: boolean | null;
  cover_year_text: string | null;
  cover_subtitle_text: string | null;
  cover_customizations: unknown;
  page_count: number | null;
  pages: unknown;
  status: string;
  last_saved_at: string;
  created_at: string;
  /** Album type — drives the AI prompt's subject-priority rules and the
   *  client-side preferredMinRank rules. Sourced from the Shopify product
   *  the customer bought (future webhook wiring) or set manually today. */
  album_category: AlbumCategory | null;
  /** Admin toggle: when true, the AI lays photos out in UPLOAD ORDER (the order
   *  the customer added photos = photos.created_at ASC), ignoring EXIF/date.
   *  Default false = today's date-based AI ordering. Only the photo SEQUENCE
   *  changes; layout/grouping/cropping/face work is untouched. See ai.ts. */
  use_upload_order?: boolean;
  /** Last save by a person (editor / restore) and who. Migration 145. */
  hand_edited_at?: string | null;
  hand_edited_by?: string | null;
  /** B5 — gap between adjacent photos, mm (0 = flush). Migration 143. */
  /** Base layout rules stamp (migration 234). 'base-v1' = 12/15 mm margins,
   *  4 mm gaps on every grid page. Absent = edge to edge, as before. */
  layout_rules?: string | null;
  photo_gap_mm?: number;
  /** B5 — colour shown in the gap, `#RRGGBB`. */
  photo_gap_color?: string;
  /** Admin-granted one-shot re-open of a submitted album's editor. The
   *  customer-facing gate lets them back in while true; /submit resets it to
   *  false. See migration 017 + editorAccess.ts. */
  editor_unlocked?: boolean;
  /** Admin-opened "share for upload" window: the customer can re-enter a
   *  submitted album and APPEND more photos until upload_allowed_until passes.
   *  Surfaces the prominent "Ajouter des photos" button. /submit resets it.
   *  See migration 018 + editorAccess.ts. */
  upload_allowed?: boolean;
  /** ISO timestamp when the share-for-upload window closes (grant + 48h). */
  upload_allowed_until?: string | null;
  /** Per-album generated PDF URLs (mirrored by /pdf/finalize). */
  cover_pdf_url?: string | null;
  interior_pdf_url?: string | null;
  full_book_pdf_url?: string | null;
  /** Content hash of this album's PDF source at last generation + when it ran.
   *  Drives skip-unchanged-album-on-regenerate. See albumSourceHash + migration 029. */
  pdf_source_hash?: string | null;
  pdf_generated_at?: string | null;
  /** Furthest flow step the customer reached (monotonic, server-tracked):
   *  design → upload → mode → magic → preview → editor → confirm → success.
   *  Read on resume to land on the EXACT step (clamped to what data supports).
   *  See sessionsApi.advanceFlowStep + worker resume-link deriveSubState. */
  flow_step?: string | null;
}


/** A receipt the assistant read, waiting on a person. Migration 121. */
export interface PaymentProposal {
  id: string;
  order_id: string;
  /** What the model read off the slip. Null means it could not read it — which
   *  is a real answer, and must be shown as one rather than as a zero. */
  amount: number | null;
  payee: string | null;
  paid_on: string | null;
  kind: 'advance' | 'full' | null;
  proof_url: string | null;
  /** The automatic check passed. `false` is not a rejection — it is the reason
   *  this needs eyes. */
  verdict_ok: boolean;
  verdict_reason: string | null;
  created_at: string;
  order: {
    id: string;
    shopify_order_name: string | null;
    customer_name: string | null;
    total_price: number | string | null;
    advance_received_amount: number | null;
    status: string | null;
  } | null;
}

/** The four things a conversation can be owed that an order row cannot show.
 *  Migration 123. */
export type OpenLoopKind = 'add_album' | 'edits' | 'photos_promised' | 'promise_unkept';

/** Something the WhatsApp conversation says is still owed. Migration 123. */
export interface OpenLoop {
  id: string;
  order_id: string;
  kind: OpenLoopKind;
  /** The customer's (or our) own words, copied from the chat and truncated.
   *  ⚠️ NEVER MODEL PROSE — the worker refuses any quote that is not in the
   *  transcript character for character. */
  quote: string | null;
  album_index: number | null;
  /** When it was said. This is what makes an old loop look old. */
  asked_at: string | null;
  created_at: string;
  order: {
    id: string;
    shopify_order_id: string | null;
    shopify_order_name: string | null;
    customer_name: string | null;
    customer_phone: string | null;
    status: string | null;
    paid_at: string | null;
  } | null;
}

export const sessionsApi = {
  get: (orderId: string, albumIndex: number) =>
    apiFetch<{ session: AlbumSession | null }>(`/sessions/${orderId}/${albumIndex}`),

  upsert: (orderId: string, albumIndex: number, data: Partial<AlbumSession> & {
    /** Last-writer guard: last_saved_at of the state the editor loaded. The
     *  worker 409s (code stale_write) when the stored row is newer. */
    expected_last_saved_at?: string;
  }, opts?: { keepalive?: boolean }) =>
    apiFetch<{ session: AlbumSession }>(`/sessions/${orderId}/${albumIndex}`, {
      method: 'POST',
      body: JSON.stringify(data),
      // keepalive lets the request COMPLETE even after iOS evicts/unloads the
      // page (the "white reload on photo-picker open" memory-kill). Without it
      // the flush save is aborted mid-flight and the last edits never reach the
      // server → work lost. Body must stay < 64KB (true for real albums).
      keepalive: opts?.keepalive,
    }),

  // ── Editor checkpoint history (operators only) ─────────────────────────────
  history: (orderId: string, albumIndex: number) =>
    apiFetch<{ checkpoints: SessionCheckpoint[] }>(`/sessions/${orderId}/${albumIndex}/history`),
  restoreCheckpoint: (orderId: string, albumIndex: number, checkpointId: string) =>
    apiFetch<{ ok: boolean; session: AlbumSession }>(
      `/sessions/${orderId}/${albumIndex}/history/${checkpointId}/restore`, { method: 'POST', body: '{}' }),

  /** Monotonic "furthest flow step reached" marker. The worker only advances
   *  (never regresses) the stored album_sessions.flow_step, so the resume /
   *  continue link can land on the EXACT step — including routing an
   *  "opened the editor, no layout yet" session to /editor instead of /flow.
   *  Callers fire-and-forget; errors are swallowed (never block the UI). */
  advanceFlowStep: (orderId: string, albumIndex: number, step: string): void => {
    void apiFetch<{ flow_step: string | null }>(
      `/sessions/${orderId}/${albumIndex}/flow-step`,
      { method: 'POST', body: JSON.stringify({ step }) },
    ).catch(() => {});
  },
};

// ─── AI Layout API ────────────────────────────────────────────────────────────

export interface LayoutPage {
  page: number;
  layout: '1-up' | '2-up' | '3-up' | '4-up' | 'wide-top' | '3-up-flip' | '2-up-side' | '5-up' | '6-up' | '4-up-main' | '2-up-even';
  photos: string[];
  /** Per-slot crop position the customer set in the editor. Keyed by
   *  `s{slotIndex}`. Optional — older sessions don't have it and the
   *  PDF generator falls back to the auto face-centroid crop. */
  slot_positions?: Record<string, { x: number; y: number; scale?: number; rotation?: number }>;
  /** WIDE-TOP layout only — top band height as a fraction of page height
   *  (0..1). The wide group photo (slot 0) fills the full-width top band at
   *  its exact aspect ratio (zero crop, every face shown); the companion
   *  (slot 1) fills the remaining bottom band. The editor, preview, and PDF
   *  generators all derive the two slot rectangles from this single value so
   *  the render pipelines stay visually identical. */
  top_h_frac?: number;
  /** Optional Canva-style template id the AI assigned to this page.
   *  When set, the editor renders the template instead of the grid
   *  layout. Validated worker-side against the AI-eligible template set
   *  before being returned. */
  template_id?: string | null;

  /** BLANK PAGE (2026-07-05) — true = pure white divider page: no photo
   *  slots, PDF fills white; the customer can add text/photos/icons as
   *  free elements. Reversible (toggle back to the AI layout). Kept in the
   *  page array so page count / divisible-by-4 are unaffected. */
  blank?: boolean;
  /** « PAGE DE FIN » (coupons plan 2026-10-28, lib/outroPage.ts): 'outro' =
   *  this page draws the brand outro image (outro_page_2_url) full page in the
   *  editor, preview and PDF. Always paired with `blank: true` and no photos. */
  kind?: 'outro';
  /** BLANK PAGE colour (2026-07-06) — fill for a blank page; default white. */
  blank_color?: string;
}

export interface ChapterMeta {
  id: number;
  label: string;
  photoCount: number;
}

export interface LayoutPlan {
  pages: LayoutPage[];
  unassigned_photos: string[];
  /** photo id → chapter id. Present only for multi-destination albums.
   *  Absent ⇒ the client pipeline runs exactly as it did pre-chapters. */
  photo_chapters?: Record<string, number>;
  /** Travel-ordered chapter metadata. Present only when photo_chapters is. */
  chapters?: ChapterMeta[];
}

export const aiApi = {
  generateLayout: (session_id: string, total_pages: number, layout_preference: string, opts: {
    /** The order id in the page's URL (/flow/:id, /editor/:id/…). REQUIRED —
     *  /ai/layout is locked (2026-10-13) and this is the customer's pass: the
     *  worker checks it is the order this album belongs to. Admins pass too. */
    shopify_order_id: string;
    signal?: AbortSignal; use_templates?: boolean; use_upload_order?: boolean; layout_override?: string;
    /** false = the prewarm: compute only, the worker writes nothing. */
    persist?: boolean }) =>
    apiFetch<{ layout: LayoutPlan }>('/ai/layout', {
      method: 'POST',
      body: JSON.stringify({
        session_id,
        shopify_order_id: opts.shopify_order_id,
        total_pages,
        layout_preference,
        // #1194: a call from the app is a PERSON. The worker refuses machine
        // callers on a hand-edited album; a click here is the one thing it
        // lets through. The prewarm is not a click — it persists nothing.
        source: opts?.persist === false ? 'prewarm' : 'human',
        ...(opts?.persist === false ? { persist: false } : {}),
        // The editor's live layout toggle. When present the worker uses it in
        // preference to the (possibly stale) stored order.layout.
        ...(opts?.layout_override !== undefined ? { layout_override: opts.layout_override } : {}),
        ...(opts?.use_templates !== undefined ? { use_templates: opts.use_templates } : {}),
        // FIX #4 (2026-07-05): AI Magic sends use_upload_order:true so first
        // generation honours the customer's upload sequence (see worker).
        ...(opts?.use_upload_order !== undefined ? { use_upload_order: opts.use_upload_order } : {}),
      }),
      signal: opts?.signal,
    }),

  /**
   * Resilience poll (Fix D). Returns the RAW AI layout the worker persisted via
   * waitUntil for this session — survives the customer backgrounding their
   * phone mid-generation (which kills the awaited generateLayout fetch). The
   * caller MUST compare `pending.photos_key` to the current photo set's
   * `photosKey()` and ignore a mismatch (stale — generated for a different
   * set). `generating` echoes whether a run is still in flight (≤ 3 min old).
   */
  pendingLayout: (session_id: string, opts?: { signal?: AbortSignal }) =>
    apiFetch<{
      pending: { result: LayoutPlan; photos_key: string; source: 'ai' | 'deterministic'; created_at: string } | null;
      generating: boolean;
    }>(`/ai/layout/pending/${session_id}`, { signal: opts?.signal }),

  /** Clear pending_ai_layout + ai_generating_at after the client finalizes a
   *  generation, so a later poll/reload never re-finalizes a consumed result.
   *  Fire-and-forget; idempotent server-side. */
  clearPendingLayout: (session_id: string): void => {
    // keepalive: finalize navigates to 'preview' right after firing this — a
    // plain fetch dies with the page and the pending survives as a landmine
    // that re-applies the old AI plan over newer edits (#D294). The worker now
    // also self-clears server-side (saved-after-generated / 24h expiry), this
    // is belt-and-braces.
    void apiFetch(`/ai/layout/pending/${session_id}/clear`, { method: 'POST', keepalive: true })
      .catch(() => { /* swallow — the worker's staleness guards cover it */ });
  },
  /**
   * Fire-and-forget — log a customer-side editor event into layout_feedback.
   * Failures are swallowed so feedback can never break the editor.
   */
  feedback: (session_id: string, event_type: string, payload?: unknown): void => {
    void apiFetch('/ai/feedback', {
      method: 'POST',
      body: JSON.stringify({ session_id, event_type, payload }),
    }).catch(() => { /* swallow */ });
  },
};

// ─── Magic upload link (admin generates, client uses) ────────────────────────

export interface UploadLinkOrder {
  /** Routes the post-upload flow CTA (/flow/:id). */
  shopify_order_id?: string;
  customer_name: string | null;
  bundle: 'Solo' | 'Duo' | 'Trio' | null;
  page_count: number;
  destinations: Array<{ name: string; year: string | null }>;
}

export interface UploadLinkSession {
  id: string;
  album_index: number;
  destination: string | null;
  year: string | null;
  /** Small public cached cover thumbnail for the album-selection cards. */
  cover_thumbnail_url?: string | null;
  /** The album's selected cover, with the fields needed to composite the FULL
   *  cover preview (bg color + design icon + title text) via <CoverThumbnail>,
   *  exactly like the cover-selection page. `thumbnail_url`/`design_url` alone
   *  are icon-only — the full look is composited at render time. */
  cover?: Pick<
    Cover,
    'id' | 'name' | 'design_url' | 'thumbnail_url' | 'dominant_color' | 'text_zones' | 'format' | 'width_px' | 'height_px' | 'back_cover_style'
  > | null;
  /** Photos the client has already uploaded to this album. */
  photo_count?: number;
}

export const resumeLinkApi = {
  /** Generate (or fetch the existing) smart-resume link for an order. The
   *  public /continue/:token route derives the sub-state and redirects the
   *  client into the normal flow (/flow/:id or /editor/...). Idempotent unless
   *  `rotate` is passed. */
  generate: (shopifyOrderId: string, rotate = false) =>
    apiFetch<{ token: string; continue_url: string }>(
      `/resume-link/generate/${shopifyOrderId}${rotate ? '?rotate=1' : ''}`,
      { method: 'POST' },
    ),
};

export const uploadLinkApi = {
  // ── admin ──
  generate: (shopifyOrderId: string) =>
    apiFetch<{ token: string; expires_at: string; upload_url: string }>(
      `/upload-link/generate/${shopifyOrderId}`,
      { method: 'POST' },
    ),
  revoke: (shopifyOrderId: string) =>
    apiFetch<{ success: boolean }>(`/upload-link/revoke/${shopifyOrderId}`, {
      method: 'POST',
    }),

  // ── public (no auth) ──
  resolve: async (token: string): Promise<{
    order: UploadLinkOrder;
    sessions: UploadLinkSession[];
    expires_at: string | null;
  }> => {
    const res = await fetch(`${API_BASE}/upload-link/public/${token}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error ?? `HTTP ${res.status}`);
    }
    return res.json();
  },
  photoUploadUrl: async (
    token: string,
    body: {
      session_id: string;
      filename: string;
      content_type: string;
      submission_batch_id?: string;
      content_hash?: string | null;
      file_size_kb?: number | null;
    },
  ): Promise<{
    // Upload fields absent when `duplicate` is true.
    upload_url?: string;
    photo_id?: string;
    storage_path?: string;
    content_type?: string;
    preview_upload_url?: string | null;
    preview_storage_path?: string;
    // FIX 3 — parallel small-thumbnail upload URL + its storage path.
    thumbnail_upload_url?: string | null;
    thumbnail_storage_path?: string;
    // MIGRATION 128 — the ~320px tray tile, replacing a billed transform.
    tile_upload_url?: string | null;
    tile_storage_path?: string;
    // Resumable / TUS block for the original. Null when the worker's
    // SUPABASE_JWT_SECRET is unset → client falls back to the signed-URL PUT.
    tus?: TusBlock | null;
    duplicate?: boolean;
  }> => {
    const res = await fetch(`${API_BASE}/upload-link/public/${token}/photo-upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error ?? `HTTP ${res.status}`);
    }
    return res.json();
  },

  // Mint a fresh TUS auth JWT so a long resumable upload can swap its
  // Authorization header before the original 1h token expires.
  /** A fresh upload token for THIS file only (storage RLS, migration 240). */
  tusRefresh: async (token: string, objectPath: string): Promise<string> => {
    const res = await fetch(`${API_BASE}/upload-link/public/${token}/tus-refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ object_path: objectPath }),
    });
    if (!res.ok) throw new Error(`tus-refresh HTTP ${res.status}`);
    const data = (await res.json()) as { token: string };
    return data.token;
  },

  // Save EXIF + dimensions + (optionally) the preview path. Called once per
  // photo after the original upload finishes. Non-blocking on the submit
  // pipeline — if it errors, the photo still exists, just without metadata.
  photoMetadata: async (
    token: string,
    photoId: string,
    body: {
      exif_date?: string | null;
      exif_gps_lat?: number | null;
      exif_gps_lng?: number | null;
      width?: number | null;
      height?: number | null;
      file_size_kb?: number | null;
      preview_uploaded?: boolean;
      preview_storage_path?: string | null;
      // FIX 3 — small editor thumbnail path, when the client uploaded one.
      thumbnail_storage_path?: string | null;
      tile_storage_path?: string | null;
    },
  ): Promise<{ success: boolean; updated: string[] }> => {
    const res = await fetch(`${API_BASE}/upload-link/public/${token}/photo-metadata/${photoId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error ?? `HTTP ${res.status}`);
    }
    return res.json();
  },

  // List the photos already uploaded to one album (review gallery). Token is
  // the pass — no JWT. Returns a signed thumbnail URL per photo. Photos are
  // baked upright at intake, so the client renders signed_url as-is.
  listPhotos: async (
    token: string,
    albumIndex: number,
  ): Promise<{
    photos: Array<{
      id: string;
      signed_url: string | null;
      orientation: number | null;
      width: number | null;
      height: number | null;
    }>;
  }> => {
    const res = await fetch(
      `${API_BASE}/upload-link/public/${token}/photos?album_index=${albumIndex}`,
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error ?? `HTTP ${res.status}`);
    }
    return res.json();
  },

  // Delete photos from one album. Worker scopes the delete to this album's
  // session — ids from another album/order are ignored. Returns how many rows
  // were actually deleted.
  deletePhotos: async (
    token: string,
    albumIndex: number,
    photoIds: string[],
  ): Promise<{ deleted: number }> => {
    const res = await fetch(`${API_BASE}/upload-link/public/${token}/photos`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ album_index: albumIndex, photo_ids: photoIds }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error ?? `HTTP ${res.status}`);
    }
    return res.json();
  },

  // Rename one album (title = album_sessions.destination, max 60 chars).
  // Token is the pass — no JWT.
  setAlbumTitle: async (
    token: string,
    session_id: string,
    title: string,
  ): Promise<{ success: boolean; destination: string }> => {
    const res = await fetch(`${API_BASE}/upload-link/public/${token}/album-title`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id, title }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error ?? `HTTP ${res.status}`);
    }
    return res.json();
  },
};

// ─── Shopify status check ─────────────────────────────────────────────────────

export const shopifyApi = {
  status: () =>
    apiFetch<{ connected: boolean; install_url: string }>('/shopify/status'),
};

// ─── Editor Resources — fonts + icons (Sections 8 & 9) ───────────────────────

export interface EditorFont {
  id: string;
  name: string;
  google_url: string;
  is_active?: boolean;
  sort_order?: number;
  created_at: string;
}

export interface EditorIcon {
  id: string;
  name: string;
  svg: string;
  tags: string[];
  sort_order?: number;
  created_at: string;
}

export const editorResourcesApi = {
  listFonts: () => apiFetch<{ fonts: EditorFont[] }>('/editor-resources/fonts'),
  addFont: (body: { name: string; google_url: string; sort_order?: number }) =>
    apiFetch<{ font: EditorFont }>('/editor-resources/fonts', { method: 'POST', body: JSON.stringify(body) }),
  deleteFont: (id: string) =>
    apiFetch<{ success: boolean }>(`/editor-resources/fonts/${id}`, { method: 'DELETE' }),

  listIcons: () => apiFetch<{ icons: EditorIcon[] }>('/editor-resources/icons'),
  addIcon: (body: { name: string; svg: string; tags?: string[]; sort_order?: number }) =>
    apiFetch<{ icon: EditorIcon }>('/editor-resources/icons', { method: 'POST', body: JSON.stringify(body) }),
  deleteIcon: (id: string) =>
    apiFetch<{ success: boolean }>(`/editor-resources/icons/${id}`, { method: 'DELETE' }),
};

// ─── Internal notifications (Admin + Confirmation Agent) ─────────────────────
export type NotificationKind = 'order.new' | 'lead.new' | 'delivery.problem' | 'message.failed' | 'alert';

export interface AppNotification {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string | null;
  order_id: string | null;
  shopify_order_id: string | null;
  meta: Record<string, unknown> | null;
  created_at: string;
  read_at: string | null;
}

export const notificationsApi = {
  /** This user's feed, newest first, plus the unread count. */
  list: (limit = 20) =>
    apiFetch<{ notifications: AppNotification[]; unread: number }>(`/notifications?limit=${limit}`),
  /** Mark specific ids read, or every unread one. */
  markRead: (body: { ids?: string[]; all?: boolean }) =>
    apiFetch<{ ok: boolean; unread: number }>('/notifications/read', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// ─── Web Push (admin PWA, 2026-10-01) ────────────────────────────────────────
// The worker's /push routes (docs/superpowers/plans/2026-10-01-pwa-push.md).
// Every call is for the CALLER: their devices, their per-alert choices.
export type PushPlatform = 'ios' | 'android' | 'desktop' | 'other';
export type AlertGroup = 'reply' | 'now' | 'system' | 'info';
export type AlertChannel = 'push' | 'whatsapp' | 'both' | 'bell';

export interface PushSubscribeBody {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  platform?: PushPlatform;
}

export interface PushPrefKind {
  kind: string;
  label: string;
  group: AlertGroup;
  channel: AlertChannel;
  /** true = no choice saved yet, the family's default applies. */
  isDefault: boolean;
}

/** POST /push/test answer. Fields beyond `ok` are read only when present. */
/** POST /push/test (worker routes/push.ts). A 404 (thrown) = no device registered for this user. */
export interface PushTestResult {
  ok?: boolean;
  /** This user's registered devices. */
  devices?: number;
  /** Devices the push service accepted the test for. */
  pushed?: number;
  /** Devices the push service said are gone (404/410) — the server deleted them. */
  gone?: number;
  failed?: number;
  errors?: string[];
  error?: string;
}

export const pushApi = {
  /** VAPID public key (base64url, uncompressed P-256) for pushManager.subscribe. */
  key: () => apiFetch<{ publicKey: string }>('/push/key'),
  /** Register (upsert by endpoint) this device for the caller. */
  subscribe: (body: PushSubscribeBody) =>
    apiFetch<{ ok?: boolean }>('/push/subscribe', { method: 'POST', body: JSON.stringify(body) }),
  unsubscribe: (endpoint: string) =>
    apiFetch<{ ok?: boolean }>('/push/subscribe', { method: 'DELETE', body: JSON.stringify({ endpoint }) }),
  /** Every alert family with the caller's channel for it. */
  prefs: () => apiFetch<{ kinds: PushPrefKind[] }>('/push/prefs'),
  setPref: (kind: string, channel: AlertChannel) =>
    apiFetch<{ ok?: boolean }>('/push/prefs', { method: 'PUT', body: JSON.stringify({ kind, channel }) }),
  /** A test push to the caller's own devices only — or, with 'wa_line', a fake
   *  « WhatsApp line down » alert marked TEST. */
  test: (kind: 'test' | 'wa_line' = 'test') =>
    apiFetch<PushTestResult>('/push/test', { method: 'POST', body: JSON.stringify({ kind }) }),
};

// ─── The delivery page (2026-10-10) ─────────────────────────────────────────
export type AmeexLane = 'cree' | 'route' | 'livraison' | 'livre';
export type AmeexProblemKind = 'unreachable' | 'refused' | 'silent' | 'not_picked';
export interface AmeexBoardParcel {
  code: string; orderId: string; shopifyOrderId: string; orderName: string | null;
  name: string | null; city: string | null;
  status: AmeexCanonicalStatus | null; raw: string | null; sub: string | null;
  lastEventAt: string | null; cod: number | null;
  lane: AmeexLane;
  problem: { kind: AmeexProblemKind; days?: number; sub?: string | null } | null;
}
export type AmeexCheckReason = 'old_unshipped' | 'never_picked' | 'silent_long' | 'returned' | 'cancelled_parcel';
export interface AmeexCheckItem {
  kind: 'order' | 'parcel'; reason: AmeexCheckReason;
  orderId: string; shopifyOrderId: string; orderName: string | null; name: string | null;
  code?: string; lastEventAt?: string | null; createdAt?: string | null; missing?: string[];
}
export interface AmeexOwed { amount: number; cod: number; fees: number; payouts: number; parcels: number; since: string; lastPayoutAt: string | null }
export interface AmeexBoard {
  numbers: { enRoute: number; delivered7: number; problems: number; owed: AmeexOwed | null };
  parcels: AmeexBoardParcel[];
  ready: Array<Pick<Order, 'id' | 'shopify_order_id' | 'shopify_order_name' | 'customer_name' | 'customer_phone' | 'total_price' | 'status' | 'created_at'> & { delivery_city?: string | null }>;
  toCheck: AmeexCheckItem[];
  notYet: number;
  listOk: boolean;
}
export interface AmeexParcelEvent { at: string | null; statut: string; name: string; comment: string | null; city: string | null; hub: string | null; problem: boolean }
export interface AmeexParcelDetail {
  code: string; tracked: boolean;
  order: { id: string; shopifyOrderId: string; orderName: string | null; name: string | null; status: string | null; total: number | null } | null;
  client: { name: string | null; phone: string | null; chatId: string | null };
  city: string | null; origin: string;
  courier: { name: string | null; phone: string | null } | null;
  events: AmeexParcelEvent[];
  attempts: number; agentAt: string | null; repliedAt: string | null;
  cod: number | null; fees: { delivery: number | null; returned: number | null };
  situation: string | null; deliveredAt: string | null; lastPayoutAt: string | null;
}

// ─── COUPONS (2026-10-28, docs/superpowers/plans/2026-10-28-coupons.md) ──────
// Admin side of the coupon system. Routes per the plan's CONTRACTS: /coupons/*
// (perm coupons.manage) and /orders/:id/coupon (admin variant ?admin=1).
// Everything ships OFF behind app_settings.coupons_enabled / coupon_redeem_enabled.

export type CouponDiscountType = 'fixed' | 'percent';
export type CouponExpiryMode = 'after_delivery' | 'fixed_date';
/** 'patch' = a plain colour block, no content — hides something baked into
 *  the artwork (e.g. a sample code the designer drew). */
export type CouponBoxRole = 'self_code' | 'gift_code' | 'qr' | 'patch';
export type CouponQrTarget = 'review' | 'prefill';
export type CouponAlign = 'left' | 'center' | 'right';

/** One box on a coupon design — x,y,w,h are 0..1 fractions of the design image. */
export interface CouponBox {
  role: CouponBoxRole;
  x: number;
  y: number;
  w: number;
  h: number;
  font?: string;
  color?: string;
  /** Text height as a % of the box height (see pages/admin/coupons/couponMath.ts). */
  size_pct?: number;
  align?: CouponAlign;
  qr_target?: CouponQrTarget;
  /** Solid fill painted over the artwork BEFORE the code / QR (hex). Covers a
   *  sample code baked into the design. A 'patch' box is only this fill. */
  bg?: string;
  /** Plain text printed right after the code, same size/colour, regular
   *  weight (e.g. « · Valable 30 jours après réception. ») — for when the
   *  code box covers a line of the artwork that went on after the code. */
  after?: string;
}

/** One of the 4 coupons of the 2×2 sheet: which design on the front and on the back. */
export interface CouponCell {
  front_design_id: string | null;
  back_design_id: string | null;
}

export interface CouponCampaign {
  id: string;
  name: string;
  discount_type: CouponDiscountType;
  discount_value: number;
  min_order_dh: number;
  combinable: boolean;
  expiry_mode: CouponExpiryMode;
  expiry_days: number;
  expiry_date: string | null;
  active: boolean;
  print_in_albums: boolean;
  print_priority: number;
  max_codes: number | null;
  self_prefix: string;
  gift_prefix: string;
  shared_code: string | null;
  cells: CouponCell[];
  // ─── influencer codes (2026-11-02) — all about the SHARED code ───
  /** Total orders that may hold the shared code (cancelled ones free their place); null = no limit. */
  max_uses?: number | null;
  /** The shared code once per client (same phone). Default true. */
  once_per_client?: boolean;
  influencer_name?: string | null;
  /** 'fixed' = DH per delivered order · 'percent' = % of each delivered order. */
  commission_type?: 'fixed' | 'percent' | null;
  commission_value?: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface CouponDesign {
  id: string;
  campaign_id: string;
  name: string;
  side: 'front' | 'back';
  image_url: string;
  storage_path: string | null;
  width_px: number | null;
  height_px: number | null;
  boxes: CouponBox[];
  /** How the image sits on the coupon. 'cover' (default) = fills the coupon
   *  + bleed, edges cut off; 'contain' = the WHOLE image inside the trim, the
   *  rest (bleed included) filled with bg_color. For artwork with a frame
   *  drawn near its edge. */
  fit?: 'cover' | 'contain';
  /** The fill around a 'contain' image (hex). Null = white. */
  bg_color?: string | null;
  active: boolean;
  sort: number;
  created_at?: string;
}

/** The worker's admin status of a code (worker/src/lib/coupons.ts codeStatus):
 *  active = valid now; not_yet = printed, its order not delivered yet (after-delivery
 *  rule); reserved = held by an order being checked out. 'issued' = older lists. */
export type CouponCodeStatus = 'active' | 'not_yet' | 'reserved' | 'used' | 'expired' | 'void' | 'issued';

/** A row of GET /coupons/codes. The order fields are joins the list may carry;
 *  every one is optional so an older/leaner response still renders. */
export interface CouponCodeRow {
  id: string;
  code: string;
  campaign_id: string;
  kind: 'self' | 'gift' | 'shared';
  issued_order_id: string | null;
  cell: number | null;
  single_use: boolean;
  expires_at: string | null;
  void_at: string | null;
  created_at: string;
  /** The list's coarse status ('issued' | 'used' | 'expired' | 'void'). */
  status?: CouponCodeStatus;
  /** The detailed state (active / not_yet / reserved / used / expired / void). */
  state?: CouponCodeStatus;
  /** The order the code was printed for. */
  issued_order_name?: string | null;
  issued_shopify_order_id?: string | null;
  /** The order it was used on (latest non-released redemption). */
  used_order_id?: string | null;
  used_order_name?: string | null;
  used_shopify_order_id?: string | null;
  used_at?: string | null;
  discount_dh?: number | null;
}

/** One campaign's figures. Money = DELIVERED orders only (house rule). */
export interface CouponCampaignStats {
  campaign_id: string;
  name?: string;
  printed: number;
  used: number;
  /** Delivered orders' money. */
  revenue_dh: number;
  /** Orders that used a code of the campaign (absent on an older worker). */
  orders?: number;
  /** Of those, delivered. */
  delivered?: number;
  /** Owed to the influencer = commission on delivered orders only. */
  commission_dh?: number;
  influencer_name?: string | null;
  commission_type?: 'fixed' | 'percent' | null;
  commission_value?: number | null;
}

export interface CouponStats {
  printed: number;
  used: number;
  /** Delivered orders only. */
  revenue_dh: number;
  by_campaign: CouponCampaignStats[];
}

/** Why a code was refused — validateCoupon's reasons (worker/src/lib/coupons.ts). */
export type CouponRefusal =
  | 'unknown' | 'used' | 'expired' | 'not_yet' | 'min_order' | 'inactive' | 'already_has_coupon'
  | 'own_order' | 'max_uses' | 'once_per_client' | 'disabled' | 'locked';

/** POST/DELETE /orders/:id/coupon: a refusal is 200 {ok:false, reason, message};
 *  a success is {ok:true} + the checkout-summary shape (coupon_code, coupon_discount, total…). */
export interface ApplyCouponResult {
  ok: boolean;
  reason?: CouponRefusal;
  /** The client's French words for a refusal. */
  message?: string;
  min_order_dh?: number;
  coupon_code?: string | null;
  coupon_discount?: number;
  total?: number;
  subtotal?: number;
  shopify_synced?: boolean;
}

export const couponsApi = {
  // Campaigns
  listCampaigns: () => apiFetch<{ campaigns: CouponCampaign[] }>('/coupons/campaigns'),
  createCampaign: (data: Partial<CouponCampaign> & { name: string }) =>
    apiFetch<{ campaign: CouponCampaign }>('/coupons/campaigns', { method: 'POST', body: JSON.stringify(data) }),
  updateCampaign: (id: string, data: Partial<CouponCampaign>) =>
    apiFetch<{ campaign: CouponCampaign }>(`/coupons/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteCampaign: (id: string) =>
    apiFetch<{ ok: boolean }>(`/coupons/campaigns/${id}`, { method: 'DELETE' }),

  // Designs
  listDesigns: (campaignId?: string) =>
    apiFetch<{ designs: CouponDesign[] }>(`/coupons/designs${campaignId ? `?campaign_id=${encodeURIComponent(campaignId)}` : ''}`),
  createDesign: (data: Partial<CouponDesign> & { campaign_id: string; name: string; side: 'front' | 'back'; image_url: string }) =>
    apiFetch<{ design: CouponDesign }>('/coupons/designs', { method: 'POST', body: JSON.stringify(data) }),
  updateDesign: (id: string, data: Partial<CouponDesign>) =>
    apiFetch<{ design: CouponDesign }>(`/coupons/designs/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteDesign: (id: string) =>
    apiFetch<{ ok: boolean }>(`/coupons/designs/${id}`, { method: 'DELETE' }),
  /** Signed upload into the public `coupon-designs` bucket. */
  designUploadUrl: (filename: string, content_type: string) =>
    apiFetch<{ upload_url: string; token?: string; storage_path: string; public_url: string; content_type?: string }>(
      '/coupons/designs/upload-url', { method: 'POST', body: JSON.stringify({ filename, content_type }) }),

  // Numbers + codes
  stats: () => apiFetch<CouponStats>('/coupons/stats'),
  codes: (params: { campaign_id?: string; status?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.campaign_id) qs.set('campaign_id', params.campaign_id);
    if (params.status) qs.set('status', params.status);
    const q = qs.toString();
    return apiFetch<{ codes: CouponCodeRow[] }>(`/coupons/codes${q ? `?${q}` : ''}`);
  },

  // A coupon on one order — the ADMIN variant (WhatsApp orders; perm orders edit).
  applyToOrder: (shopifyOrderId: string, code: string) =>
    apiFetch<ApplyCouponResult>(`/orders/${shopifyOrderId}/coupon?admin=1`, { method: 'POST', body: JSON.stringify({ code }) }),
  removeFromOrder: (shopifyOrderId: string) =>
    apiFetch<ApplyCouponResult>(`/orders/${shopifyOrderId}/coupon?admin=1`, { method: 'DELETE' }),
};

/** GET /cleanup/log — migration 280 `photo_deletions`. */
export interface PhotoDeletionRow {
  id: string;
  order_id: string | null;
  order_name: string | null;
  dry_run: boolean;
  album_photos: number;
  whatsapp_copies: number;
  bytes_freed: number;
  days_after_delivery: number | null;
  created_at: string;
}

// ─── « Photo Vivante » (2026-11-03, worker routes/living.ts) ────────────────
export interface LivingItem {
  album_index: number;
  status: 'waiting' | 'draft' | 'ready';
  scans: number;
  last_scan_at: string | null;
  link: string;
  still_url: string | null;
  has_video: boolean;
  updated_at: string;
  /** Set when the still is a photo already in the album (« Une photo de
   *  l'album », 2026-11-04): nothing new prints. Null = a new last page. */
  photo_id?: string | null;
  /** The server is making the played video (migration 290, 1–3 min). */
  preparing?: boolean;
  /** Why the server could not make it, in plain words. */
  prep_error?: string | null;
}
type LivingPaths = { video: string; still: string };
/** Where the video's first frame sits on the still, in 0–1 (null = it fills the photo). */
export type LivingPlacement = { x: number; y: number; w: number; h: number };
type LivingUpload = { link: string; paths: LivingPaths; upload: LivingPaths };
/** One album of the admin « Photo Vivante » page (GET /living/all). */
export interface LivingListItem extends LivingItem {
  shopify_order_id: string;
  order_name: string | null;
  customer_name: string | null;
  order_status: string | null;
  created_at: string;
}
/** What a Photo Vivante upload needs, whoever does it (team or client). */
export interface LivingUploader {
  upload: (albumIndex: number, videoExt: string, photoId?: string) => Promise<LivingUpload>;
  ready: (albumIndex: number, paths: LivingPaths, stillAt: number, placement?: LivingPlacement | null) => Promise<{ ok: true; link: string }>;
}
const livingUploader = (base: string): LivingUploader => ({
  upload: (albumIndex, videoExt, photoId) => apiFetch<LivingUpload>(`${base}/upload`,
    { method: 'POST', body: JSON.stringify({ album_index: albumIndex, video_ext: videoExt, ...(photoId ? { photo_id: photoId } : {}) }) }),
  ready: (albumIndex, paths, stillAt, placement) => apiFetch<{ ok: true; link: string }>(`${base}/ready`,
    { method: 'POST', body: JSON.stringify({ album_index: albumIndex, paths, still_at: stillAt, ...(placement ? { placement } : {}) }) }),
});
export const livingApi = {
  forOrder: (shopifyOrderId: string) =>
    apiFetch<{ sold: boolean; items: LivingItem[] }>(`/living/order/${encodeURIComponent(shopifyOrderId)}`),
  /** The team, from the order page. */
  team: (shopifyOrderId: string) => livingUploader(`/living/order/${encodeURIComponent(shopifyOrderId)}`),
  /** « Supprimer »: the album's video and photo copy go, back to « Vidéo attendue », same link. */
  remove: (shopifyOrderId: string, albumIndex: number) =>
    apiFetch<{ ok: true; removed: number }>(`/living/order/${encodeURIComponent(shopifyOrderId)}/${albumIndex}`, { method: 'DELETE' }),
  all: () => apiFetch<{ items: LivingListItem[] }>('/living/all'),
  /** The client, from the album editor (the order id is the pass). */
  mine: (shopifyOrderId: string) =>
    apiFetch<{ items: Pick<LivingItem, 'album_index' | 'status' | 'link' | 'still_url' | 'has_video' | 'updated_at'>[] }>(`/living/mine/${encodeURIComponent(shopifyOrderId)}`),
  client: (shopifyOrderId: string) => livingUploader(`/living/mine/${encodeURIComponent(shopifyOrderId)}`),
};
